// ---------------------------------------------------------------------
// INSTALLATION & SETUP GUIDE (Create React App Version)
// ---------------------------------------------------------------------
// 1. Create a new React project (Standard Method):
//    npx create-react-app cognito-journal
//    cd cognito-journal
//
// 2. Install required dependencies:
//    npm install axios lucide-react
//
// 3. Install Tailwind CSS:
//    npm install -D tailwindcss postcss autoprefixer
//    npx tailwindcss init
//
// 4. Configure Tailwind:
//    - Open 'tailwind.config.js' and set content to: 
//      content: ["./src/**/*.{js,jsx,ts,tsx}"],
//    - Open 'src/index.css' and replace contents with:
//      @tailwind base;
//      @tailwind components;
//      @tailwind utilities;
//
// 5. Run the Application:
//    npm start
//
// 6. How to use this file:
//    - Open 'src/App.js' (or App.jsx).
//    - Delete everything inside it.
//    - Paste ALL the code below into it.
//    - Ensure your backend is running on http://localhost:4000
// ---------------------------------------------------------------------

import { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect, memo } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
// import axios from 'axios';
import api from "./utils/api";
import supabase from "./utils/supabaseClient";
// import { 
//     Mic, MicOff, Send, Save, BookOpen, Home, User, Sparkles, X, 
//     ChevronLeft, ChevronRight, Upload, MessageCircle,
//     Eye, EyeOff, Lock, Phone, Activity, Heart, ArrowRight, LogOut, Calendar,
//     CheckCircle2, Circle, Flame, Plus, Feather
// } from 'lucide-react';
import { Mic, MicOff, Send, BookOpen, Home, User, Sparkles, X, Plus,
    ChevronLeft, ChevronRight, ChevronUp, ChevronDown, MessageCircle,
    Eye, EyeOff, Lock, ArrowLeft, Calendar, Feather, Check,
    Trash2, RotateCcw, Volume2, VolumeX,
    Bold, Italic, Underline, Strikethrough, Highlighter,
    List, ListOrdered, CheckSquare, Table, PenLine, Maximize2,
    GripVertical, Search, FlaskConical, ImagePlus, ListPlus, Square, Menu } from "lucide-react";

import { Loader2, Layers3, AudioLines, Share2, MessageCircleQuestion } from 'lucide-react';
import AnimatedWaveform from './components/AnimatedWaveform';
import ReflectionOfTheDay, { CADENCE as REFLECTION_CADENCE } from './components/ReflectionOfTheDay';
import CompanionCard, { GENERIC_OPENER_QUESTION, composeOpenerMessage } from './components/CompanionCard';
import { clearAccountFromBrowser } from './utils/accountCleanup';
import JournalRhythmCard from './components/JournalRhythmCard';
import SmartReflectionCard from './components/SmartReflectionCard';
import { useJournalIndex } from './hooks/useJournalIndex';
import ReactDOM from 'react-dom';
import { VoiceNoteCard } from './components/VoiceNoteCard';
import { VoiceTab } from './components/VoiceTab';
import { VoiceCapture } from './components/voice/VoiceCapture';
import JournalSidebar from './components/JournalSidebar';
import {
    DndContext, closestCenter, PointerSensor, KeyboardSensor,
    useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates,
    verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProfilePage } from './components/ProfilePage';
import { ProfileAvatar } from './components/ProfileAvatar';
import { AiConsentModal } from './components/AiConsentModal';
import { AiEnableDialog } from './components/AiEnableDialog';
import { aiNudgeAllowed } from './utils/aiNudge';
import { AiGate } from './components/AiGate';
import { useChatPipelineStream } from './hooks/useChatPipelineStream';
import { transcribe } from './utils/voice/transcribe';
import { speak, cancelSpeech, isSpeechSupported } from './utils/voice/speak';
import { ChatThinkingTimeline } from './components/chat/ChatThinkingTimeline';
import { SupportResources } from './components/chat/SupportResources';
import ChatHistoryDrawer from './components/ChatHistoryDrawer';
import TemporaryChatToggle from './components/TemporaryChatToggle';
import { AI_FEATURES } from './utils/aiFeatures';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE_CODE, normalizeLanguageCode } from './utils/languages';
import { sanitizeHTML, stripTags } from './utils/richText';

// The Google flow leaves the page entirely, so React state doesn't survive to
// the callback. The language picked on the sign-up card rides across the
// redirect in localStorage; it is read once and cleared on return.
const PENDING_LANGUAGE_KEY = 'pendingPreferredLanguage';

// --- Global Setup ---
// TODO: PASTE YOUR GEMINI API KEY HERE TO FIX 403 ERRORS
// const BACKEND_URL = "http://localhost:8080";
// const BACKEND_URL = process.env.REACT_APP_IC_BACKEND_URL;

// --- Journal photo limits (tweak here) ---
// MAX_PHOTOS_PER_BATCH: how many photos one file-picker selection can add.
// MAX_PHOTOS_PER_DAY:   hard ceiling on photos stored for a single journal day
//                       (enforced client-side here + as a backstop in the backend
//                       bulk-save; keep the two in sync if you change this).
const MAX_PHOTOS_PER_BATCH = 10;
const MAX_PHOTOS_PER_DAY = 15;

// --- Auth UI helpers (defined outside App to prevent remount on every render) ---
const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true" className="flex-shrink-0">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
);

const AuthDivider = () => (
    <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-stone-200" />
        <span className="text-[11px] font-semibold text-stone-400 uppercase tracking-widest">or</span>
        <div className="flex-1 h-px bg-stone-200" />
    </div>
);

const AuthInput = ({ type, value, onChange, placeholder, icon: Icon, rightEl }) => (
    <div className="relative group">
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-stone-600 transition-colors" size={18} />
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className="w-full pl-11 pr-10 py-4 bg-stone-50 rounded-2xl outline-none text-stone-800 font-medium placeholder:text-stone-400 focus:bg-white focus:ring-2 focus:ring-stone-200 transition-all text-[15px]"
        />
        {rightEl && <div className="absolute right-4 top-1/2 -translate-y-1/2">{rightEl}</div>}
    </div>
);

// --- Utility: Date Handling (Fixed for Local Timezone) ---

// Returns today's date as YYYY-MM-DD in local time
const getLocalToday = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Formats a YYYY-MM-DD string into separate parts for display
const formatDisplayDate = (dateString) => {
    if (!dateString) return { fullDate: '', weekday: '' };
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    return {
        fullDate: date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
        weekday: date.toLocaleDateString("en-US", { weekday: "long" })
    };
};

// "SAT, JUN 27" — uppercase abbreviated weekday + month + day for the Home header.
const formatHomeDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const weekday = date.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
    const monthShort = date.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
    return `${weekday}, ${monthShort} ${date.getDate()}`;
};

// --- Utility: Voice Recognition Hook ---
const useVoiceInput = (onResult) => {
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);

    useEffect(() => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;

            recognitionRef.current.onresult = (event) => {
                let interimTranscript = '';
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    } else {
                        interimTranscript += event.results[i][0].transcript;
                    }
                }
                onResult(finalTranscript, interimTranscript);
            };

            recognitionRef.current.onerror = (event) => {
                console.error("Speech recognition error", event.error);
                setIsListening(false);
            };
            
            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }
    }, [onResult]);

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        } else {
            recognitionRef.current?.start();
            setIsListening(true);
        }
    };

    return { isListening, toggleListening, hasSupport: !!recognitionRef.current };
};

// --- Components ---

// Injects rich-editor CSS rules once into <head>
const GlobalStyles = () => {
    useEffect(() => {
        if (document.getElementById('rich-editor-styles')) return;
        const style = document.createElement('style');
        style.id = 'rich-editor-styles';
        style.textContent = `
.checklist { list-style: none; padding-left: 0; }
.checklist li { display: flex; align-items: flex-start; gap: 8px; margin: 4px 0; }
.checklist li input[type="checkbox"] {
  appearance: none; -webkit-appearance: none;
  width: 18px; height: 18px; min-width: 18px;
  border: 2px solid #a8a29e; border-radius: 4px; margin-top: 3px; cursor: pointer;
  transition: background 200ms, border-color 200ms; flex-shrink: 0;
}
.checklist li input[type="checkbox"]:checked {
  background: #1C1917; border-color: #1C1917;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 7l4 4 6-6' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-size: 12px; background-repeat: no-repeat; background-position: center;
}
.checklist li input[type="checkbox"]:checked + span { text-decoration: line-through; color: #a8a29e; }
.rich-editor ul { list-style-type: disc; padding-left: 1.5em; margin: 4px 0; }
.rich-editor ol { list-style-type: decimal; padding-left: 1.5em; margin: 4px 0; }
.rich-editor li { margin: 2px 0; }
.rich-editor table { border-collapse: collapse; width: 100%; margin: 8px 0; }
.rich-editor td, .rich-editor th { border: 1px solid #e7e5e4; padding: 8px 12px; min-width: 60px; vertical-align: top; }
.rich-editor img { width: 110px; height: 88px; object-fit: contain; background: white; border-radius: 10px; cursor: pointer; display: inline-block; vertical-align: top; margin: 4px 3px; box-shadow: 0 2px 8px rgba(0,0,0,0.12); transition: transform 120ms; }
.rich-editor img:active { transform: scale(0.96); }
.line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        `;
        document.head.appendChild(style);
        return () => { const el = document.getElementById('rich-editor-styles'); if (el) el.remove(); };
    }, []);
    return null;
};

// Floating selection toolbar — portal-rendered above selected text
const SelectionToolbar = ({ rect, editorRef }) => {
    const [activeFormats, setActiveFormats] = useState({});

    useEffect(() => {
        setActiveFormats({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            strikeThrough: document.queryCommandState('strikeThrough'),
        });
    }, [rect]);

    const applyFormat = (e, cmd, value) => {
        e.preventDefault();
        document.execCommand(cmd, false, value);
        editorRef.current?.focus();
    };

    const top = rect.top + window.scrollY - 52;
    const left = Math.max(100, Math.min(
        rect.left + rect.width / 2 + window.scrollX,
        window.innerWidth - 100
    ));

    const buttons = [
        { cmd: 'bold', icon: <Bold size={14} strokeWidth={2.5} />, label: 'Bold' },
        { cmd: 'italic', icon: <Italic size={14} strokeWidth={2.5} />, label: 'Italic' },
        { cmd: 'underline', icon: <Underline size={14} strokeWidth={2.5} />, label: 'Underline' },
        { cmd: 'strikeThrough', icon: <Strikethrough size={14} strokeWidth={2.5} />, label: 'Strike' },
        { cmd: 'hiliteColor', value: '#fef08a', icon: <Highlighter size={14} strokeWidth={2.5} />, label: 'Highlight' },
    ];

    return ReactDOM.createPortal(
        <div
            style={{ position: 'fixed', top: top - window.scrollY, left, transform: 'translateX(-50%)', zIndex: 9999 }}
            className="flex items-center gap-0.5 px-2 py-1.5 rounded-full shadow-2xl bg-[#1C1917] text-white"
        >
            {buttons.map(btn => (
                <button
                    key={btn.cmd}
                    onPointerDown={(e) => applyFormat(e, btn.cmd, btn.value)}
                    className={`relative group p-1.5 rounded-full transition-all active:scale-90 hover:bg-white/15 ${activeFormats[btn.cmd] ? 'bg-white/20' : ''}`}
                    aria-label={btn.label}
                    title={btn.label}
                >
                    {btn.icon}
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white text-stone-800 text-[10px] px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap shadow-sm">
                        {btn.label}
                    </span>
                </button>
            ))}
            {/* Down arrow */}
            <div className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#1C1917] rotate-45 rounded-sm" />
        </div>,
        document.body
    );
};

// Block-level formatting toolbar — pure button row; chrome supplied by caller via className.
const BlockToolbar = ({
    editorRef, onOpenDrawing, onAddPhoto, onAddPrompts, onChange,
    isEditorActive = true,
    className = "flex items-center gap-0.5 overflow-x-auto mt-2 h-10 px-1.5 bg-stone-50 rounded-xl border border-stone-100 scrollbar-hide",
}) => {
    // Sync React state after execCommand — list commands don't always fire input events
    const syncState = () => {
        setTimeout(() => {
            if (!editorRef.current) return;
            const raw = editorRef.current.innerHTML || '';
            const normalized = (raw === '<br>' || raw === '<div><br></div>') ? '' : raw;
            onChange?.(sanitizeHTML(normalized));
        }, 0);
    };

    const exec = (e, cmd, val) => {
        e.preventDefault();
        editorRef.current?.focus();
        document.execCommand(cmd, false, val);
        syncState();
    };

    const getBlockParent = (node) => {
        const BLOCKS = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3']);
        let cur = node;
        while (cur && cur !== editorRef.current) {
            if (cur.nodeType === 1 && BLOCKS.has(cur.tagName)) return cur;
            cur = cur.parentNode;
        }
        return null;
    };

    const moveLineUp = (e) => {
        e.preventDefault();
        const sel = window.getSelection();
        if (!sel?.focusNode) return;
        const block = getBlockParent(sel.focusNode);
        if (!block?.previousElementSibling) return;
        block.parentNode.insertBefore(block, block.previousElementSibling);
        syncState();
    };

    const moveLineDown = (e) => {
        e.preventDefault();
        const sel = window.getSelection();
        if (!sel?.focusNode) return;
        const block = getBlockParent(sel.focusNode);
        if (!block?.nextElementSibling) return;
        block.parentNode.insertBefore(block.nextElementSibling, block);
        syncState();
    };

    const insertChecklist = (e) => {
        e.preventDefault();
        document.execCommand('insertHTML', false,
            '<ul class="checklist"><li><input type="checkbox"><span>&nbsp;Task</span></li></ul><p><br></p>'
        );
        syncState();
    };

    const insertTable = (e) => {
        e.preventDefault();
        document.execCommand('insertHTML', false,
            '<table><tbody>' +
            '<tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>' +
            '<tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>' +
            '</tbody></table><p><br></p>'
        );
        syncState();
    };

    const ToolBtn = ({ label, icon, onPointerDown, onClick, active }) => (
        <button
            type="button"
            onPointerDown={onPointerDown}
            onClick={onClick}
            aria-label={label}
            title={label}
            className={`relative group flex-shrink-0 p-2.5 rounded-xl transition-all active:scale-90 ${active ? 'bg-stone-200 text-stone-800' : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700'}`}
        >
            {icon}
            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-[10px] px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
                {label}
            </span>
        </button>
    );

    // Aa popover — iOS-style frosted-glass sub-menu for text formatting + lists + line moves.
    const [isAaOpen, setIsAaOpen] = useState(false);
    const aaButtonRef = useRef(null);
    const popoverRef = useRef(null);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
    const POPOVER_W = 300;

    useLayoutEffect(() => {
        if (!isAaOpen || !aaButtonRef.current) return;
        const r = aaButtonRef.current.getBoundingClientRect();
        const POPOVER_H = 220;
        let left = Math.max(8, Math.min(window.innerWidth - POPOVER_W - 8, r.right - POPOVER_W));
        let top = r.bottom + 6;
        if (top + POPOVER_H > window.innerHeight - 8) top = Math.max(8, r.top - POPOVER_H - 6);
        setPopoverPos({ top, left });
    }, [isAaOpen]);

    useEffect(() => {
        if (!isAaOpen) return;
        const outside = (e) => {
            if (popoverRef.current?.contains(e.target) || aaButtonRef.current?.contains(e.target)) return;
            setIsAaOpen(false);
        };
        const esc = (e) => { if (e.key === 'Escape') setIsAaOpen(false); };
        document.addEventListener('mousedown', outside);
        document.addEventListener('touchstart', outside);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', outside);
            document.removeEventListener('touchstart', outside);
            document.removeEventListener('keydown', esc);
        };
    }, [isAaOpen]);

    const PopoverBtn = ({ label, icon, onPointerDown }) => (
        <button
            type="button"
            onPointerDown={onPointerDown}
            aria-label={label}
            title={label}
            className="flex flex-col items-center justify-center gap-1 py-2.5 px-1 rounded-xl hover:bg-stone-100/80 active:bg-stone-200/80 active:scale-95 transition text-stone-600 hover:text-stone-800"
        >
            {icon}
            <span className="text-[10px] font-medium leading-none tracking-tight">{label}</span>
        </button>
    );

    // Draw and Aa act on the focused editor, so they're dimmed and inert when none is active.
    // Add Visual Memories / Add Prompts are standalone (open file picker / curator) — always interactive.
    const gatedClass = `inline-flex items-center transition-opacity duration-200 ${
        isEditorActive ? 'opacity-100' : 'opacity-40 pointer-events-none'
    }`;

    return (
        <div className={className}>
            <span className={gatedClass}>
                <ToolBtn label="Draw" icon={<PenLine size={20} />} onPointerDown={(e) => { e.preventDefault(); onOpenDrawing(); }} />
            </span>
            {onAddPhoto && (
                // iOS Safari: file-picker `.click()` MUST run inside a real `click`
                // event, not `pointerdown`. onPointerDown just guards editor focus.
                <ToolBtn
                    label="Add Visual Memories"
                    icon={<ImagePlus size={20} />}
                    onPointerDown={(e) => { e.preventDefault(); }}
                    onClick={onAddPhoto}
                />
            )}
            {onAddPrompts && (
                <ToolBtn
                    label="Add Prompts"
                    icon={<ListPlus size={20} />}
                    onPointerDown={(e) => { e.preventDefault(); onAddPrompts(); }}
                />
            )}
            <span className={gatedClass}>
                <button
                    ref={aaButtonRef}
                    type="button"
                    onPointerDown={(e) => { e.preventDefault(); setIsAaOpen(v => !v); }}
                    aria-label="Text formatting"
                    aria-expanded={isAaOpen}
                    title="Text formatting"
                    className={`shrink-0 inline-flex items-center justify-center h-9 min-w-[44px] px-3 rounded-xl transition-all active:scale-95 font-serif text-[16px] leading-none ${
                        isAaOpen ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                    }`}
                >
                    Aa
                </button>
            </span>
            {isAaOpen && ReactDOM.createPortal(
                <div
                    ref={popoverRef}
                    style={{ position: 'fixed', top: popoverPos.top, left: popoverPos.left, width: POPOVER_W }}
                    className="z-[80] bg-white/85 backdrop-blur-2xl border border-stone-200/70 rounded-2xl shadow-[0_10px_30px_rgba(45,41,38,0.16)] p-2 animate-in fade-in zoom-in-95 slide-in-from-top-1 duration-150"
                    role="menu"
                    aria-label="Text formatting"
                >
                    <div className="grid grid-cols-4 gap-1">
                        <PopoverBtn label="Bold" icon={<Bold size={18}/>} onPointerDown={(e) => exec(e, 'bold')} />
                        <PopoverBtn label="Italic" icon={<Italic size={18}/>} onPointerDown={(e) => exec(e, 'italic')} />
                        <PopoverBtn label="Underline" icon={<Underline size={18}/>} onPointerDown={(e) => exec(e, 'underline')} />
                        <PopoverBtn label="Strike" icon={<Strikethrough size={18}/>} onPointerDown={(e) => exec(e, 'strikeThrough')} />
                    </div>
                    <div className="h-px bg-stone-200/60 my-1.5 mx-1" />
                    <div className="grid grid-cols-4 gap-1">
                        <PopoverBtn label="Bullet" icon={<List size={18}/>} onPointerDown={(e) => exec(e, 'insertUnorderedList')} />
                        <PopoverBtn label="Numbered" icon={<ListOrdered size={18}/>} onPointerDown={(e) => exec(e, 'insertOrderedList')} />
                        <PopoverBtn label="Checklist" icon={<CheckSquare size={18}/>} onPointerDown={insertChecklist} />
                        <PopoverBtn label="Table" icon={<Table size={18}/>} onPointerDown={insertTable} />
                    </div>
                    <div className="h-px bg-stone-200/60 my-1.5 mx-1" />
                    <div className="grid grid-cols-2 gap-1">
                        <PopoverBtn label="Move Up" icon={<ChevronUp size={18}/>} onPointerDown={moveLineUp} />
                        <PopoverBtn label="Move Down" icon={<ChevronDown size={18}/>} onPointerDown={moveLineDown} />
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

// Drawing canvas modal
const DrawingCanvas = ({ isOpen, onClose, onInsert }) => {
    const canvasRef = useRef(null);
    const [tool, setTool] = useState('pen');
    const [color, setColor] = useState('#1C1917');
    const [lineWidth, setLineWidth] = useState(3);
    const isDrawing = useRef(false);
    const lastPoint = useRef(null);
    const startPoint = useRef(null);
    const snapshotRef = useRef(null);

    const COLORS = ['#1C1917','#FFFFFF','#EF4444','#F97316','#EAB308','#22C55E','#3B82F6','#8B5CF6','#EC4899','#78716C'];
    const TOOLS = [
        { id: 'pen', label: 'Pen', icon: '✏️' },
        { id: 'eraser', label: 'Erase', icon: '⬜' },
        { id: 'line', label: 'Line', icon: '╱' },
        { id: 'rect', label: 'Rect', icon: '▭' },
        { id: 'circle', label: 'Circle', icon: '○' },
    ];

    useEffect(() => {
        if (!isOpen || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = canvas.offsetWidth * dpr;
        canvas.height = canvas.offsetHeight * dpr;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#FAF9F7';
        ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
    }, [isOpen]);

    const getCtx = () => canvasRef.current?.getContext('2d');
    const getPoint = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handlePointerDown = (e) => {
        e.preventDefault();
        canvasRef.current.setPointerCapture(e.pointerId);
        isDrawing.current = true;
        const pt = getPoint(e);
        lastPoint.current = pt;
        startPoint.current = pt;
        const ctx = getCtx();
        if (['line', 'rect', 'circle'].includes(tool)) {
            const canvas = canvasRef.current;
            snapshotRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
        }
    };

    const handlePointerMove = (e) => {
        if (!isDrawing.current) return;
        const ctx = getCtx();
        const pt = getPoint(e);
        const pressure = e.pressure || 0.5;

        if (tool === 'pen') {
            ctx.globalCompositeOperation = 'source-over';
            ctx.strokeStyle = color;
            ctx.lineWidth = Math.max(1, lineWidth * pressure * 2);
            ctx.beginPath();
            ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
            ctx.lineTo(pt.x, pt.y);
            ctx.stroke();
            lastPoint.current = pt;
        } else if (tool === 'eraser') {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.lineWidth = 20;
            ctx.beginPath();
            ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
            ctx.lineTo(pt.x, pt.y);
            ctx.stroke();
            ctx.globalCompositeOperation = 'source-over';
            lastPoint.current = pt;
        } else {
            ctx.putImageData(snapshotRef.current, 0, 0);
            ctx.strokeStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.globalCompositeOperation = 'source-over';
            const { x: sx, y: sy } = startPoint.current;
            ctx.beginPath();
            if (tool === 'line') {
                ctx.moveTo(sx, sy);
                ctx.lineTo(pt.x, pt.y);
                ctx.stroke();
            } else if (tool === 'rect') {
                ctx.strokeRect(sx, sy, pt.x - sx, pt.y - sy);
            } else if (tool === 'circle') {
                const rx = Math.abs(pt.x - sx) / 2;
                const ry = Math.abs(pt.y - sy) / 2;
                ctx.ellipse(sx + (pt.x - sx) / 2, sy + (pt.y - sy) / 2, rx || 1, ry || 1, 0, 0, Math.PI * 2);
                ctx.stroke();
            }
        }
    };

    const handlePointerUp = () => {
        isDrawing.current = false;
        snapshotRef.current = null;
    };

    const handleDone = () => {
        const dataURL = canvasRef.current.toDataURL('image/png');
        onInsert(dataURL);
        onClose();
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex flex-col items-center justify-end sm:justify-center">
            <div
                className="bg-[#FAF9F7] w-full sm:max-w-2xl sm:rounded-3xl flex flex-col overflow-hidden"
                style={{ height: '92vh', paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                {/* Top toolbar */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-100 bg-white/80 backdrop-blur-sm overflow-x-auto flex-shrink-0">
                    {/* Tool buttons */}
                    <div className="flex items-center gap-1 mr-2">
                        {TOOLS.map(t => (
                            <button
                                key={t.id}
                                onClick={() => setTool(t.id)}
                                className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${tool === t.id ? 'bg-stone-800 text-white shadow-sm' : 'text-stone-500 hover:bg-stone-100'}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="w-px h-6 bg-stone-200 flex-shrink-0" />
                    {/* Color swatches */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {COLORS.map(c => (
                            <button
                                key={c}
                                onClick={() => setColor(c)}
                                className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? 'border-stone-700 scale-125' : 'border-stone-300'}`}
                                style={{ backgroundColor: c }}
                            />
                        ))}
                    </div>
                    {/* Line width */}
                    <div className="w-px h-6 bg-stone-200 flex-shrink-0 ml-1" />
                    <input
                        type="range" min="1" max="12" value={lineWidth}
                        onChange={e => setLineWidth(Number(e.target.value))}
                        className="w-20 flex-shrink-0 accent-stone-700"
                    />
                    {/* Actions */}
                    <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                        <button onClick={onClose} className="px-4 py-1.5 text-sm text-stone-500 hover:text-stone-700 rounded-xl hover:bg-stone-100 transition-all">Cancel</button>
                        <button onClick={handleDone} className="px-4 py-1.5 text-sm font-semibold bg-stone-800 text-white rounded-xl hover:bg-stone-700 transition-all shadow-sm">Insert</button>
                    </div>
                </div>
                {/* Canvas */}
                <canvas
                    ref={canvasRef}
                    className="flex-1 touch-none cursor-crosshair w-full block"
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerLeave={handlePointerUp}
                />
            </div>
        </div>,
        document.body
    );
};

// Restores data URLs on <img data-drawing-id> elements that iOS drag stripped.
function restoreDrawings(html, storeRef) {
    if (!storeRef?.current?.size) return html;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    let changed = false;
    tmp.querySelectorAll('img[data-drawing-id]').forEach(img => {
        if (!img.getAttribute('src')) {
            const stored = storeRef.current.get(img.getAttribute('data-drawing-id'));
            if (stored) { img.setAttribute('src', stored); changed = true; }
        }
    });
    return changed ? tmp.innerHTML : html;
}

// Today's Story writing prompt: an anchor question that never changes — the
// noticing it asks for should be the same every day — plus one rotating
// question underneath, so each day also brings a second, smaller thing to look
// for. The mobile app shows the same pair for the same date; its twin is
// `StoryPrompt` in mobile/lib/features/journal/presentation/widgets/story_placeholder.dart.
// Keep the wording, the order, and the epoch-day rotation in step across both.
const STORY_ANCHOR_PROMPT = 'What is one small thing that went well today?';

const ROTATING_STORY_PROMPTS = [
    'Who showed you kindness today — or who did you support?',
    'What is one small thing you did just for yourself today?',
    'What went just a little better than expected today?',
];

// The rotating question for a 'YYYY-MM-DD' date. Keyed off the entry's own date
// rather than when it was first opened, so a day always shows the same pair —
// including when the user scrolls back to it weeks later. Counting whole days
// since the epoch (not day-of-month or day-of-year) keeps the cycle from
// stuttering across month and year ends; parsing as UTC keeps it off the
// timezone. A date that will not parse falls back to the first question rather
// than throwing: a hint is never worth a blank screen.
const rotatingStoryPrompt = (ymd) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
    if (!match) return ROTATING_STORY_PROMPTS[0];
    const days = Math.floor(Date.UTC(+match[1], +match[2] - 1, +match[3]) / 86400000);
    if (!Number.isFinite(days)) return ROTATING_STORY_PROMPTS[0];
    // Modulo of a negative day count would index off the front of the array.
    const i = ((days % ROTATING_STORY_PROMPTS.length) + ROTATING_STORY_PROMPTS.length) % ROTATING_STORY_PROMPTS.length;
    return ROTATING_STORY_PROMPTS[i];
};

// Rich text editor — replaces VoiceTextArea for Today's Story.
// onActivate(payload) fires on focus so the journal-level toolbar can drive THIS editor.
const RichEditor = ({ label, labelStyle = 'section', value, onChange, placeholder, minHeight = '180px', onFocus, onOpenDrawing, onAddPhoto, onAddPrompts, onActivate, editorRef, drawingStoreRef, topSlot, voice, headerRight }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [selectionRect, setSelectionRect] = useState(null);
    const [expandedSrc, setExpandedSrc] = useState(null);
    const [isFocusMode, setIsFocusMode] = useState(false);
    const [isFocusEditorFocused, setIsFocusEditorFocused] = useState(false);
    const [focusSelectionRect, setFocusSelectionRect] = useState(null);
    const focusEditorRef = useRef(null);
    const isFocusModeRef = useRef(false);
    useEffect(() => { isFocusModeRef.current = isFocusMode; }, [isFocusMode]);

    // Voice is lifted to the App scope (single SpeechRecognition instance routed to activeEditor.el).
    // RichEditor only consumes the voice prop for the focus-mode header button.
    const isListening = !!voice?.isListening;
    const toggleListening = voice?.toggleListening ?? (() => {});
    const hasSupport = !!voice?.hasSupport;

    // Sync value → innerHTML only when editor is not focused (prevents cursor jump)
    useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        // Backfill drawing store from any drawings already present in this value
        // (covers initial load, date switching, and server-fetched content)
        if (drawingStoreRef?.current) {
            const tmp = document.createElement('div');
            tmp.innerHTML = sanitizeHTML(value || '');
            tmp.querySelectorAll('img[data-drawing-id]').forEach(img => {
                const id = img.getAttribute('data-drawing-id');
                const src = img.getAttribute('src');
                if (id && src && !drawingStoreRef.current.has(id))
                    drawingStoreRef.current.set(id, src);
            });
        }
        if (document.activeElement === el) return;
        const clean = sanitizeHTML(value || '');
        if (el.innerHTML !== clean) el.innerHTML = clean;
    }, [value, editorRef, drawingStoreRef]);

    const handleInput = useCallback(() => {
        const raw = editorRef.current?.innerHTML || '';
        const normalized = (raw === '<br>' || raw === '<div><br></div>') ? '' : raw;
        onChange(restoreDrawings(sanitizeHTML(normalized), drawingStoreRef));
    }, [editorRef, onChange, drawingStoreRef]);

    // Track selection for SelectionToolbar
    useEffect(() => {
        const check = () => {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed || !editorRef.current?.contains(sel.anchorNode)) {
                setSelectionRect(null); return;
            }
            setSelectionRect(sel.getRangeAt(0).getBoundingClientRect());
        };
        document.addEventListener('selectionchange', check);
        return () => document.removeEventListener('selectionchange', check);
    }, [editorRef]);

    const handleEditorClick = useCallback((e) => {
        if (e.target.tagName === 'IMG') {
            e.preventDefault();
            const src = e.target.getAttribute('src') ||
                drawingStoreRef?.current?.get(e.target.getAttribute('data-drawing-id'));
            if (src) setExpandedSrc(src);
        }
    }, [drawingStoreRef]);

    // Sync value → focus editor when mode is open
    useEffect(() => {
        const el = focusEditorRef.current;
        if (!el || !isFocusMode || document.activeElement === el) return;
        const clean = sanitizeHTML(value || '');
        if (el.innerHTML !== clean) el.innerHTML = clean;
    }, [value, isFocusMode]);

    // Track selection inside focus editor
    useEffect(() => {
        if (!isFocusMode) return;
        const check = () => {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed || !focusEditorRef.current?.contains(sel.anchorNode)) {
                setFocusSelectionRect(null); return;
            }
            setFocusSelectionRect(sel.getRangeAt(0).getBoundingClientRect());
        };
        document.addEventListener('selectionchange', check);
        return () => document.removeEventListener('selectionchange', check);
    }, [isFocusMode]);

    const handleFocusInput = useCallback(() => {
        const raw = focusEditorRef.current?.innerHTML || '';
        const normalized = (raw === '<br>' || raw === '<div><br></div>') ? '' : raw;
        onChange(restoreDrawings(sanitizeHTML(normalized), drawingStoreRef));
    }, [onChange, drawingStoreRef]);

    const handleFocusEditorClick = useCallback((e) => {
        if (e.target.tagName === 'IMG') {
            e.preventDefault();
            const src = e.target.getAttribute('src') ||
                drawingStoreRef?.current?.get(e.target.getAttribute('data-drawing-id'));
            if (src) setExpandedSrc(src);
        }
    }, [drawingStoreRef]);

    const enterFocusMode = () => setIsFocusMode(true);
    // 'prompt' is the guided-reflection question line — sits above the answer body, slightly
    // larger, tighter tracking. Default style is the small-caps section header used by
    // Today's Story, Gratitudes, etc.
    const labelClass = labelStyle === 'prompt'
        ? 'text-base font-normal text-stone-700 leading-snug tracking-tight block'
        : 'text-[11px] font-semibold text-stone-500 uppercase tracking-[0.18em] flex items-center gap-2';

    return (
        <div className="relative group">
            <div className="mb-3 flex items-center justify-between gap-3">
                <label className={labelClass}>
                    {label}
                </label>
                {headerRight && (
                    <div className="shrink-0">{headerRight}</div>
                )}
            </div>
            <div>
                {topSlot && (
                    <div className="pb-3">
                        {topSlot}
                    </div>
                )}
                <div className="relative">
                    {/* Placeholder */}
                    {!value && !isFocused && (
                        <p className="absolute top-0 left-0 right-0 text-lg text-stone-300 leading-relaxed pointer-events-none whitespace-pre-wrap select-none z-10">
                            {placeholder}
                        </p>
                    )}
                    <div
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        autoCorrect="off"
                        autoCapitalize="sentences"
                        spellCheck={true}
                        onInput={handleInput}
                        onClick={handleEditorClick}
                        onFocus={() => {
                            setIsFocused(true);
                            onFocus?.();
                            if (editorRef.current) {
                                onActivate?.({ el: editorRef.current, onChange, onOpenDrawing, onAddPhoto, onAddPrompts, enterFocusMode });
                            }
                        }}
                        onBlur={() => setIsFocused(false)}
                        className="w-full text-lg leading-relaxed text-stone-700 outline-none bg-transparent rich-editor"
                        style={{ minHeight }}
                    />
                </div>
            </div>
            {selectionRect && isFocused && (
                <SelectionToolbar rect={selectionRect} editorRef={editorRef} />
            )}
            {/* Image lightbox */}
            {expandedSrc && ReactDOM.createPortal(
                <div
                    className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center"
                    onClick={() => setExpandedSrc(null)}
                >
                    <img
                        src={expandedSrc}
                        alt=""
                        className="max-w-[92vw] max-h-[88vh] rounded-2xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                        style={{ objectFit: 'contain' }}
                    />
                    <button
                        className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-all"
                        onClick={() => setExpandedSrc(null)}
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>,
                document.body
            )}

            {/* Focus mode */}
            {isFocusMode && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[150] bg-[#FDFCF8] flex flex-col">
                    {/* Header — title + close */}
                    <div
                        className="flex items-center justify-between px-5 py-3 bg-white/90 backdrop-blur-md shrink-0"
                        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
                    >
                        <span className={labelStyle === 'prompt'
                            ? 'text-base font-normal text-stone-700 leading-snug tracking-tight pr-3'
                            : 'text-xs font-semibold text-stone-500 uppercase tracking-widest'}>
                            {label}
                        </span>
                        <button
                            onClick={() => setIsFocusMode(false)}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 text-stone-500 hover:bg-stone-200 active:scale-95 transition-all"
                            aria-label="Exit focus mode"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Formatting toolbar — sits directly below header, full width, divider on bottom */}
                    <div className="bg-white/90 backdrop-blur-md border-b border-stone-100 px-3 pb-2 flex items-center gap-2 shrink-0">
                        <div className="flex-1 min-w-0">
                            <BlockToolbar
                                editorRef={focusEditorRef}
                                onOpenDrawing={() => { setIsFocusMode(false); onOpenDrawing(); }}
                                onAddPhoto={onAddPhoto ? () => { setIsFocusMode(false); onAddPhoto(); } : undefined}
                                onAddPrompts={onAddPrompts ? () => { setIsFocusMode(false); onAddPrompts(); } : undefined}
                                onChange={onChange}
                                className="flex items-center gap-0.5 overflow-x-auto h-10 px-1 scrollbar-hide"
                            />
                        </div>
                        {hasSupport && (
                            <button
                                onClick={toggleListening}
                                className={`p-2.5 rounded-full flex-shrink-0 transition-all duration-300 ${isListening ? 'bg-rose-50 text-rose-500 animate-pulse' : 'bg-stone-100 text-stone-400 hover:bg-stone-200'}`}
                                title="Dictate"
                            >
                                {isListening ? <MicOff size={18} /> : <Mic size={18} />}
                            </button>
                        )}
                    </div>

                    {/* Scrollable editor area — now reaches the bottom edge */}
                    <div className="flex-1 overflow-y-auto relative">
                        {!value && !isFocusEditorFocused && (
                            <p className="absolute top-6 left-6 right-6 text-xl text-stone-300 leading-relaxed pointer-events-none whitespace-pre-wrap select-none z-10">
                                {placeholder}
                            </p>
                        )}
                        <div
                            ref={focusEditorRef}
                            contentEditable
                            suppressContentEditableWarning
                            autoCorrect="off"
                            autoCapitalize="sentences"
                            spellCheck={true}
                            onInput={handleFocusInput}
                            onClick={handleFocusEditorClick}
                            onFocus={() => {
                                setIsFocusEditorFocused(true);
                                onFocus?.();
                                if (focusEditorRef.current) {
                                    onActivate?.({ el: focusEditorRef.current, onChange, onOpenDrawing, onAddPhoto });
                                }
                            }}
                            onBlur={() => setIsFocusEditorFocused(false)}
                            className="w-full px-6 py-6 text-xl leading-relaxed text-stone-700 outline-none rich-editor"
                            style={{
                                minHeight: 'calc(100dvh - 160px)',
                                paddingBottom: 'max(2rem, env(safe-area-inset-bottom))'
                            }}
                        />
                    </div>

                    {/* Floating selection toolbar */}
                    {focusSelectionRect && isFocusEditorFocused && (
                        <SelectionToolbar rect={focusSelectionRect} editorRef={focusEditorRef} />
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const GlassCard = ({ children, className = "" }) => (
    <div className={`bg-white/90 backdrop-blur-xl border border-white/50 shadow-2xl rounded-3xl p-6 ${className}`}>
        {children}
    </div>
);


// ========================================================================
// GUIDED REFLECTIONS — user-curated prompt cards (redesign 2026-05-24)
// ========================================================================

// Mirrors wiki/resources/design-system.md "Guided Reflections — Category Colors".
const CATEGORY_COLORS = {
    'Reflection':                   '#7C9A82',
    'Gratitude':                    '#D4A574',
    'Emotional Processing':         '#8FA5B5',
    'Self-Compassion':              '#C19A9B',
    'Self-Growth':                  '#9CA86E',
    'Fitness':                      '#C97B5E',
    'Diet':                         '#B59D6B',
    'Work':                         '#6B6055',
    'Relationships':                '#A78BAB',
    'Yearly Review':                '#7FB0A8',
    'Yearly Planning':              '#7FB0A8',
    'Yearly Values':                '#7FB0A8',
    'Yearly Gratitude & Growth':    '#7FB0A8',
};
const categoryColor = (cat) => CATEGORY_COLORS[cat] || '#7C9A82';

const CADENCE_LABEL = {
    'daily':       'Daily',
    'weekly':      'Weekly',
    'monthly':     'Monthly',
    'yearly':      'Yearly',
    'event-based': 'When it happens',
};

// One draggable, editable card per curated prompt.
const GuidedPromptCard = ({
    prompt, onChangeResponse, onToggleView, onRemove,
    onFocus, onOpenDrawing, onAddInlinePhoto, onAddPrompts, onActivate, drawingStoreRef, voice,
}) => {
    const editorRef = useRef(null);
    const {
        attributes, listeners, setNodeRef, transform, transition, isDragging,
    } = useSortable({ id: prompt.reflectionId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };

    const color = categoryColor(prompt.category);
    const isResearch = prompt.promptView === 'research';
    const displayText = isResearch ? prompt.researchPrompt : prompt.simplePrompt;

    return (
        <div
            ref={setNodeRef}
            style={style}
            id={`gr-prompt-${prompt.promptId}`}
            className="relative group pt-5 pb-1 border-t border-stone-100 first:border-t-0 first:pt-2"
        >
            <button
                type="button"
                aria-label="Drag to reorder"
                className="absolute -left-5 top-6 p-1 text-stone-300 hover:text-stone-500 cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 transition-opacity"
                {...attributes}
                {...listeners}
            >
                <GripVertical size={14} />
            </button>

            <div className="flex items-center gap-3 mb-3">
                <span
                    className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: `${color}1F`, color }}
                >
                    {prompt.category}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                    {CADENCE_LABEL[prompt.primaryCadence] || prompt.primaryCadence}
                </span>
                <button
                    type="button"
                    onClick={() => onToggleView(prompt.promptId)}
                    aria-label={isResearch ? 'Show simple prompt' : 'Show research prompt'}
                    title={isResearch ? 'Showing research prompt — tap for simple' : 'Showing simple prompt — tap for research'}
                    className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md transition-colors ${
                        isResearch
                            ? 'bg-stone-100 text-stone-700'
                            : 'text-stone-400 hover:bg-stone-50 hover:text-stone-600'
                    }`}
                >
                    <FlaskConical size={12} />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">Roll Prompts</span>
                </button>
                <button
                    type="button"
                    onClick={() => onRemove(prompt.promptId)}
                    aria-label="Remove prompt"
                    className="ml-auto p-1 rounded-full text-stone-300 hover:bg-rose-50 hover:text-rose-500 transition-colors opacity-60 group-hover:opacity-100"
                >
                    <X size={14} />
                </button>
            </div>

            <RichEditor
                label={displayText}
                labelStyle="prompt"
                placeholder="Your thoughts..."
                value={prompt.response || ''}
                onChange={onChangeResponse}
                onFocus={onFocus}
                onOpenDrawing={onOpenDrawing}
                onAddPhoto={onAddInlinePhoto ? () => onAddInlinePhoto(editorRef.current, onChangeResponse) : undefined}
                onAddPrompts={onAddPrompts}
                onActivate={onActivate}
                editorRef={editorRef}
                drawingStoreRef={drawingStoreRef}
                voice={voice}
                minHeight="140px"
            />
        </div>
    );
};

// Focus-mode modal. Opens on three prompts chosen for this person ("For you"),
// with the full catalogue one tap away.
//
// WHY IT DOES NOT OPEN ON THE LIST
// The catalogue is 3,697 prompts across 21 categories. Opening onto it asks the
// user to shop before they can write, and the scan is where journaling turns
// into a task. The picks come from the same selection path as the Home hero, so
// the first card is always the prompt the rest of the app is already showing.
const CuratePromptsModal = ({
    isOpen, onClose, catalog, initiallySelected, onConfirm, picks = [], picksLoading = false,
}) => {
    const [activeCategory, setActiveCategory] = useState('All');
    const [activeCadence, setActiveCadence] = useState('all');
    const [query, setQuery] = useState('');
    // 'picks' = the three chosen prompts; 'browse' = the full filtered catalogue.
    const [view, setView] = useState('picks');
    // `locked` = prompts already in the journal for this day; immutable from the modal (remove via per-prompt button on the journal page).
    // `added` = newly selected in this modal session; only these get counted/sent as additions.
    const [locked, setLocked] = useState(() => new Set(initiallySelected));
    const [added, setAdded] = useState(() => new Set());

    useEffect(() => {
        if (isOpen) {
            setLocked(new Set(initiallySelected));
            setAdded(new Set());
            setActiveCategory('All');
            setActiveCadence('all');
            setQuery('');
            setView('picks');
        }
    }, [isOpen, initiallySelected]);

    if (!isOpen) return null;

    const categories = ['All', ...Array.from(new Set((catalog || []).map(p => p.category)))];
    const cadenceTabs = [
        { id: 'all', label: 'All' },
        { id: 'daily', label: 'Daily' },
        { id: 'weekly', label: 'Weekly' },
        { id: 'monthly', label: 'Monthly' },
        { id: 'yearly', label: 'Yearly' },
    ];

    const q = query.trim().toLowerCase();
    const filtered = (catalog || []).filter(p => {
        if (activeCategory !== 'All' && p.category !== activeCategory) return false;
        if (activeCadence !== 'all' && p.primaryCadence !== activeCadence) return false;
        if (q && !p.simplePrompt.toLowerCase().includes(q)) return false;
        return true;
    });

    const toggleId = (id) => {
        if (locked.has(id)) return;
        setAdded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleConfirm = () => {
        // Locked first preserves their existing display_order in the parent's array→idx mapping; newly added are appended.
        onConfirm([...locked, ...added]);
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[150] bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                className="bg-[#FDFCF8] rounded-[2rem] shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-7 pt-7 pb-4 border-b border-stone-100">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h2 className="text-xl font-bold text-stone-800 tracking-tight">
                                {view === 'picks' ? 'For you today' : 'Curate Your Journaling Path'}
                            </h2>
                            <p className="text-sm text-stone-500 mt-1">
                                {view === 'picks'
                                    ? 'Three prompts chosen from what you have been writing.'
                                    : 'Select prompts to guide your writing.'}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            aria-label="Close"
                            className="p-2 rounded-full text-stone-400 hover:bg-stone-100 hover:text-stone-600 transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>
                    {/* Cadence tabs + search — browse only; the picks view has nothing to filter */}
                    <div className={`mt-4 items-center gap-3 flex-wrap ${view === 'browse' ? 'flex' : 'hidden'}`}>
                        <div className="flex gap-1 bg-stone-100/70 rounded-full p-1">
                            {cadenceTabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveCadence(tab.id)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                                        activeCadence === tab.id
                                            ? 'bg-white text-stone-800 shadow-sm'
                                            : 'text-stone-500 hover:text-stone-700'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        <div className="relative flex-1 min-w-[160px]">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search prompts..."
                                className="w-full pl-8 pr-3 py-2 text-sm bg-stone-100/70 border border-transparent rounded-full focus:bg-white focus:border-stone-200 focus:outline-none transition-colors"
                            />
                        </div>
                    </div>
                </div>

                {/* Body: three picks, or the full catalogue */}
                {view === 'picks' ? (
                    <div className="flex-1 overflow-y-auto px-7 py-6 space-y-3">
                        {picksLoading ? (
                            <div className="space-y-3" aria-live="polite">
                                {[0, 1, 2].map(i => (
                                    <div key={i} className="h-24 rounded-2xl bg-stone-100/70 animate-pulse" />
                                ))}
                            </div>
                        ) : picks.length === 0 ? (
                            <div className="text-center py-10">
                                <p className="text-sm text-stone-500">Nothing picked out yet — write an entry and we will have something for you tomorrow.</p>
                                <button
                                    onClick={() => setView('browse')}
                                    className="mt-4 text-sm font-semibold text-stone-700 underline underline-offset-4 hover:text-stone-900"
                                >
                                    Browse all prompts
                                </button>
                            </div>
                        ) : (
                            <>
                                {picks.map(p => {
                                    const isLocked = locked.has(p.promptId);
                                    const isChecked = isLocked || added.has(p.promptId);
                                    const color = categoryColor(p.category);
                                    return (
                                        <label
                                            key={p.promptId}
                                            className={`block p-4 rounded-2xl border transition-colors ${
                                                isLocked
                                                    ? 'bg-stone-50/70 border-stone-200 cursor-not-allowed'
                                                    : isChecked
                                                        ? 'bg-white border-stone-300 shadow-sm cursor-pointer'
                                                        : 'bg-white border-stone-200 hover:border-stone-300 cursor-pointer'
                                            }`}
                                            style={{ borderLeftWidth: '3px', borderLeftColor: color }}
                                        >
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={isLocked}
                                                    onChange={() => toggleId(p.promptId)}
                                                    className={`mt-1 h-4 w-4 rounded border-stone-300 focus:ring-stone-400 ${
                                                        isLocked ? 'text-stone-400 cursor-not-allowed' : 'text-stone-700'
                                                    }`}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1.5">
                                                        <span
                                                            className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                                                            style={{ backgroundColor: `${color}22`, color }}
                                                        >
                                                            {p.category}
                                                        </span>
                                                        {p.isHero && (
                                                            <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">
                                                                Today&apos;s reflection
                                                            </span>
                                                        )}
                                                        {isLocked && (
                                                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-stone-400 ml-auto">
                                                                <Lock size={10} />
                                                                In journal
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className={`text-[15px] leading-snug ${isLocked ? 'text-stone-500' : 'text-stone-800'}`}>
                                                        {p.simplePrompt}
                                                    </p>
                                                    {/* Only rendered when the backend had real evidence for it. */}
                                                    {p.reason?.text && (
                                                        <p className="mt-2 text-[11px] text-stone-400 leading-relaxed">
                                                            ↳ {p.reason.text}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                                <button
                                    onClick={() => setView('browse')}
                                    className="w-full mt-2 py-3 text-sm font-semibold text-stone-600 rounded-2xl border border-dashed border-stone-300 hover:bg-stone-50 hover:text-stone-800 transition-colors"
                                >
                                    Browse all {(catalog || []).length.toLocaleString()} prompts
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                <div className="flex flex-1 min-h-0">
                    <nav className="w-40 sm:w-48 border-r border-stone-100 overflow-y-auto py-3 px-2 space-y-0.5 shrink-0">
                        {categories.map(cat => {
                            const isActive = activeCategory === cat;
                            const color = cat === 'All' ? '#78716c' : categoryColor(cat);
                            return (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    className={`w-full text-left px-3 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                                        isActive
                                            ? 'bg-stone-100 text-stone-800'
                                            : 'text-stone-500 hover:bg-stone-50 hover:text-stone-700'
                                    }`}
                                >
                                    <span
                                        className="w-2 h-2 rounded-full shrink-0"
                                        style={{ backgroundColor: color }}
                                    />
                                    <span className="truncate">{cat}</span>
                                </button>
                            );
                        })}
                    </nav>

                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                        {filtered.length === 0 ? (
                            <div className="text-center text-sm text-stone-400 py-12">
                                No prompts match these filters.
                            </div>
                        ) : filtered.map(p => {
                            const isLocked = locked.has(p.id);
                            const isChecked = isLocked || added.has(p.id);
                            return (
                                <label
                                    key={p.id}
                                    className={`flex items-start gap-3 p-3 rounded-2xl transition-colors border ${
                                        isLocked
                                            ? 'bg-stone-50/70 border-stone-200 cursor-not-allowed'
                                            : isChecked
                                                ? 'bg-white border-stone-200 shadow-sm cursor-pointer'
                                                : 'border-transparent hover:bg-stone-50 cursor-pointer'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={isLocked}
                                        onChange={() => toggleId(p.id)}
                                        className={`mt-1 h-4 w-4 rounded border-stone-300 focus:ring-stone-400 ${
                                            isLocked ? 'text-stone-400 cursor-not-allowed' : 'text-stone-700'
                                        }`}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm leading-snug ${isLocked ? 'text-stone-500' : 'text-stone-700'}`}>{p.simplePrompt}</p>
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <span
                                                className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                                                style={{ backgroundColor: `${categoryColor(p.category)}22`, color: categoryColor(p.category) }}
                                            >
                                                {p.category}
                                            </span>
                                            <span className="text-[10px] text-stone-400 uppercase tracking-wide">
                                                {CADENCE_LABEL[p.primaryCadence] || p.primaryCadence}
                                            </span>
                                            {p.estimatedMinutes ? (
                                                <span className="text-[10px] text-stone-400">~{p.estimatedMinutes} min</span>
                                            ) : null}
                                            {isLocked && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-stone-400 ml-auto">
                                                    <Lock size={10} />
                                                    In journal
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </div>
                )}

                {/* Footer */}
                <div className="px-7 py-4 border-t border-stone-100 bg-white/60 flex items-center justify-end gap-3">
                    {view === 'browse' && picks.length > 0 && (
                        <button
                            onClick={() => setView('picks')}
                            className="mr-auto px-4 py-2 text-sm font-semibold text-stone-500 hover:text-stone-700 transition-colors"
                        >
                            ← For you
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-stone-500 hover:text-stone-700 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={added.size === 0}
                        className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors shadow-sm ${
                            added.size === 0
                                ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                                : 'bg-stone-800 text-white hover:bg-stone-700'
                        }`}
                    >
                        {added.size === 0
                            ? 'Nothing new to add'
                            : `Add: ${added.size} ${added.size === 1 ? 'Prompt' : 'Prompts'} to Journal`}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// Inline list of curated prompts — rendered inside the Today's Story card after the free-text editor.
// No own chrome; relies on `curate` lifted to the App scope so the page-level curator modal can also call it.
// Returns null when there's nothing curated so the merged card collapses cleanly.
const GuidedReflectionsSection = ({
    guidedPrompts, setGuidedPrompts,
    curate,
    setActiveSection, setDrawingTarget, drawingStoreRef,
    setActiveEditor,
    onAddInlinePhoto, onAddPrompts,
    voice,
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleRemove = (promptId) => {
        const remaining = guidedPrompts
            .filter(p => p.promptId !== promptId)
            .map((p, i) => ({ promptId: p.promptId, promptView: p.promptView, displayOrder: i }));
        curate(remaining);
    };

    const handleToggleView = (promptId) => {
        const updated = guidedPrompts.map((p, i) => ({
            promptId: p.promptId,
            promptView: p.promptId === promptId
                ? (p.promptView === 'research' ? 'simple' : 'research')
                : p.promptView,
            displayOrder: i,
        }));
        curate(updated);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = guidedPrompts.findIndex(p => p.reflectionId === active.id);
        const newIdx = guidedPrompts.findIndex(p => p.reflectionId === over.id);
        if (oldIdx === -1 || newIdx === -1) return;
        const reordered = arrayMove(guidedPrompts, oldIdx, newIdx);
        setGuidedPrompts(reordered.map((p, i) => ({ ...p, displayOrder: i })));
        curate(reordered.map((p, i) => ({
            promptId: p.promptId, promptView: p.promptView, displayOrder: i,
        })));
    };

    const handleChangeResponse = (reflectionId) => (val) => {
        setGuidedPrompts(prev => prev.map(p =>
            p.reflectionId === reflectionId ? { ...p, response: val } : p
        ));
    };

    if (guidedPrompts.length === 0) return null;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={guidedPrompts.map(p => p.reflectionId)}
                strategy={verticalListSortingStrategy}
            >
                <div>
                    {guidedPrompts.map(prompt => (
                        <GuidedPromptCard
                            key={prompt.reflectionId}
                            prompt={prompt}
                            onChangeResponse={handleChangeResponse(prompt.reflectionId)}
                            onToggleView={handleToggleView}
                            onRemove={handleRemove}
                            onFocus={() => setActiveSection('guidedReflections')}
                            onOpenDrawing={() => setDrawingTarget('guidedReflections')}
                            onAddInlinePhoto={onAddInlinePhoto}
                            onAddPrompts={onAddPrompts}
                            onActivate={setActiveEditor}
                            drawingStoreRef={drawingStoreRef}
                            voice={voice}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

// const VoiceTextArea = ({ label, value, onChange, placeholder, minHeight = "120px", onFocus }) => {
//     const [localValue, setLocalValue] = useState(value);
    
//     // Sync local value when prop changes (e.g. date switching)
//     useEffect(() => { setLocalValue(value); }, [value]);

//     // Handle manual typing
//     const handleManualChange = (e) => {
//         const val = e.target.value;
//         setLocalValue(val);
//         onChange(val); // Updates local state only
//     };

//     const handleVoiceResult = useCallback((final, interim) => {
//         if (final) {
//             const newValue = localValue + (localValue ? ' ' : '') + final;
//             setLocalValue(newValue);
//             onChange(newValue); // Updates local state only
//         }
//     }, [localValue, onChange]);

//     const { isListening, toggleListening, hasSupport } = useVoiceInput(handleVoiceResult);

//     return (
//         <div className="relative group">
//             <div className="flex justify-between items-center mb-3 px-1">
//                 <label className="text-sm font-bold text-stone-500 uppercase tracking-widest flex items-center gap-2">
//                     {label}
//                 </label>
//                 {hasSupport && (
//                     <button 
//                         onClick={toggleListening}
//                         className={`p-2 rounded-full transition-all duration-300 ${isListening ? 'bg-rose-50 text-rose-500 animate-pulse' : 'bg-stone-50 text-stone-400 hover:bg-stone-100 hover:text-stone-600'}`}
//                         title="Dictate"
//                     >
//                         {isListening ? <MicOff size={16} /> : <Mic size={16} />}
//                     </button>
//                 )}
//             </div>
//             <textarea
//                 value={localValue}
//                 onChange={handleManualChange}
//                 onFocus={onFocus}
//                 placeholder={placeholder}
//                 style={{ minHeight }}
//                 className={`w-full p-5 rounded-2xl border-none bg-stone-50/50 focus:bg-white focus:ring-2 focus:ring-stone-200/50 transition-all resize-none text-stone-700 placeholder-stone-300 text-lg leading-relaxed shadow-inner ${isListening ? 'ring-2 ring-rose-100' : ''}`}
//             />
//         </div>
//     );
// };

// const ImageUploader = ({ images = [], onImagesChange, userId, date }) => {
//     // console.log("ImageUploader fun inside :", images);
//     const fileInputRef = useRef(null);

//     const handleFileChange = async (e) => {
//         const file = e.target.files[0];
//         if (file) {
//             const reader = new FileReader();
//             reader.onloadend = () => {
//                 const img = new Image();
//                 img.src = reader.result;
//                 img.onload = async () => {
//                     const canvas = document.createElement('canvas');
//                     const MAX_WIDTH = 800;
//                     const scaleSize = MAX_WIDTH / img.width;
//                     canvas.width = MAX_WIDTH;
//                     canvas.height = img.height * scaleSize;
//                     const ctx = canvas.getContext('2d');
//                     ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
//                     const resizedBase64 = canvas.toDataURL('image/jpeg', 0.7);
    
//                     // Upload to backend → Google Storage → return URL
//                     const url = await uploadToBackend(resizedBase64);
    
//                     // Store URL instead of Base64
//                     onImagesChange([...images, url]);
//                 };
//             };
//             reader.readAsDataURL(file);
//         }
//     };

// import React, { useState, useRef } from 'react';
// import { Upload, X, Loader2 } from 'lucide-react';



//     return (
//         <div className="mt-2">
//             <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
//                 {images.map((img, idx) => ( 
                    
//                     <div key={idx} className="relative flex-shrink-0 group">
//                         <img src={img} alt="entry" className="h-28 w-28 object-cover rounded-2xl border border-stone-100 shadow-sm" />
//                         <button 
//                             onClick={() => removeImage(idx)}
//                             className="absolute -top-2 -right-2 bg-white text-rose-400 rounded-full p-1.5 shadow-sm hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"
//                         >
//                             <X size={14} />
//                         </button>
//                     </div>
//                 ))
//                 }
//                 <button 
//                     onClick={() => fileInputRef.current.click()}
//                     className="h-28 w-28 flex flex-col items-center justify-center border-2 border-dashed border-stone-200 rounded-2xl text-stone-400 hover:bg-stone-50 hover:border-stone-300 transition-all group"
//                 >
//                     <Upload size={24} className="group-hover:scale-110 transition-transform"/>
//                     <span className="text-[10px] font-bold mt-2 uppercase tracking-wide">Add Photo</span>
//                 </button>
//             </div>
//             <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
//         </div>
//     );
// };

// --- Memory Recall Helpers ---
const CHARS_PER_PAGE = 120;

function splitIntoPages(text) {
    if (!text) return [''];
    const pages = [];
    let start = 0;
    while (start < text.length) {
        let end = start + CHARS_PER_PAGE;
        if (end < text.length) {
            const lastSpace = text.lastIndexOf(' ', end);
            if (lastSpace > start) end = lastSpace;
        }
        pages.push(text.slice(start, end).trim());
        start = end;
    }
    return pages.length > 0 ? pages : [''];
}

const TAG_COLOR_CLASSES = {
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red:   'bg-red-50 text-red-600',
};

// --- Memory Card Component ---
function MemoryCard({ mem, onOpenJournal }) {
    // Live chat passes `chunk_text` (full body). Chat-history snapshots store
    // the same body under the same key going forward; older snapshots used
    // `excerpt`. Read both so old conversations stay readable.
    const pages = splitIntoPages(mem.chunk_text || mem.excerpt || "");
    const [activePage, setActivePage] = useState(0);

    return (
        <div
            className="min-w-[260px] max-w-[280px] bg-white rounded-2xl border border-stone-100 p-4 shadow-sm snap-start flex-shrink-0 flex flex-col"
            data-testid="memory-card"
        >
            <div className="text-[11px] uppercase tracking-wide font-sans font-bold text-stone-500 mb-1">
                {mem.relative_time} &middot; {mem.formatted_date}
            </div>
            <div className="flex gap-1 mb-2">
                {pages.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setActivePage(i)}
                        className={`h-1.5 w-1.5 rounded-full transition-colors ${i === activePage ? 'bg-stone-700' : 'bg-stone-300 hover:bg-stone-400'}`}
                        aria-label={`Page ${i + 1} of ${pages.length}`}
                        data-testid="page-dot"
                    />
                ))}
            </div>
            <p className="text-sm italic text-stone-600 font-serif leading-relaxed h-[4.5rem] overflow-hidden mb-3" data-testid="chunk-page">
                &ldquo;{pages[activePage]}{activePage < pages.length - 1 ? '...' : ''}&rdquo;
            </p>
            {mem.tag_label && (
                <span className={`text-xs px-3 py-1 rounded-full font-sans self-start ${TAG_COLOR_CLASSES[mem.tag_color] || TAG_COLOR_CLASSES.amber}`} data-testid="need-tag">
                    {mem.tag_label}
                </span>
            )}
            {mem.entry_iso_date && (
                <button
                    onClick={() => onOpenJournal?.(mem.entry_iso_date)}
                    className="mt-auto pt-3 self-start inline-flex items-center gap-1 text-[11px] font-sans font-semibold text-stone-500 hover:text-stone-700 transition-colors"
                    data-testid="memory-open-journal"
                >
                    View journal entry &rarr;
                </button>
            )}
        </div>
    );
}

// --- Memory Recall Component ---
function MemoryRecall({ memories, onOpenJournal }) {
    const [expanded, setExpanded] = useState(false);
    const scrollRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);

    // Only surface "scroll to see all" when the cards actually overflow the row.
    // Overflows on mobile / narrow chat columns; hidden on desktop when they fit.
    useEffect(() => {
        const el = scrollRef.current;
        if (!expanded || !el) {
            setIsScrollable(false);
            return;
        }
        const check = () => setIsScrollable(el.scrollWidth > el.clientWidth + 1);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [expanded, memories]);

    if (!memories || memories.length === 0) return null;

    return (
        <div className="mt-3 ml-4" data-testid="memory-recall">
            <button
                onClick={() => setExpanded(prev => !prev)}
                className="bg-white border border-stone-200 rounded-full px-4 py-2 flex items-center gap-2 text-sm font-sans text-stone-600 hover:border-stone-300 transition-colors"
                aria-label={expanded ? "hide memories" : `${memories.length} memories recalled`}
                data-testid="memory-toggle"
            >
                <span className="h-2 w-2 rounded-full bg-amber-700" />
                {expanded ? "hide memories" : `${memories.length} memories recalled`}
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {expanded && (
                <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="memory-panel">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-[10px] uppercase tracking-widest font-sans text-stone-400 font-bold">
                            Journal Entries Referenced
                        </span>
                        {isScrollable && (
                            <span className="text-[10px] font-sans text-stone-400">
                                scroll to see all &rarr;
                            </span>
                        )}
                    </div>
                    <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide" data-testid="memory-scroll">
                        {memories.map((mem) => (
                            <MemoryCard key={mem.id} mem={mem} onOpenJournal={onOpenJournal} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


// --- Feed View Component ---
// Fallback avatar for feed days with no photo/drawing. Audio-only and prompt-only
// days each get a distinct, warm-tinted glyph so a quick scroll reads at a glance;
// everything else keeps the quiet journal book. Purely presentational + memoized.
const FEED_ICONS = {
    audio:  { Icon: AudioLines,            grad: 'from-emerald-100 via-teal-50 to-emerald-50', fg: 'text-emerald-600' },
    prompt: { Icon: MessageCircleQuestion, grad: 'from-amber-100 via-orange-50 to-amber-50',   fg: 'text-amber-600' },
    text:   { Icon: BookOpen,              grad: 'from-stone-100 to-stone-50',                  fg: 'text-stone-400' },
};

const FeedFallbackIcon = memo(function FeedFallbackIcon({ kind }) {
    const { Icon, grad, fg } = FEED_ICONS[kind] || FEED_ICONS.text;
    return (
        <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${grad}`} aria-hidden="true">
            <Icon size={26} strokeWidth={1.75} className={fg} />
        </div>
    );
});

function FeedView({
    feedData,
    feedLoading,
    feedError,
    feedHasMore,
    feedStats,
    onLoadMore,
    onRefresh,
    onNavigateToJournal
}) {
    const [expandedEntries, setExpandedEntries] = useState(new Set());

    const toggleExpanded = (date) => {
        setExpandedEntries(prev => {
            const newSet = new Set(prev);
            if (newSet.has(date)) {
                newSet.delete(date);
            } else {
                newSet.add(date);
            }
            return newSet;
        });
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
            });
        }
    };

    const formatTagDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    };

    // Soothing, warm-shifted gradients per valence (design-system: sage + amber accents).
    const getValenceGradient = (valence) => {
        switch (valence) {
            case 'positive': return 'from-emerald-200 via-teal-100 to-amber-100';
            case 'mixed':    return 'from-amber-200 via-rose-100 to-orange-100';
            case 'negative': return 'from-indigo-200 via-slate-200 to-stone-200';
            case 'neutral':  return 'from-stone-200 via-stone-100 to-amber-50';
            default:         return 'from-stone-200 via-stone-100 to-stone-200';
        }
    };

    if (feedError) {
        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="text-center py-12">
                    <div className="text-4xl mb-4">😔</div>
                    <h2 className="text-xl font-semibold text-stone-700 mb-2">Unable to load memories</h2>
                    <p className="text-stone-500 mb-6">{feedError}</p>
                    <button
                        onClick={onRefresh}
                        className="px-6 py-3 bg-stone-800 text-white rounded-full font-medium hover:bg-stone-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header - Clean design matching feed_view_1.png */}
            <div className="text-center space-y-3 mb-6">
                <h1 className="text-lg font-bold text-stone-800 uppercase tracking-wide">MY JOURNAL<br />MEMORIES</h1>

                {feedStats && (
                    <div className="text-xs text-stone-500">
                        {feedStats.totalDays} entries • {feedStats.daysThisMonth} this month
                    </div>
                )}
            </div>

            {/* Feed Content */}
            {feedData.length === 0 && !feedLoading ? (
                <div className="text-center py-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center shadow-2xl border-4 border-white/40">
                        <div className="text-5xl">📖</div>
                    </div>
                    <h2 className="text-2xl font-bold text-stone-800 mb-3">Your memories await</h2>
                    <p className="text-stone-600 mb-8 max-w-md mx-auto leading-relaxed">Start journaling to see your thoughts and memories come alive in this beautiful feed</p>
                    <button
                        onClick={() => onNavigateToJournal(new Date().toISOString().split('T')[0])}
                        className="group px-8 py-4 bg-gradient-to-r from-stone-800 to-stone-700 text-white rounded-full font-semibold hover:from-stone-700 hover:to-stone-600 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                    >
                        <span className="flex items-center gap-2">
                            Begin Your Journey
                            <div className="w-5 h-5 rounded-full bg-white/20 group-hover:bg-white/30 transition-colors duration-300 flex items-center justify-center">
                                <Plus size={12} />
                            </div>
                        </span>
                    </button>
                </div>
            ) : (
                <div className="space-y-8">
                    {/* Refresh Button */}
                    <div className="flex justify-center">
                        <button
                            onClick={onRefresh}
                            disabled={feedLoading}
                            className="flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full shadow-lg text-sm text-stone-600 hover:text-stone-800 hover:shadow-xl transition-all duration-300 disabled:opacity-50 border border-white/40"
                        >
                            {feedLoading ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <RotateCcw size={16} />
                            )}
                            {feedLoading ? 'Loading...' : 'Refresh'}
                        </button>
                    </div>

                    {/* Feed Items — pill cards matching feed_view_1.png */}
                    <div className="space-y-10 max-w-md mx-auto pt-4">
                        {feedData.map((entry, index) => {
                            const isExpanded = expandedEntries.has(entry.date);
                            // Preview priority: typed note → voice/echo transcript →
                            // reflection answer → chunk summary → a kind-aware label.
                            const fallbackLabel = entry.iconKind === 'audio'
                                ? 'Voice note'
                                : entry.iconKind === 'prompt'
                                    ? 'Guided reflection'
                                    : 'Journal entry';
                            const shortText = entry.journalPreview
                                || entry.voiceTranscriptPreview
                                || entry.promptPreview
                                || entry.previewText
                                || entry.summaryText
                                || fallbackLabel;
                            const offsetClass = index % 2 === 1 ? 'ml-10' : 'mr-10';

                            return (
                                <div key={entry.date}>
                                    <div className={`relative ${offsetClass}`}>
                                        {/* Date label (italic) sits above the pill */}
                                        <div className="absolute -top-3 left-6 text-xs italic text-stone-500 bg-transparent px-1 z-10">
                                            {formatTagDate(entry.date)}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => toggleExpanded(entry.date)}
                                            data-testid="feed-entry"
                                            className="w-full flex items-center gap-3 p-2 pr-5 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow text-left"
                                        >
                                            <div className={`flex-shrink-0 p-[3px] rounded-full bg-gradient-to-br ${getValenceGradient(entry.valence)} shadow-sm`}>
                                                <div className="w-20 h-20 rounded-full overflow-hidden bg-white ring-2 ring-white">
                                                    {entry.iconImage ? (
                                                        <img
                                                            src={entry.iconImage}
                                                            alt={`Memory from ${formatTagDate(entry.date)}`}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <FeedFallbackIcon kind={entry.iconKind} />
                                                    )}
                                                </div>
                                            </div>
                                            <p className="flex-1 text-xs text-stone-700 leading-snug line-clamp-4">
                                                {shortText}
                                            </p>
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div className="mt-4 bg-white rounded-2xl border border-stone-200 p-6 shadow-lg animate-in fade-in slide-in-from-top-4 duration-300">
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-start">
                                                    <h3 className="text-lg font-semibold text-stone-800">
                                                        {formatDate(entry.date)}
                                                    </h3>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onNavigateToJournal(entry.date);
                                                        }}
                                                        className="px-4 py-2 bg-stone-800 text-white text-sm rounded-full hover:bg-stone-700 transition-colors"
                                                    >
                                                        View Full Entry
                                                    </button>
                                                </div>

                                                {entry.journalPreview && (
                                                    <div className="prose prose-sm max-w-none text-stone-700">
                                                        <p>{entry.journalPreview}</p>
                                                    </div>
                                                )}

                                                {entry.tags && entry.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {entry.tags.slice(0, 5).map((tag, ti) => (
                                                            <span
                                                                key={ti}
                                                                className="px-3 py-1 text-xs bg-stone-100 text-stone-600 rounded-full"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Load More Button - Clean */}
                    {feedHasMore && (
                        <div className="flex justify-center mt-8">
                            <button
                                onClick={onLoadMore}
                                disabled={feedLoading}
                                className="flex items-center gap-2 px-6 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full text-sm font-medium transition-colors disabled:opacity-50"
                            >
                                {feedLoading ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        Load More
                                        <ChevronDown size={14} />
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Full-screen photo viewer — phone-style: swipe / arrow-key / chevron navigation,
// a position counter, and native share (Web Share API → WhatsApp etc. on mobile,
// download fallback on desktop). Portaled to <body> so it escapes the card stack.
// Rendered only while open, so the keyboard + scroll-lock effects mount/unmount
// exactly with the open/close lifecycle.
const PhotoViewer = ({ images, index, onClose, onIndexChange }) => {
    const total = images.length;
    const url = images[index];
    const touchStartX = useRef(null);
    const [isSharing, setIsSharing] = useState(false);

    const goPrev = useCallback(() => onIndexChange(Math.max(0, index - 1)), [index, onIndexChange]);
    const goNext = useCallback(() => onIndexChange(Math.min(total - 1, index + 1)), [index, total, onIndexChange]);

    // Keyboard (desktop): ←/→ navigate, Esc closes.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
            else if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [goPrev, goNext, onClose]);

    // Lock body scroll while open so iOS Safari doesn't scroll the page behind the overlay.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    if (!url) return null;

    const onTouchStart = (e) => { touchStartX.current = e.touches[0]?.clientX ?? null; };
    const onTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
        if (Math.abs(dx) > 40) { if (dx > 0) goPrev(); else goNext(); }
        touchStartX.current = null;
    };

    // Fetch the (signed-URL) original as a File and open the OS share sheet →
    // WhatsApp / Messages / etc. Desktop or browsers without file-share fall back
    // to a download. We never share the raw signed URL — it expires (~1h) and leaks.
    const handleShare = async () => {
        if (isSharing) return;
        setIsSharing(true);
        try {
            const resp = await fetch(url);
            const blob = await resp.blob();
            const type = blob.type || 'image/jpeg';
            const ext = (type.split('/')[1] || 'jpg').split('+')[0];
            const file = new File([blob], `memory-${index + 1}.${ext}`, { type });
            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file] });
            } else {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = file.name;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(a.href);
            }
        } catch (err) {
            if (err?.name !== 'AbortError') console.error('Share failed:', err);
        } finally {
            setIsSharing(false);
        }
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center select-none"
            onClick={onClose}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            <img
                src={url}
                alt=""
                className="max-w-[92vw] max-h-[86vh] rounded-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                style={{ objectFit: 'contain' }}
                draggable={false}
            />

            {/* Top bar: counter · share · close */}
            <div
                className="absolute top-4 inset-x-0 flex items-center justify-between px-5"
                onClick={(e) => e.stopPropagation()}
            >
                <span className="text-white/80 text-sm font-sans tabular-nums">
                    {total > 1 ? `${index + 1} / ${total}` : ''}
                </span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-all disabled:opacity-40"
                        onClick={handleShare}
                        disabled={isSharing}
                        aria-label="Share photo"
                    >
                        {isSharing ? <Loader2 size={18} className="animate-spin" /> : <Share2 size={18} />}
                    </button>
                    <button
                        type="button"
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-all"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Prev / Next — clamped at the ends (iPhone-style, no wrap) */}
            {index > 0 && (
                <button
                    type="button"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-all"
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    aria-label="Previous photo"
                >
                    <ChevronLeft size={24} />
                </button>
            )}
            {index < total - 1 && (
                <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-all"
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    aria-label="Next photo"
                >
                    <ChevronRight size={24} />
                </button>
            )}
        </div>,
        document.body
    );
};

// --- Main App ---
export default function App() {
    // --- State ---
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState(''); 
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isGuest, setIsGuest] = useState(false);
    const [guestBannerVisible, setGuestBannerVisible] = useState(true);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [email, setEmail] = useState('');
    
    // Auth UI State
    const [isRegisterMode, setIsRegisterMode] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [authScreen, setAuthScreen] = useState('signin'); // 'signin'|'signup'|'forgot'|'checkEmail'|'reset'
    const [resetToken, setResetToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    // showPasswordPanel moved to ProfilePage.jsx
    const [regErrors, setRegErrors] = useState({});
    const [dailyQuote, setDailyQuote] = useState("");
    const [loadingQuote, setLoadingQuote] = useState(true);
    // const moodBackgrounds = {
    //     calm: "from-stone-100 via-amber-50 to-stone-200",
    //     hopeful: "from-amber-100 via-rose-50 to-sky-100",
    //     resilient: "from-slate-200 via-stone-100 to-emerald-100",
    //     focused: "from-slate-100 via-stone-200 to-slate-300",
    //     reflective: "from-indigo-100 via-stone-100 to-amber-100",
    //   };
      const moodBackgrounds = {
        calm: {
          gradient: "from-stone-100 via-stone-200 to-stone-300",
          glow: "circle_at_50%_30%",
          grain: "opacity-[0.02]",
        },
        hopeful: {
          gradient: "from-amber-100 via-rose-100 to-sky-200",
          glow: "circle_at_30%_20%",
          grain: "opacity-[0.025]",
        },
        resilient: {
          gradient: "from-slate-300 via-stone-400 to-emerald-300",
          glow: "circle_at_70%_30%",
          grain: "opacity-[0.03]",
        },
        focused: {
          gradient: "from-slate-200 via-slate-300 to-slate-400",
          glow: "circle_at_50%_20%",
          grain: "opacity-[0.02]",
        },
        reflective: {
          gradient: "from-indigo-200 via-stone-200 to-amber-200",
          glow: "circle_at_40%_25%",
          grain: "opacity-[0.03]",
        },
      };
      
    const [aiMood, setAiMood] = useState("calm");
    const moodStyle = moodBackgrounds[aiMood] ?? moodBackgrounds.calm;
    const [quoteMode, setQuoteMode] = useState("personal");

    const wisdomStyles = ["frostian", "stoic", "humanist", "poetic"];
    const randomSIndex = Math.floor(Math.random() * wisdomStyles.length);
    const [styleIndex, setStyleIndex] = useState(randomSIndex);
    const style = wisdomStyles[styleIndex];

    const wisdomThemes = [
        "choice",
        "perseverance",
        "clarity",
        "patience",
        "effort",
        "uncertainty",
        "discipline",
        "growth",
        "acceptance",
      ];
    const randomtIndex = Math.floor(Math.random() * wisdomThemes.length);
    const [themeIndex, setThemeIndex] = useState(randomtIndex);
    const theme = wisdomThemes[themeIndex];

    const nextWisdomStyle = () => {
        setStyleIndex((prev) => (prev + 1) % wisdomStyles.length);
      };
    const nextTheme = () => {
        setThemeIndex((prev) => (prev + 1) % wisdomThemes.length);
      };

    // Registration Form State
    const [regForm, setRegForm] = useState({ 
        name: '', goal: '', age: '', occupation: '',
        gender: '', cellphone: '', city: '', activities: '',
        password: '', confirmPassword: '',
        preferred_language: DEFAULT_LANGUAGE_CODE
    });

    const [activeTab, setActiveTab] = useState(() => localStorage.getItem('activeTab') || 'home');
    const queryClient = useQueryClient();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        () => localStorage.getItem('journal-sidebar-collapsed') === '1'
    );
    useEffect(() => {
        localStorage.setItem('journal-sidebar-collapsed', sidebarCollapsed ? '1' : '0');
    }, [sidebarCollapsed]);
    // Mobile entries drawer — opened from the header pill (next to Home), lives in JournalSidebar.
    const [entriesOpen, setEntriesOpen] = useState(false);
    // Remembers the last non-profile tab so tapping the profile avatar a second time returns you there.
    const previousTabRef = useRef(activeTab !== 'profile' ? activeTab : 'home');
    useEffect(() => {
        if (activeTab !== 'profile') previousTabRef.current = activeTab;
    }, [activeTab]);

    // Data State
    const [profile, setProfile] = useState({
        name: '', goal: '', age: '', dob: '', occupation: '',
        gender: '', cellphone: '', city: '', activities: '',
        preferred_language: DEFAULT_LANGUAGE_CODE
    });
    // Password change state moved to ProfilePage.jsx

    // Store the last successfully saved profile to compare against for changes
    const [lastSavedProfile, setLastSavedProfile] = useState({});
    const [isProfileDirty, setIsProfileDirty] = useState(false);
    const isNameValid = profile.name.trim().length > 0;
    
    // Use getLocalToday to ensure we start with the correct local date string
    const [currentDate, setCurrentDate] = useState(getLocalToday());
    
    // allJournals acts as the "Server State Cache". We compare current edits against this to detect changes.
    const [allJournals, setAllJournals] = useState({});
    // GR embedding_status per date — only written by loadJournals, never overwritten by saves.
    // Drawing canvas target — which editor section opened the canvas
    const [drawingTarget, setDrawingTarget] = useState(null); // 'notes' | null
    const [voiceCaptureOpen, setVoiceCaptureOpen] = useState(false);
    // Home's "Explore & Archives" pill opens the Voice tab. The nonce bumps on
    // every tap so the tab remounts and refetches, rather than showing whatever
    // list it held when the user last left it. (It used to carry a sub-tab name
    // as well; the Notes/Echoes split was merged away on 2026-09-23.)
    const [voiceNav, setVoiceNav] = useState({ n: 0 });
    const openVoice = () => {
        setVoiceNav(v => ({ n: v.n + 1 }));
        handleTabChange('voice');
    };
    // Bumped after Start Writing curates a prompt so the journal reloads today's
    // guided reflections even when currentDate was already today.
    const [guidedReloadNonce, setGuidedReloadNonce] = useState(0);
    const pendingScrollPromptIdRef = useRef(null);
    const notesEditorRef = useRef(null);
    const drawingStoreRef = useRef(new Map()); // id → dataURL, survives iOS drag src-strip

    // Today's Story inline photo upload (replaces standalone Visual Memories section)
    const photoFileInputRef = useRef(null);
    // Photo upload target: 'strip' (Today's Story day-level images array) or
    // { el, onChange } (insert <img> inline into a prompt answer editor).
    const photoTargetRef = useRef('strip');
    const [isPhotoUploading, setIsPhotoUploading] = useState(false);
    // {done, total} while a batch uploads; null when idle. Drives the "Uploading n of m" hint.
    const [photoUploadProgress, setPhotoUploadProgress] = useState(null);
    // Last upload error message (shown under the strip so a failed add isn't silent).
    const [photoError, setPhotoError] = useState(null);
    // Index into journalEntry.images for the full-screen viewer; null when closed.
    const [expandedPhotoIndex, setExpandedPhotoIndex] = useState(null);

    // Feed State (React Query)
    const {
        data: feedQueryData,
        fetchNextPage,
        hasNextPage,
        isFetching: feedLoading,
        isError: feedIsError,
        refetch: refetchFeed,
    } = useInfiniteQuery({
        queryKey: ['feed', userId],
        queryFn: ({ pageParam = 0 }) =>
            api.get(`/user/${userId}/feed`, { params: { limit: 20, offset: pageParam } })
                .then(res => res.data),
        getNextPageParam: (lastPage, allPages) =>
            lastPage.hasMore ? allPages.flatMap(p => p.entries || []).length : undefined,
        initialPageParam: 0,
        enabled: isLoggedIn && !!userId,
        staleTime: 2 * 60 * 1000,
    });

    const { data: feedStats } = useQuery({
        queryKey: ['feedStats', userId],
        queryFn: () => api.get(`/user/${userId}/feed/stats`).then(res => res.data),
        enabled: isLoggedIn && !!userId,
        staleTime: 5 * 60 * 1000,
    });

    const feedData = feedQueryData?.pages.flatMap(p => p.entries || []) ?? [];
    const feedError = feedIsError ? 'Failed to load your journals' : null;
    const feedHasMore = hasNextPage ?? true;
    
    const [aiDataConsent, setAiDataConsent] = useState(null); // null | true | false
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [profileRequestTab, setProfileRequestTab] = useState(null);
    // { reason } when an AI opt-in prompt is pending, null otherwise. One piece
    // of state for every surface, so two nudges can never stack.
    const [aiNudge, setAiNudge] = useState(null);
    // The passive half of the same story: shown after a save made with AI off
    // when the cooldown has already spent this week's ask.
    const [aiOffNotice, setAiOffNotice] = useState(false);
    useEffect(() => {
        if (!aiOffNotice) return undefined;
        const t = setTimeout(() => setAiOffNotice(false), 6000);
        return () => clearTimeout(t);
    }, [aiOffNotice]);

    // Smart Reflection card — null until a save earns one, then { reflection, invitation }.
    const [smartReflection, setSmartReflection] = useState(null);
    // The day's stored reflection, or null. Drives whether the toolbar button is
    // offered at all — see the fetch effect below.
    const [dayReflection, setDayReflection] = useState(null);

    // Journal Entry State (The current "Draft")
    const [journalEntry, setJournalEntryRaw] = useState({
        notes: '', gratitude: '', images: []
    });
    // Wrap setter to persist drafts to sessionStorage on every edit.
    // Only call this for user-initiated changes (onChange, clear, undo).
    // For system loads (server fetch, logout reset) use setJournalEntryRaw directly.
    const setJournalEntry = useCallback((valOrFn) => {
        setJournalEntryRaw(prev => {
            const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
            try { sessionStorage.setItem(`journal-draft-${currentDate}`, JSON.stringify(next)); } catch {}
            return next;
        });
    }, [currentDate]);

    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [activeSection, setActiveSection] = useState('notes');
    const [isCuratorOpen, setIsCuratorOpen] = useState(false);
    const [, setUndoHistory] = useState({ notes: [], gratitude: [] });
    const [, setGuidedUndoHistory] = useState([]); // stack of response[] snapshots

    // Last-focused rich editor — drives the journal-level formatting toolbar.
    // Shape: { el: HTMLElement, onChange, onOpenDrawing, onAddPhoto, onAddPrompts, enterFocusMode } | null
    const [activeEditor, setActiveEditor] = useState(null);
    const activeEditorRefAdapter = useRef(null);
    useEffect(() => {
        activeEditorRefAdapter.current = activeEditor?.el || null;
    }, [activeEditor]);

    // Voice input lifted out of RichEditor — one SpeechRecognition instance for the whole journal,
    // routed to whichever editor is currently active. The voice button lives in the journal toolbar
    // (and is forwarded down for the focus-mode header, since focus mode covers the toolbar).
    const activeEditorRef = useRef(activeEditor);
    useEffect(() => { activeEditorRef.current = activeEditor; }, [activeEditor]);
    const journalVoice = useVoiceInput(
        useCallback((final) => {
            const ae = activeEditorRef.current;
            if (!final || !ae?.el) return;
            ae.el.focus();
            document.execCommand('insertText', false, final);
            ae.onChange?.(sanitizeHTML(ae.el.innerHTML));
        }, [])
    );

    // Guided Reflections — DB-backed state (user-curated, per-date; redesign 2026-05-24)
    const [guidedPrompts, setGuidedPrompts] = useState([]);
    // shape: [{ reflectionId, promptId, promptOrgNo, category, primaryCadence,
    //           simplePrompt, researchPrompt, promptView, displayOrder,
    //           status, response, completedAt }]
    const [guidedToast, setGuidedToast] = useState(null);

    // Prompt catalog from ic.prompts — loaded once per session, used by curator modal
    const promptCatalogQuery = useQuery({
        queryKey: ['promptCatalog'],
        queryFn: async () => {
            const { data } = await api.get('/prompts');
            return data?.prompts || [];
        },
        staleTime: Infinity,
        enabled: !!userId,
    });

    // Prompt picks — the curator's opening view. Three prompts chosen for this
    // user on this date, each carrying the reason it was chosen. Fetched only
    // while the modal is open: 3,697 prompts are too many to open onto a list,
    // and the picks are the answer to "what should I write about today".
    // Pick 1 is the same prompt the Home hero shows, by construction.
    const promptPicksQuery = useQuery({
        queryKey: ['promptPicks', userId, currentDate],
        queryFn: async () => {
            const { data } = await api.get(`/user/${userId}/prompt-picks`, {
                params: { date: currentDate, limit: 3 },
            });
            return data?.picks || [];
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!userId && isCuratorOpen,
    });

    // Home "Reflections of the Day" — the recommended daily prompt. The backend
    // caches per day, so refetching is cheap and returns the same prompt until a
    // newer entry appears. Keyed by today's local date so it rolls at midnight.
    const reflectionOfTheDayQuery = useQuery({
        queryKey: ['reflectionOfTheDay', userId, getLocalToday()],
        queryFn: async () => {
            // Time-of-day is derived here (client knows the timezone) so the backend
            // can prefer a prompt phrased for this moment. Not in the query key —
            // it's a soft preference and the pick stays stable for the day.
            const h = new Date().getHours();
            const tod = h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
            const { data } = await api.get(`/user/${userId}/reflection-of-the-day`, {
                params: { date: getLocalToday(), tod },
            });
            return data;
        },
        staleTime: 1000 * 60 * 30,
        enabled: !!userId,
    });

    // Home Companion card: a line about the latest entry + one follow-up
    // question. The backend decides once per new entry and caches, so a refetch
    // is a primary-key read unless something new has been written. Guests always
    // get the generic card, so they don't ask. Refetched after a journal save.
    const companionOpenerQuery = useQuery({
        queryKey: ['companionOpener', userId],
        queryFn: async () => {
            const { data } = await api.get(`/user/${userId}/companion-opener`);
            return data;
        },
        staleTime: 1000 * 60 * 2,
        enabled: !!userId && !isGuest,
    });

    // Home "welcome back" pulse — core values + journaling rhythm for the banner.
    // Gentle greeting data, not live state, so it's cached generously.
    const journalPulseQuery = useQuery({
        queryKey: ['journalPulse', userId],
        queryFn: async () => {
            const { data } = await api.get(`/user/${userId}/journal-pulse`);
            return data;
        },
        staleTime: 1000 * 60 * 60,
        enabled: !!userId,
    });

    // Tracks responses as last loaded/saved — { [reflectionId]: response } — to detect real changes
    const savedGuidedResponsesRef = useRef({});

    // Floating toolbar scroll state
    const [toolbarFloating, setToolbarFloating] = useState(false);


    const [aiResponse, setAiResponse] = useState(null);
    const [isAiLoading, setIsAiLoading] = useState(false);

    // Chat State
    const CHAT_SEED_GREETING = { role: 'ai', content: "Hello. I'm here to listen without judgment. How are you feeling right now?" };
    const CHAT_RESUME_GREETING = { role: 'ai', content: "Picking up where we left off — I'm here whenever you are." };
    const [chatHistory, setChatHistory] = useState([CHAT_SEED_GREETING]);
    const [showChatHistory, setShowChatHistory] = useState(false);
    const [chatInput, setChatInput] = useState("");
    const chatInputRef = useRef(null);
    // Set by Home's "Open Dialogue" and spent by the next send: the id of the
    // opener sitting at the top of a fresh thread, so the server can save it as
    // the conversation's first message. Cleared whenever the thread changes.
    const pendingOpenerIdRef = useRef(null);
    // After send (chatInput resets to ''), collapse the auto-grown textarea back to one line.
    useEffect(() => {
        if (chatInput === '' && chatInputRef.current) {
            chatInputRef.current.style.height = 'auto';
        }
    }, [chatInput]);
    const [chatCount, setChatCount] = useState(0);
    const CHAT_LIMIT = 100;
    // Transient bottom-of-screen toast — fires only when used-count crosses each 10%
    // boundary (chatCount = 10, 20, 30, ...). Tracking "tens used" instead of remaining
    // avoids the prior off-by-one that fired the toast after the very first message.
    const [chatToast, setChatToast] = useState(null);
    const lastToastTenRef = useRef(0);
    useEffect(() => {
        if (chatCount <= 0) return;
        const usedTens = Math.floor(chatCount / 10);
        if (usedTens <= lastToastTenRef.current) return;
        lastToastTenRef.current = usedTens;
        const remaining = CHAT_LIMIT - chatCount;
        if (remaining <= 0) return;
        setChatToast(`${remaining} message${remaining === 1 ? '' : 's'} left in this chat`);
    }, [chatCount]);
    useEffect(() => {
        if (!chatToast) return;
        const t = setTimeout(() => setChatToast(null), 3500);
        return () => clearTimeout(t);
    }, [chatToast]);
    const chatEndRef = useRef(null);
    const dateInputRef = useRef(null); // Ref to programmatic date picker access
    const inPageToolbarRef = useRef(null);

    // V2 streaming chat state. Guest path (api.post '/api/ai/guest-chat') is unaffected.
    const [conversationId, setConversationId] = useState(() => {
        if (typeof window === "undefined") return null;
        const existing = window.sessionStorage.getItem("mw_conversation_id");
        if (existing) return existing;
        const fresh = (typeof crypto !== "undefined" && crypto.randomUUID)
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        window.sessionStorage.setItem("mw_conversation_id", fresh);
        return fresh;
    });
    // Temporary chat: the turn goes out with a null conversationId, which is
    // exactly what makes the backend skip persistence (persistChatTurn
    // early-returns without one). Nothing is written, so nothing needs purging.
    const [isTemporaryChat, setIsTemporaryChat] = useState(false);
    const chat = useChatPipelineStream({
        userId,
        conversationId: isTemporaryChat ? null : conversationId,
    });
    const v2FoldRef = useRef(false);

    useEffect(() => {
        if (isTemporaryChat) return; // the echo is the null we sent on purpose
        const echoed = chat.state.conversationId;
        if (echoed && echoed !== conversationId) {
            setConversationId(echoed);
            try { window.sessionStorage.setItem("mw_conversation_id", echoed); } catch {}
        }
    }, [chat.state.conversationId, conversationId, isTemporaryChat]);

    // When the V2 stream completes, fold the final reply into chatHistory.
    useEffect(() => {
        if (chat.state.isStreaming) { v2FoldRef.current = false; return; }
        if (v2FoldRef.current) return;
        if (chat.state.stages.length === 0 && !chat.state.streamedText && !chat.state.error) return;
        v2FoldRef.current = true;

        if (chat.state.error) {
            setChatHistory(prev => [...prev, { role: 'ai', content: "I'm here with you, even if the connection stumbled." }]);
        } else if (chat.state.streamedText) {
            setChatHistory(prev => [...prev, {
                role: 'ai',
                content: chat.state.streamedText,
                memories: chat.state.memories,
                isDistress: chat.state.isDistress,
            }]);
        }
        chat.reset();
    }, [chat.state.isStreaming, chat.state.error, chat.state.streamedText, chat.state.memories, chat.state.isDistress, chat.state.stages.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Chat history (save / resume / delete) handlers ---------------------
    const handleNewChat = useCallback(() => {
        chat.abort();
        cancelSpeech();
        const fresh = (typeof crypto !== "undefined" && crypto.randomUUID)
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        try { window.sessionStorage.setItem("mw_conversation_id", fresh); } catch {}
        setConversationId(fresh);
        setChatHistory([CHAT_SEED_GREETING]);
        pendingOpenerIdRef.current = null;
        setChatCount(0);
        lastToastTenRef.current = 0;
        chat.reset();
    }, [chat]); // eslint-disable-line react-hooks/exhaustive-deps

    // Switching mode always starts a fresh thread — a saved thread can't be
    // retroactively un-saved, and a temporary one can't be back-filled. Turning
    // temporary *off* lets the current temporary thread go for good, so that
    // direction asks first.
    const handleToggleTemporary = useCallback((next) => {
        if (!next && isTemporaryChat && chatHistory.length > 1) {
            const ok = window.confirm(
                "Discard this temporary chat?\n\nIt was never saved, so leaving temporary mode lets it go for good."
            );
            if (!ok) return;
        }
        setIsTemporaryChat(next);
        handleNewChat();
    }, [isTemporaryChat, chatHistory.length, handleNewChat]);

    const handlePickChat = useCallback((detail) => {
        if (!detail?.conversation?.id) return;
        chat.abort();
        cancelSpeech();
        const cid = detail.conversation.id;
        pendingOpenerIdRef.current = null;
        setIsTemporaryChat(false); // resuming a saved thread leaves temporary mode
        try { window.sessionStorage.setItem("mw_conversation_id", cid); } catch {}
        setConversationId(cid);
        const hydrated = [
            CHAT_RESUME_GREETING,
            ...(detail.messages || []).map((m) => ({
                role: m.role === 'user' ? 'user' : 'ai',
                content: m.content,
                memories: m.memories || undefined,
            })),
        ];
        setChatHistory(hydrated);
        const userTurns = (detail.messages || []).filter((m) => m.role === 'user').length;
        setChatCount(userTurns);
        lastToastTenRef.current = Math.floor(userTurns / 10);
        chat.reset();
    }, [chat]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Chat voice I/O (server-side Whisper via /api/voice/transcribe; system TTS for output) ---
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [ttsEnabled, setTtsEnabled] = useState(false);
    const [voiceError, setVoiceError] = useState(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);

    const startRecording = useCallback(async () => {
        setVoiceError(null);
        // Release the iOS audio session held by SpeechSynthesis so getUserMedia
        // can capture cleanly — otherwise the next transcription returns silence.
        cancelSpeech();
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
            recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
                setIsTranscribing(true);
                try {
                    const text = await transcribe(blob);
                    if (text) setChatInput((prev) => (prev ? `${prev} ${text}` : text));
                    else setVoiceError('No speech detected');
                } catch (err) {
                    console.error('Transcription failed', err);
                    setVoiceError(err?.response?.data?.error || err?.message || 'Transcription failed');
                } finally {
                    setIsTranscribing(false);
                }
            };
            recorderRef.current = recorder;
            recorder.start();
            setIsRecording(true);
        } catch (err) {
            console.error('Mic access denied', err);
            setVoiceError(err?.message || 'Mic access denied');
            setIsRecording(false);
        }
    }, []);

    const stopRecording = useCallback(() => {
        if (recorderRef.current && recorderRef.current.state === 'recording') {
            recorderRef.current.stop();
            setIsRecording(false);
        }
    }, []);

    // Read out completed assistant replies when TTS is on. One-shot: auto-disables
    // after speaking so a user who toggled it in private isn't surprised by audio later.
    useEffect(() => {
        if (!ttsEnabled) return;
        const last = chatHistory[chatHistory.length - 1];
        if (last?.role === 'ai' && last.content) {
            speak(last.content).then(() => setTtsEnabled(false));
        }
    }, [chatHistory, ttsEnabled]);

    useEffect(() => () => cancelSpeech(), []);

    useEffect(() => {
        if (activeTab === "profile" && isProfileDirty) {
          setProfile(lastSavedProfile);
          setIsProfileDirty(false);
        }
      }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // Detect password reset token in URL on first load
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('resetToken');
        if (token) {
            setResetToken(token);
            setAuthScreen('reset');
            // Clean the token from the URL without reloading
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    // Handle OAuth redirect return (Google, etc.)
    useEffect(() => {
        const handleOAuthReturn = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;
            setAuthLoading(true);
            setAuthError('');
            try {
                // Only honoured when this sign-in creates the account; for a
                // returning user the backend keeps the language they saved.
                const pendingLanguage = localStorage.getItem(PENDING_LANGUAGE_KEY);
                localStorage.removeItem(PENDING_LANGUAGE_KEY);
                const res = await api.post('/auth/social-callback', {
                    supabaseToken: session.access_token,
                    preferred_language: normalizeLanguageCode(pendingLanguage),
                });
                const { token, userId: uid } = res.data;
                if (!token || !uid) throw new Error('Invalid response');
                localStorage.setItem('token', token);
                localStorage.setItem('userId', uid);
                localStorage.setItem('sessionStart', Date.now());
                setUserId(uid);
                setEmail(session.user.email || '');
                setIsLoggedIn(true);
                const profileData = await fetchProfile(uid);
                setProfile(profileData);
                setLastSavedProfile(profileData);
                setIsProfileDirty(false);
                setAiDataConsent(profileData.ai_data_consent);
                if (profileData.ai_data_consent === null) setShowConsentModal(true);
                await loadJournals(uid);
                // Disabled 2026-08-12: dailyQuote is set but never rendered, so
                // this was an LLM call per sign-in for nothing. Endpoint and
                // backend chain are intact — uncomment when Home shows a quote.
                // try {
                //     const q = await api.post('/api/ai/quote', { userId: uid, mode: 'wisdom' });
                //     setDailyQuote(q.data.quote);
                // } catch { setDailyQuote('Take today at the pace your heart allows.'); }
                // finally { setLoadingQuote(false); }
            } catch {
                setAuthError('Sign-in failed. Please try again.');
            } finally {
                setAuthLoading(false);
                await supabase.auth.signOut(); // we use custom JWT from here on
            }
        };
        handleOAuthReturn();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    
    // useEffect(() => {
    //     const fetchQuote = async () => {
    //     console.log("Before login in App")
    //     try {
    //         const res = await api.post("/api/ai/quote", {
    //         userId,
    //         mode: "personal",
    //         theme: theme,
    //         style: style
    //         });
    //         setDailyQuote(res.data.quote);
    //     } catch {
    //         setDailyQuote("Take today at the pace your heart allows.");
    //     } finally {
    //         setLoadingQuote(false);
    //     }
    //     };
    //     if (activeTab == "home") {fetchQuote()};
    // }, []);


    // --- Backend Logic ---

    const fetchProfile = async (uid) => {
        const res = await api.get(`/user/${uid}`);
        const data = res.data.profile || res.data;
        return {
            name: data.name || '',
            email: data.email || '',
            goal: data.goal || '',
            age: data.age || '',
            dob: data.dob ? data.dob.slice(0, 10) : '',
            occupation: data.profession || '',
            gender: data.gender || '',
            cellphone: data.cellphone || '',
            city: data.city || '',
            activities: data.activities || '',
            // Always a code the picker can match — the column is NOT NULL, but
            // a pre-migration cached response would arrive without it.
            preferred_language: normalizeLanguageCode(data.preferred_language),
            ai_data_consent: data.ai_data_consent ?? null,
            // Signed by the server, good for about an hour. A rendering detail
            // with a short shelf life — never persisted, refreshed on each load.
            avatar_url: data.avatar_url || '',
        };
    };

    // The photo is written by its own endpoint, so a change is already saved by
    // the time it gets here: mirror it into `lastSavedProfile` too, or the form
    // would light up "unsaved changes" for something nobody edited.
    const applyProfilePhoto = (url) => {
        setProfile(p => ({ ...p, avatar_url: url }));
        setLastSavedProfile(p => (p ? { ...p, avatar_url: url } : p));
    };

    const loadJournals = async (uid) => {
        try {
            const res = await api.get(`/user/${uid}/journals`);
            const entries = res.data || [];
            const structuredData = {};
            const seenSections = new Set(); // guard: entries are newest-first, skip older duplicates

            entries.forEach(entry => {
                const d = entry.date || entry.entry_date;
                if (!d) return;

                const sectionKey = `${d}::${entry.section}`;
                if (seenSections.has(sectionKey)) return;
                seenSections.add(sectionKey);

                if (!structuredData[d]) {
                    structuredData[d] = {
                        notes: '',
                        gratitude: '',
                        guidedReflections: '',
                        grEmbeddingStatus: null,
                        images: [],
                        voiceNotes: []
                    };
                }

                if (entry.section === 'Daily Notes') {
                    structuredData[d].notes = entry.content;
                }

                else if (entry.section === 'Reflections') {
                    // Legacy section — capture raw content; merged into notes after the loop.
                    structuredData[d]._legacyReflectionsRaw = entry.content || '';
                    structuredData[d]._legacyReflectionsMerged = true;
                }

                else if (entry.section === 'Gratitude') {
                    structuredData[d].gratitude = entry.content;
                }
    
                else if (entry.section === 'Guided Reflections') {
                    structuredData[d].guidedReflections = entry.content;
                    structuredData[d].grEmbeddingStatus = entry.embedding_status;
                }

                else if (entry.section === 'Voice Note') {
                    // Master row's content_encrypted is the JSON path list (denormalized
                    // mirror of children); the children array is what the UI renders.
                    structuredData[d].voiceNotes = Array.isArray(entry.voice_notes)
                        ? entry.voice_notes
                        : [];
                }

                else if (entry.section === 'Images') {
                    try {
                        let raw = entry.content;

                        // Handle backend returning double-escaped JSON arrays:
                        if (typeof raw === "string") {
                            raw = raw.replace(/""/g, '"');      // Normalize weird double quotes
                            raw = raw.replace(/^"|"$/g, "");    // Remove surrounding quotes if present
    
                            if (raw.startsWith("[") && raw.endsWith("]")) {
                                raw = JSON.parse(raw);
                            }
                        }
                        // Raw is now parsed JSON
                        if (Array.isArray(raw)) {
                            structuredData[d].images = raw.map(item => {
                                if (typeof item !== "string") return null;
                                // 1. Remove wrapping quotes
                                let cleaned = item.replace(/^"+|"+$/g, "");
                                // 2. Sanity check: only valid URL or base64
                                if (cleaned.startsWith("http") || cleaned.startsWith("data:image")) {
                                    // console.log("Claned ", cleaned)
                                    return cleaned;
                                }
                        
                                return null;
                            }).filter(Boolean);
                        } else {
                            structuredData[d].images = [];
                        }
                        // console.log("structuredData ", structuredData[d], d)
    
                    } catch (e) {
                        console.warn("Failed parsing images:", entry.content, e);
                        structuredData[d].images = [];
                    }
                }
                // console.log("structuredData ", d, " ", structuredData[d]);
            });

            // Post-process: fold any legacy Reflections content into notes (Daily Notes first, then
            // a blank line, then Reflections). Sweep happens lazily on the next save.
            Object.keys(structuredData).forEach(d => {
                if (structuredData[d]._legacyReflectionsMerged) {
                    structuredData[d].notes = [structuredData[d].notes, structuredData[d]._legacyReflectionsRaw]
                        .filter(Boolean)
                        .join('\n\n');
                    delete structuredData[d]._legacyReflectionsRaw;
                }
            });

            setAllJournals(structuredData);

        } catch (err) {
            console.error("Journal load failed", err);
        }
    };
    
    const updateProfileField = (field, value) => {
        setProfile(p => {
            const next = { ...p, [field]: value };
            return next;
        });
        setIsProfileDirty(true);
    };
    // --- INTELLIGENT PERSISTENCE LOGIC ---

    const persistProfile = async (currentProfile) => {
        // Compare with last saved state
        const hasChanged = JSON.stringify(currentProfile) !== JSON.stringify(lastSavedProfile);
        
        if (!hasChanged) {
            console.log("No profile changes detected. Skipping save.");
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                user_id: userId,
                name: currentProfile.name,
                age: currentProfile.age,
                dob: currentProfile.dob || null,
                goal: currentProfile.goal,
                profession: currentProfile.occupation,
                gender: currentProfile.gender,
                cellphone: currentProfile.cellphone,
                city: currentProfile.city,
                activities: currentProfile.activities,
                preferred_language: normalizeLanguageCode(currentProfile.preferred_language)
            };
            // console.log("Profile update before call ", payload)
            await api.post(`/user`, payload);
            console.log("Profile persisted to DB");
            setLastSavedProfile(currentProfile); // Update cache
            setIsProfileDirty(false);
        } catch (e) {
            console.error("Save profile failed", e);
        } finally {
            setTimeout(() => setIsSaving(false), 500);
        }
    };

    const navigateToJournal = (date) => {
        setCurrentDate(date);
        setActiveTab('journal');
    };


    const persistJournal = async (currentEntry, dateStr) => {
        // Compare current draft (journalEntry) against server cache (allJournals)
        const originalEntry = allJournals[dateStr] || { notes: '', gratitude: '', images: [] };
        const hasLegacyReflectionsMerged = !!originalEntry._legacyReflectionsMerged;

        const payloadEntries = [];

        // Only add fields that have actually changed. If a legacy Reflections row is being swept on
        // this save, force a Daily Notes write so the merged body lands in the DB before we drop the
        // legacy row — otherwise the merged text would silently disappear on next read.
        if ((currentEntry.notes || '') !== (originalEntry.notes || '') || hasLegacyReflectionsMerged) {
            payloadEntries.push({ date: dateStr, section: "Daily Notes", content: currentEntry.notes });
        }
        if ((currentEntry.gratitude || '') !== (originalEntry.gratitude || '')) {
            payloadEntries.push({ date: dateStr, section: "Gratitude", content: currentEntry.gratitude });
        }
        if (JSON.stringify(currentEntry.images || []) !== JSON.stringify(originalEntry.images || [])) {
            payloadEntries.push({ date: dateStr, section: "Images", content: JSON.stringify( currentEntry.images.map(url => url.trim()) ) });
        }
        if (hasLegacyReflectionsMerged) {
            payloadEntries.push({ date: dateStr, section: "Reflections", content: '', _delete: true });
        }

        // Guided-reflection diffs ride in the same payload so commit + embedding-enqueue
        // happen in one backend transaction (no Task A / Task B race).
        const changedGR = guidedPrompts.filter(p => {
            const saved = savedGuidedResponsesRef.current[p.reflectionId] ?? '';
            return (p.response || '') !== saved;
        });
        const guidedResponses = changedGR.map(p => ({
            reflectionId: p.reflectionId,
            promptId: p.promptId,
            response: p.response || '',
        }));

        if (payloadEntries.length === 0 && guidedResponses.length === 0) {
            console.log(`No journal or GR changes detected for ${dateStr}. Skipping save.`);
            return;
        }

        setIsSaving(true);
        try {
            const { data: saveData } = await api.post(`/user/${userId}/journals/bulk`, {
                entries: payloadEntries,
                guidedResponses,
                guidedDate: dateStr,
            });
            console.log(`Journal for ${dateStr} persisted: ${payloadEntries.length} entries + ${guidedResponses.length} GR responses`);

            // The entry is already stored; AI is the only thing it missed out
            // on. Exactly one of two things happens — the dialog when the
            // cooldown allows an ask, otherwise a passive line. The cooldown is
            // there to stop us asking too often, not to stop us telling: going
            // silent would leave every later save looking identical to one made
            // with AI on.
            if (saveData?.aiConsented === false) {
                if (aiNudgeAllowed()) {
                    setAiNudge({
                        reason: 'Your entry is saved. Turning on AI also tags it and lets chat draw on what you have written.',
                    });
                } else {
                    setAiOffNotice(true);
                }
            }

            if (payloadEntries.length > 0) {
                setAllJournals(prev => {
                    const nextDate = { ...prev[dateStr], ...currentEntry };
                    delete nextDate._legacyReflectionsMerged;
                    return { ...prev, [dateStr]: nextDate };
                });
                try { sessionStorage.removeItem(`journal-draft-${dateStr}`); } catch {}
                queryClient.invalidateQueries({ queryKey: ['journal-index', userId] });
            }
            // Outside the payloadEntries branch — a guided-reflections-only save
            // (no Daily Notes/Gratitude change) must still refresh these. The
            // Home Companion card talks about the latest entry, and the rhythm
            // card's word/entry counts and dots now include guided reflections
            // too (the ic.journals master row for that section never holds the
            // real text — see backend/server_pg.js), so either kind of save
            // needs to invalidate both, not just a payloadEntries-only save.
            if (payloadEntries.length > 0 || guidedResponses.length > 0) {
                queryClient.invalidateQueries({ queryKey: ['companionOpener', userId] });
                queryClient.invalidateQueries({ queryKey: ['journalPulse', userId] });
            }

            // Bring the GR baseline forward so the next diff is accurate.
            changedGR.forEach(p => {
                savedGuidedResponsesRef.current[p.reflectionId] = p.response || '';
            });
        } catch (e) {
            console.error("Save journal failed", e);
        } finally {
            setIsSaving(false);
        }
    };

    // Single source of truth for any change to the curated set (add / remove / reorder / view-toggle).
    // Lifted from GuidedReflectionsSection so the page-level CuratePromptsModal can call it.
    // Preserves in-progress (unsaved) responses across the server roundtrip.
    const curateGuidedReflections = async (selectionsInOrder) => {
        const localResponses = Object.fromEntries(
            guidedPrompts.map(p => [`${p.promptId}`, p.response || ''])
        );
        try {
            const res = await api.post(`/user/${userId}/guided-reflections/curate`, {
                date: currentDate,
                selections: selectionsInOrder,
            });
            const fresh = (res.data?.prompts || []).map(p => {
                const localResp = localResponses[`${p.promptId}`];
                return localResp !== undefined && localResp !== ''
                    ? { ...p, response: localResp }
                    : p;
            });
            setGuidedPrompts(fresh);
            const baseline = { ...savedGuidedResponsesRef.current };
            const keepIds = new Set(fresh.map(p => p.reflectionId));
            Object.keys(baseline).forEach(id => {
                if (!keepIds.has(Number(id)) && !keepIds.has(id)) delete baseline[id];
            });
            fresh.forEach(p => {
                if (!(p.reflectionId in baseline) && !(`${p.reflectionId}` in baseline)) {
                    baseline[p.reflectionId] = p.response || '';
                }
            });
            savedGuidedResponsesRef.current = baseline;
        } catch (err) {
            console.error('Curate failed:', err);
        }
    };

    const handleConfirmCuration = (promptIds) => {
        const existingByPromptId = Object.fromEntries(guidedPrompts.map(p => [p.promptId, p]));
        const selections = promptIds.map((promptId, idx) => {
            const existing = existingByPromptId[promptId];
            return {
                promptId,
                promptView: existing?.promptView || 'simple',
                displayOrder: idx,
            };
        });
        curateGuidedReflections(selections);
        setIsCuratorOpen(false);
    };

    const triggerAddPrompts = () => setIsCuratorOpen(true);

    const handleManualSave = async () => {
        await persistJournal(journalEntry, currentDate);
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 1800);
        offerSmartReflection(currentDate);
    };

    // Whether the day on screen has a Smart Reflection, read off the journal
    // index — already fetched for the sidebar and shared through react-query's
    // cache, so this costs no request of its own and no per-day round trip.
    //
    // `dayReflection` is a lazy cache of the TEXT, filled on first open (or by a
    // save that just generated one) and cleared when the day changes so one
    // day's card can never be shown for another.
    const { data: journalIndexEntries = [] } = useJournalIndex(userId);
    const dayHasReflection = useMemo(
        () => journalIndexEntries.some(e => e.date === currentDate && e.hasReflection === true),
        [journalIndexEntries, currentDate],
    );
    useEffect(() => { setDayReflection(null); }, [currentDate]);

    // Opens it: from memory when we already have the text, otherwise one fetch,
    // then held. The button is only enabled when there is something to open.
    const showStoredReflection = useCallback(async () => {
        if (dayReflection) { setSmartReflection(dayReflection); return; }
        if (!userId) return;
        try {
            const { data } = await api.get(`/user/${userId}/journal-reflection`, {
                params: { date: currentDate },
            });
            const text = (data?.reflection || '').trim();
            if (!text) return;
            const found = {
                reflection: text,
                invitation: (data?.invitation || '').trim(),
                date: currentDate,
                feedback: data?.feedback || null,
            };
            setDayReflection(found);
            setSmartReflection(found);
        } catch {
            // Nothing to show; the button simply did nothing this once.
        }
    }, [dayReflection, userId, currentDate]);

    // Smart Reflection — the short note shown once after the first substantive
    // save of a day. Deliberately fired ONLY from the manual save: the auto-saves
    // on tab change, date change, and sign-out are housekeeping the user did not
    // ask for, and a card appearing as someone signs out would be a bug.
    //
    // Not awaited by the caller, and it never surfaces a failure. The server
    // answers with a null reflection for every ordinary reason to stay quiet —
    // AI off, already shown today, too little written, a heavy moment — and a
    // network failure is treated identically. The entry is already saved.
    const offerSmartReflection = useCallback(async (dateStr) => {
        if (!userId) return;
        try {
            const { data } = await api.post(`/user/${userId}/journal-reflection`, { date: dateStr });
            const text = (data?.reflection || '').trim();
            if (!text) return;
            const fresh = {
                reflection: text,
                invitation: (data?.invitation || '').trim(),
                date: dateStr,
                feedback: null,
            };
            setSmartReflection(fresh);
            // The day now has one, so the toolbar can offer it from here on.
            setDayReflection(fresh);
        } catch {
            // Silence is the designed outcome here, not a swallowed bug.
        }
    }, [userId]);

    // Like/dislike on the open Smart Reflection card. Optimistic, and best-effort
    // like the rest of this feature: a failed vote just doesn't stick, no error
    // surfaced over a card that only ever existed as a bonus.
    const handleSmartReflectionFeedback = useCallback(async (action) => {
        if (!userId || !smartReflection?.date) return;
        const { date } = smartReflection;
        const next = action === 'clear' ? null : action;
        setSmartReflection(prev => (prev ? { ...prev, feedback: next } : prev));
        setDayReflection(prev => (prev && prev.date === date ? { ...prev, feedback: next } : prev));
        try {
            await api.post(`/user/${userId}/journal-reflection/feedback`, { date, action });
        } catch {
            // Silent failure — the vote just didn't stick; nothing to show for it.
        }
    }, [userId, smartReflection]);

    // Upload one file to GCS as an untouched original (no canvas, no re-encode) and
    // return its fresh signed URL. Preserves exact bytes + format (JPEG/PNG/HEIC).
    // The owner is derived server-side from the JWT, so no userId is sent.
    const uploadOriginalPhoto = async (file) => {
        const formData = new FormData();
        formData.append('image', file, file.name || `memory-${Date.now()}.jpg`);
        formData.append('date', currentDate);
        // Do NOT set Content-Type manually — axios/browser must set
        // `multipart/form-data; boundary=…` itself, or the backend can't parse the
        // body (multer sees no file → 400). This was the likely cause of new photos
        // silently failing to attach.
        const res = await api.post(`/api/uploadImage`, formData);
        if (!res.data?.url) throw new Error('Backend did not return image URL');
        return res.data.url;
    };

    // Photo upload handler — uploads up to 10 originals per pick, one request each,
    // sequentially so only a single file is ever in flight (gentle on memory + the
    // signed-URL throughput). Each result is revealed as it lands (progressive).
    // Routes via `photoTargetRef`: 'strip' → appends each URL to journalEntry.images
    // (Today's Story strip); { el, onChange } → inserts <img src="..."> inline into a
    // prompt answer editor (.rich-editor img CSS styles them as inline thumbnails).
    const handlePhotoFileChange = async (e) => {
        // Read the selected files FIRST — clearing the input below wipes e.target.files.
        const picked = Array.from(e.target.files || []);
        const target = photoTargetRef.current;
        photoTargetRef.current = 'strip'; // reset for next call
        // Clear AFTER capturing files so re-picking the same file(s) still fires onChange.
        if (photoFileInputRef.current) photoFileInputRef.current.value = '';

        if (picked.length === 0) return;

        // Cap the accepted files: at most MAX_PHOTOS_PER_BATCH per pick, and — for the
        // Today's Story strip — never past the day's remaining slots (MAX_PHOTOS_PER_DAY).
        // Inline prompt images live in contenteditable and aren't counted toward the day cap.
        const remainingForDay = target === 'strip'
            ? Math.max(0, MAX_PHOTOS_PER_DAY - (journalEntry.images?.length || 0))
            : MAX_PHOTOS_PER_BATCH;
        const allowed = Math.min(MAX_PHOTOS_PER_BATCH, remainingForDay);
        const files = picked.slice(0, allowed);
        if (files.length === 0) {
            // Files were picked but none accepted — explain rather than no-op silently.
            setPhotoError(`You've reached the ${MAX_PHOTOS_PER_DAY}-photo limit for this day.`);
            return;
        }

        setIsPhotoUploading(true);
        setPhotoUploadProgress({ done: 0, total: files.length });
        setPhotoError(null);
        let failures = 0;
        let lastErrMsg = null;

        for (let i = 0; i < files.length; i++) {
            try {
                const url = await uploadOriginalPhoto(files[i]);
                if (target === 'strip') {
                    setJournalEntry(prev => {
                        const curr = Array.isArray(prev.images) ? prev.images : [];
                        return { ...prev, images: [...curr, url] };
                    });
                } else if (target?.el && target?.onChange) {
                    target.el.focus();
                    document.execCommand('insertHTML', false, `<img src="${url}">`);
                    target.onChange(sanitizeHTML(target.el.innerHTML));
                }
            } catch (err) {
                failures += 1;
                // Surface the backend's reason (e.g. "Image is too large (max 25MB)")
                // so a failed add isn't a silent empty strip.
                lastErrMsg = err?.response?.data?.error || err?.message || 'Upload failed';
                console.error('Photo upload failed:', err);
            } finally {
                setPhotoUploadProgress(prev => (prev ? { ...prev, done: prev.done + 1 } : prev));
            }
        }

        setIsPhotoUploading(false);
        setPhotoUploadProgress(null);
        if (failures > 0) {
            setPhotoError(
                failures === files.length
                    ? `Couldn't add ${failures === 1 ? 'the photo' : 'photos'}: ${lastErrMsg}`
                    : `${failures} of ${files.length} photos couldn't be added: ${lastErrMsg}`
            );
        }
    };

    const triggerPhotoUpload = () => {
        // Day cap reached — don't bother opening the picker (strip shows "15 / 15").
        if ((journalEntry.images?.length || 0) >= MAX_PHOTOS_PER_DAY) return;
        photoTargetRef.current = 'strip';
        photoFileInputRef.current?.click();
    };

    // Inline insert into a specific prompt's contenteditable (passed in from GuidedPromptCard).
    const triggerInlinePhotoUpload = (el, onChange) => {
        if (!el || !onChange) return;
        photoTargetRef.current = { el, onChange };
        photoFileInputRef.current?.click();
    };

    const removePhoto = (idx) => {
        setJournalEntry(prev => ({
            ...prev,
            images: (Array.isArray(prev.images) ? prev.images : []).filter((_, i) => i !== idx)
        }));
    };

    const handleClearSection = () => {
        if (activeSection === 'guidedReflections') {
            const hasContent = guidedPrompts.some(p => (p.response || '').length > 0);
            if (!hasContent) return;
            setGuidedUndoHistory(prev => [...prev.slice(-4), guidedPrompts.map(p => p.response || '')]);
            setGuidedPrompts(prev => prev.map(p => ({ ...p, response: '' })));
            return;
        }
        const current = journalEntry[activeSection] || '';
        if (!current) return;
        setUndoHistory(prev => ({
            ...prev,
            [activeSection]: [...prev[activeSection].slice(-4), current]
        }));
        setJournalEntry(prev => ({ ...prev, [activeSection]: '' }));
        console.log("Calling setJournalEntry inside handleClearSection");
    };

    // --- Navigation & Logic Handlers ---

    const handleTabChange = async (newTab) => {
        if (newTab === activeTab) return;

        // Auto-save logic based on which tab we are LEAVING
        // if (activeTab === 'profile') {
        //     await persistProfile(profile);
        // } else if (activeTab === 'journal') {
        //     await persistJournal(journalEntry, currentDate);
        // }
        if (activeTab === 'journal') {
            await persistJournal(journalEntry, currentDate);
        }

        setActiveTab(newTab);
        localStorage.setItem('activeTab', newTab);
        setProfileRequestTab(null);
    };

    // Like / dislike on the Home hero prompt. `reflectionBusy` drives a soft
    // skeleton while a dislike fetches the next prompt. Like is a quiet ack.
    const [reflectionBusy, setReflectionBusy] = useState(false);
    const handleDailyPromptFeedback = async (action) => {
        const rec = reflectionOfTheDayQuery.data;
        if (!rec?.promptId) return;
        const today = getLocalToday();
        const h = new Date().getHours();
        const tod = h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
        try {
            if (action === 'dislike') setReflectionBusy(true);
            const { data } = await api.post(`/user/${userId}/reflection-of-the-day/feedback`, {
                date: today, tod, promptId: rec.promptId, action,
            });
            // Dislike returns the next prompt (its whole near-duplicate group skipped)
            // — swap it into the cache so the card updates immediately + persists.
            if (action === 'dislike' && data?.promptId) {
                queryClient.setQueryData(['reflectionOfTheDay', userId, today], data);
            }
        } catch (err) {
            console.error('Reflection feedback failed:', err);
        } finally {
            if (action === 'dislike') setReflectionBusy(false);
        }
    };

    // Start Writing (Home "Reflections of the Day") — curate the recommended
    // prompt into TODAY's guided reflections, then open the Journal on it.
    // Curation is a reconcile, so we merge into the existing set (never send the
    // single prompt alone, which would soft-delete the user's other prompts).
    const handleStartWriting = async () => {
        const rec = reflectionOfTheDayQuery.data;
        const promptId = rec?.promptId;
        const today = getLocalToday();
        if (promptId) {
            // Engaging with a prompt is the implicit "like" — record it, fire-and-forget,
            // so it never blocks writing. (No response is intentionally not recorded.)
            handleDailyPromptFeedback('like');
            try {
                const { data } = await api.get(`/user/${userId}/guided-reflections/${today}`);
                const existing = data?.prompts || [];
                const already = existing.some(p => p.promptId === promptId);
                let selections = existing.map(p => ({
                    promptId: p.promptId,
                    promptView: p.promptView || 'simple',
                    displayOrder: p.displayOrder ?? 0,
                }));
                if (!already) {
                    selections = [{ promptId, promptView: 'simple', displayOrder: 0 }, ...selections];
                }
                selections = selections.map((s, i) => ({ ...s, displayOrder: i }));
                await api.post(`/user/${userId}/guided-reflections/curate`, { date: today, selections });
                pendingScrollPromptIdRef.current = promptId;
            } catch (err) {
                console.error('Start Writing curation failed:', err);
            }
        }
        if (currentDate !== today) {
            await handleDateChange(today);
        }
        setGuidedReloadNonce(n => n + 1); // force today's guided reflections to reload
        handleTabChange('journal');
    };

    // Discuss with AI (Home "Reflections of the Day") — the other way to take the
    // prompt up: land on the Companion with today's question already asked, so
    // the reply is arriving as the tab opens.
    //
    // Three things make it a no-op send rather than a second turn: no consent
    // (the chat tab's AiGate is what the user should see, not a message fired
    // into a surface they haven't opted into), a turn already streaming, and the
    // same question already in the thread — tapping again from Home should
    // return you to the answer you have, not ask for it twice.
    const handleDiscussWithAI = () => {
        const rec = reflectionOfTheDayQuery.data;
        const { fallback } = REFLECTION_CADENCE[rec?.cadence] || REFLECTION_CADENCE.daily;
        const text = (rec?.simplePrompt || fallback).trim();
        handleTabChange('chat');
        if (!aiDataConsent) return;
        if (chat.state.isStreaming) return;
        if (chatHistory.some(m => m.role === 'user' && (m.content || '').trim() === text)) return;
        handleChatSubmit(text);
    };

    // Home Companion card: the two ways into chat.
    //
    // Tap to Chat (orb): a clean thread with the composer focused. A thread that
    // already holds a user message is kept in history and a new one started;
    // an untouched thread is simply reused, so repeated taps never pile up
    // empty chats.
    const handleCompanionTapToChat = () => {
        handleTabChange('chat');
        if (chat.state.isStreaming) return;
        if (chatHistory.some(m => m.role === 'user')) handleNewChat();
        setTimeout(() => chatInputRef.current?.focus(), 60);
    };

    // Open Dialogue: a new thread where the AI speaks first with exactly what
    // the card showed (acknowledgement + question for a personal opener, the
    // generic question otherwise), so the user only has to answer. A personal
    // opener's id rides on the first send, and the server saves the opener
    // ahead of that turn. Without consent, the chat tab shows its AI gate and
    // nothing is seeded. Tapping again while that same opener is on screen
    // just returns to it.
    const handleCompanionOpenDialogue = () => {
        const op = companionOpenerQuery.data;
        const personal = op?.kind === 'personal';
        const text = personal ? composeOpenerMessage(op) : GENERIC_OPENER_QUESTION;
        handleTabChange('chat');
        if (!aiDataConsent) return;
        if (chat.state.isStreaming) return;
        if (chatHistory.some(m => m.role === 'ai' && m.content === text)) return;
        handleNewChat();
        setChatHistory([{ role: 'ai', content: text }]);
        pendingOpenerIdRef.current = personal ? op.id : null;
        setTimeout(() => chatInputRef.current?.focus(), 60);
    };

    const handleDateChange = async (newDateStr) => {
        if (newDateStr === currentDate) return;
        
        // Save the OLD date's entry before switching
        await persistJournal(journalEntry, currentDate);
        
        setCurrentDate(newDateStr);
        console.log("current date set to HDC", newDateStr);
    };

    // Helper to safely navigate dates avoiding timezone issues
    const navigateDate = (offset) => {
        const [y, m, d] = currentDate.split('-').map(Number);
        const dateObj = new Date(y, m - 1, d + offset);
        const newYear = dateObj.getFullYear();
        const newMonth = String(dateObj.getMonth() + 1).padStart(2, '0');
        const newDay = String(dateObj.getDate()).padStart(2, '0');
        const newDateStr = `${newYear}-${newMonth}-${newDay}`;
        
        // Prevent navigating to future dates
        if (newDateStr > getLocalToday()) return;
        
        handleDateChange(newDateStr);
    };

    // const handleLogin = async () => {
    //     if (!userId.trim() || !password.trim()) {
    //         setAuthError("Please enter both User ID and Password.");
    //         return;
    //     }
    //     setAuthLoading(true);
    //     setAuthError('');
    //     try {
    //         const profileData = await fetchProfile(userId);
    //         setProfile(profileData);
    //         setLastSavedProfile(profileData); // Init change tracking
    //         await loadJournals(userId);
    //         setIsLoggedIn(true);
    //     } catch (e) {
    //         if (e.response && e.response.status === 404) setAuthError("Account not found. Please create one.");
    //         else setAuthError("Connection failed. Is the backend running?");
    //         console.log("Error login:", e.response)
    //     } finally {
    //         setAuthLoading(false);
    //     }
    // };

    const handleForgotPassword = async () => {
        if (!email.trim()) { setAuthError('Please enter your email address.'); return; }
        setAuthLoading(true);
        setAuthError('');
        try {
            await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
            setAuthScreen('checkEmail');
        } catch {
            setAuthError('Something went wrong. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (newPassword.length < 8) { setAuthError('Password must be at least 8 characters.'); return; }
        if (newPassword !== confirmNewPassword) { setAuthError('Passwords do not match.'); return; }
        setAuthLoading(true);
        setAuthError('');
        try {
            await api.post('/auth/reset-password', { token: resetToken, newPassword });
            setResetToken('');
            setNewPassword('');
            setConfirmNewPassword('');
            setAuthScreen('signin');
            setAuthError('');
            // Brief success signal via authError repurposed — use a separate state-free approach
            setTimeout(() => setAuthError('✓ Password updated — please sign in.'), 50);
        } catch (e) {
            setAuthError(e?.response?.data?.error || 'Reset failed. The link may have expired.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setAuthLoading(true);
        setAuthError('');
        try {
            localStorage.setItem(PENDING_LANGUAGE_KEY, normalizeLanguageCode(regForm.preferred_language));
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: window.location.origin,
                    queryParams: { prompt: 'select_account' },
                },
            });
            if (error) throw error;
            // Page redirects to Google — loading state intentionally stays
        } catch {
            setAuthError('Google sign-in failed. Please try again.');
            setAuthLoading(false);
        }
    };

    const handleLogin = async (loginEmail, loginPassword) => {
        const emailToUse = loginEmail ?? email;
        const passwordToUse = loginPassword ?? password;
        // console.log("handle Login ", emailToUse, passwordToUse)
        if (!emailToUse.trim() || !passwordToUse.trim()) {
          setAuthError("Please enter both Email and Password.");
          return;
        }
      
        setAuthLoading(true);
        setAuthError("");
        const emailNormalized = emailToUse.trim().toLowerCase();
        console.log("For login ", emailNormalized, emailToUse)
      
        try {
          const res = await api.post("/auth/login", {
            email: emailNormalized,
            password : passwordToUse,
          });
      
          const { token, userId } = res.data;
        //   console.log("After login token, id ", token, userId)
          if (!token || !userId) {
                console.log("Either user or password not valid")
                throw new Error("Invalid login response");
            }
      
          // Store token & userId
          localStorage.setItem("token", token);
          localStorage.setItem("userId", userId);
          localStorage.setItem("sessionStart", Date.now());
      
          setUserId(userId);
          setIsLoggedIn(true);
      
          // Profile + journals have no data dependency on each other — run in parallel.
          const [profileData] = await Promise.all([
              fetchProfile(userId),
              loadJournals(userId),
          ]);
          setProfile(profileData);
          setLastSavedProfile(profileData);
          setIsProfileDirty(false); //it starts clean.
          setAiDataConsent(profileData.ai_data_consent);
          if (profileData.ai_data_consent === null) setShowConsentModal(true);

          // Disabled 2026-08-12 — see the note at the sign-in call above.
          // api.post("/api/ai/quote", { userId, mode: "personal", theme, style })
          //     .then(r => setDailyQuote(r.data.quote))
          //     .catch(() => setDailyQuote("Take today at the pace your heart allows."))
          //     .finally(() => setLoadingQuote(false));
      
        } catch (e) {
          setAuthError("Invalid email or password");
        } finally {
          setAuthLoading(false);
        }
    };

    const handleGuestLogin = async () => {
        setAuthLoading(true);
        setAuthError('');
        try {
            const res = await api.post('/auth/guest');
            const { token, userId: guestUserId } = res.data;

            localStorage.setItem('token', token);
            localStorage.setItem('userId', guestUserId);
            localStorage.setItem('isGuest', 'true');
            localStorage.setItem('sessionStart', Date.now());

            setUserId(guestUserId);
            setIsGuest(true);
            setGuestBannerVisible(true);
            setIsLoggedIn(true);
            setAiDataConsent(true);
            setProfile(prev => ({ ...prev, name: 'Guest' }));

            // Disabled 2026-08-12 — see the note at the sign-in call above.
            // api.post('/api/ai/quote', { userId: guestUserId, mode: 'personal', theme, style })
            //     .then(q => setDailyQuote(q.data.quote))
            //     .catch(() => setDailyQuote('Take today at the pace your heart allows.'))
            //     .finally(() => setLoadingQuote(false));

            await loadJournals(guestUserId);
        } catch (e) {
            setAuthError('Could not start guest session. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    // const handleRegister = async () => {
    //     if (!userId.trim() || !regForm.name.trim() || !regForm.password.trim()) {
    //         setAuthError("User ID, Name, and Password are required.");
    //         return;
    //     }
    //     if (regForm.password !== regForm.confirmPassword) {
    //         setAuthError("Passwords do not match.");
    //         return;
    //     }
    //     setAuthLoading(true);
    //     setAuthError('');

    //     const newProfile = {
    //         user_id: userId,
    //         password: regForm.password,
    //         name: regForm.name,
    //         goal: regForm.goal,
    //         age: regForm.age,
    //         profession: regForm.occupation,
    //         gender: regForm.gender,
    //         cellphone: regForm.cellphone,
    //         city: regForm.city,
    //         activities: regForm.activities
    //     };

    //     try {
    //         await api.post(`${BACKEND_URL}/user`, newProfile);
    //         const savedData = { ...regForm, occupation: regForm.occupation };
    //         setProfile(savedData); 
    //         setLastSavedProfile(savedData); // Init change tracking
    //         await loadJournals(userId);
    //         setIsLoggedIn(true);
    //     } catch (e) {
    //         setAuthError("Registration failed. ID might be taken.");
    //     } finally {
    //         setAuthLoading(false);
    //     }
    // };

    const handleRegister = async () => {

        const emailNormalized = email.trim().toLowerCase();

        if (!email.trim() || !regForm.password.trim()) {
          setAuthError("Email and Password are required.");
          return;
        }
      
        if (regForm.password !== regForm.confirmPassword) {
          setAuthError("Passwords do not match.");
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          setAuthError("Please enter a valid email address.");
          return;
          }
      
        setAuthLoading(true);
        setAuthError("");
      
        // Mandatory fields for registration
        const errors = {};
        if (!regForm.name.trim()) errors.name = "Name is required.";

        if (Object.keys(errors).length > 0) {
            setRegErrors(errors);
            setAuthLoading(false);   // <-- FIX: stop spinner
            return; // stop registration
        } else {
            setRegErrors({});
        }

        try {
          const res = await api.post("/auth/register", {
            email: emailNormalized,
            password: regForm.password,
            name: regForm.name,
            gender: regForm.gender,
            age: regForm.age,
            occupation: regForm.occupation,
            goal: regForm.goal,
            city: regForm.city,
            cellphone: regForm.cellphone,
            activities: regForm.activities,
            preferred_language: normalizeLanguageCode(regForm.preferred_language)
          });
        const data = res.data;
        console.log("register response ", data)
        // Fix: populate login fields for auto-login
        setEmail(emailNormalized);
        setPassword(regForm.password);
        console.log("before LOGIN in REGIS ", regForm.password)
          // User created — now auto-login
        //   await handleLogin();
        await handleLogin(emailNormalized, regForm.password);
        setPassword("");
      
        } catch (e) {
          if (e?.response?.data?.code === 'EMAIL_ALREADY_EXISTS') {
            setAuthError("An account with this email already exists. Sign in instead?");
          } else {
            setAuthError("We couldn't create your account. Please try again.");
          }
        } finally {
          setAuthLoading(false);
        }
      };
      
    // handlePasswordChange moved to ProfilePage.jsx


    const handleSignOut = async () => {
        if (!isGuest) {
            // 1. Force save pending changes based on current tab
            setIsSaving(true);
            try {
                if (activeTab === 'journal') {
                    await persistJournal(journalEntry, currentDate);
                }
            } catch (e) {
                console.error("Error saving during sign out:", e);
            }

            // 2. Record session duration
            const sessionStart = localStorage.getItem('sessionStart');
            if (sessionStart) {
                const durationSeconds = Math.round((Date.now() - Number(sessionStart)) / 1000);
                await api.post('/auth/session-end', { duration_seconds: durationSeconds }).catch(() => {});
            }
        }

        // 3. Clear all local state
        setIsSaving(false);
        setIsLoggedIn(false);
        setIsGuest(false);
        setGuestBannerVisible(true);
        setUserId('');
        setPassword('');
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        localStorage.removeItem("isGuest");
        localStorage.removeItem("activeTab");
        localStorage.removeItem("sessionStart");
        sessionStorage.clear();
        setEmail('');
        setProfile({ name: '', goal: '', age: '', dob: '', occupation: '', gender: '', cellphone: '', city: '', activities: '', preferred_language: DEFAULT_LANGUAGE_CODE });
        setJournalEntryRaw({ notes: '', gratitude: '', images: [] });
        setGuidedPrompts([]);
        savedGuidedResponsesRef.current = {};
        setGuidedUndoHistory([]);
        setIsCuratorOpen(false);
        setGuidedToast(null);
        setAllJournals({});
        setActiveTab('home');

        // 4. Leave nothing of this account in memory. The resets above cover
        // App's own state, but every component and every cached query holds
        // its own copy, and a next account signing in on this browser was
        // shown the previous one's data (2026-09-27).
        clearAccountFromBrowser(queryClient);
    };

    const MAX_QUOTES_PER_SESSION = 5;

    const [quoteCount, setQuoteCount] = useState(0);
    const [sessionLimitReached, setSessionLimitReached] = useState(false);

    const handleQuoteTap = () => {
        if (isAiLoading) return;
      
        if (quoteCount >= MAX_QUOTES_PER_SESSION) {
          setSessionLimitReached(true);
          return;
        }
      
        generateAiMotivation();
        setQuoteCount(prev => prev + 1);
      };
      

    const generateAiMotivation = async () => {
        // if (!API_KEY) {
        //     setAiResponse("Please configure your Gemini API Key in the code (Line 38) to use this feature.");
        //     return;
        // }

        setIsAiLoading(true);
        try {
            
            // const res = await api.post("/api/ai/quote", {
            //     journalText: historyText,          // optional
            //     theme: "self-trust"                // optional
            //   });
            const res = await api.post("/api/ai/quote", {
                userId: userId,
                // journalText: quoteMode === "personal" ? historyText : "",
                mode: quoteMode,
                theme,
                style : quoteMode === "wisdom" ? style: undefined,
              });
            setAiResponse(res.data.quote);
            setAiMood(res.data.mood);

            if (quoteMode === "wisdom") {
                nextTheme();
                nextWisdomStyle();
            }

            // setAiResponse(data.candidates?.[0]?.content?.parts?.[0]?.text || "Keep pushing forward!");
        } catch (e) {
            setAiResponse(e.message === "API Key Invalid" ? "Invalid API Key. Please check your configuration." : "AI is currently taking a nap. Try again later.");
        } finally {
            setIsAiLoading(false);
        }
    };

    // `override` lets a caller submit a turn the composer never held — today
    // that is Home's "Discuss with AI". The typeof guard matters: the button and
    // the Enter handler both call this bare, so `override` arrives as a DOM
    // event, and anything other than a string must fall back to the composer.
    const handleChatSubmit = async (override) => {
        const submitted = typeof override === 'string' ? override : chatInput;
        if (!submitted.trim()) return;
        if (chatCount >= CHAT_LIMIT) return;
        // if (!API_KEY) {
        //     setChatHistory(prev => [...prev, { role: 'ai', content: "Please configure your Gemini API Key in the code to chat." }]);
        //     return;
        // }

        // const today = new Date();

        // const historyDates = Object.keys(allJournals)
        // .filter(dateStr => {
        //     const d = allJournals[dateStr];
        //     const entryDate = new Date(dateStr);

        //     if (entryDate > today) return false;

        //     const dailyLen = d?.notes?.trim().length || 0;
        //     const reflectionsLen = d?.reflections?.trim().length || 0;
        //     const gratitudeLen = d?.gratitude?.trim().length || 0;

        //     return (
        //     dailyLen > 10 ||
        //     reflectionsLen > 10 ||
        //     gratitudeLen > 10
        //     );
        // })
        // .sort((a, b) => new Date(b) - new Date(a))
        // .slice(0, 5);

        // console.log("H dates", historyDates);

        // let historyText = "";

        // historyDates.forEach(date => {
        // const d = allJournals[date];
        // const parts = [];

        // if (d?.notes?.trim()) {
        //     parts.push(`Daily Notes: ${d.notes.trim()}`);
        // }
        // if (d?.reflections?.trim()) {
        //     parts.push(`Reflections: ${d.reflections.trim()}`);
        // }
        // if (d?.gratitude?.trim()) {
        //     parts.push(`Gratitudes: ${d.gratitude.trim()}`);
        // }

        // if (parts.length) {
        //     historyText += `Date: ${date}\n${parts.join("\n")}\n---\n`;
        // }
        // });
        // console.log("History journal ",historyText)
        // {USER HISTORY JOURNAL}

        const userMsg = { role: 'user', content: submitted };
        const inputSnapshot = submitted;
        const historySnapshot = [...chatHistory, userMsg];
        setChatHistory(prev => [...prev, userMsg]);
        setChatInput("");
        setChatCount(prev => prev + 1);

        // Non-guest users go through the V2 streaming pipeline.
        if (!isGuest) {
            // Spent on this one send: only the first turn of a thread can carry
            // the opener, and the server ignores it for a thread that exists.
            const openerId = pendingOpenerIdRef.current;
            pendingOpenerIdRef.current = null;
            chat.send(inputSnapshot, historySnapshot, openerId);
            return;
        }

        // Guest chat is a single-shot, no-memory endpoint.
        try {
            const response = await api.post("/api/ai/guest-chat", { userId: userId, chatInput: inputSnapshot });
            const aiText = response.data.reply;
            setChatHistory(prev => [...prev, { role: 'ai', content: aiText }]);
        } catch (e) {
            setChatHistory(prev => [...prev, { role: 'ai', content: "I'm here with you, even if the connection stumbled." }]);
        }
    }; // END FUNCTION


    // --- Effects ---

    // Restore session from localStorage on mount (survives page refresh)
    useEffect(() => {
        const token = localStorage.getItem('token');
        const storedUserId = localStorage.getItem('userId');
        if (!token || !storedUserId) return;

        const storedIsGuest = localStorage.getItem('isGuest') === 'true';

        if (!localStorage.getItem('sessionStart')) {
            localStorage.setItem('sessionStart', Date.now());
        }

        let cancelled = false;
        const restore = async () => {
            try {
                // Validate token via profile and load journals in parallel — saves a full RTT on cold start.
                const [profileData] = await Promise.all([
                    fetchProfile(storedUserId),
                    loadJournals(storedUserId),
                ]);
                if (cancelled) return;
                setUserId(storedUserId);
                setProfile(storedIsGuest ? { ...profileData, name: 'Guest' } : profileData);
                setLastSavedProfile(profileData);
                setIsLoggedIn(true);
                setIsGuest(storedIsGuest);
                if (storedIsGuest) {
                    setAiDataConsent(true);
                } else {
                    setAiDataConsent(profileData.ai_data_consent);
                    if (profileData.ai_data_consent === null) setShowConsentModal(true);
                }
                // Disabled 2026-08-12 — see the note at the sign-in call above.
                // api.post('/api/ai/quote', { userId: storedUserId, mode: 'personal' })
                //     .then(q => { if (!cancelled) setDailyQuote(q.data.quote); })
                //     .catch(() => { if (!cancelled) setDailyQuote('Take today at the pace your heart allows.'); })
                //     .finally(() => { if (!cancelled) setLoadingQuote(false); });
            } catch {
                // Token expired or invalid — clear and stay on login
                localStorage.removeItem('token');
                localStorage.removeItem('userId');
                localStorage.removeItem('isGuest');
            }
        };
        restore();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Listen for expired-token signal from the API interceptor
    useEffect(() => {
        const handle = () => setSessionExpired(true);
        window.addEventListener("session-expired", handle);
        return () => window.removeEventListener("session-expired", handle);
    }, []);

    // When date changes (or server data loads, update the journal view.
    // Prefer a sessionStorage draft over server data only when the draft
    // has actual content that differs from the server — never let an empty
    // draft (written during logout cleanup) override real server content.
    // Uses setJournalEntryRaw so loading does NOT re-write sessionStorage.
    useEffect(() => {
        const serverEntry = allJournals[currentDate] || { notes: '', gratitude: '', images: [] };
        try {
            const raw = sessionStorage.getItem(`journal-draft-${currentDate}`);
            if (raw) {
                const draft = JSON.parse(raw);
                const draftHasContent =
                    stripTags(draft.notes).trim() ||
                    (draft.gratitude || '').trim();
                const hasDraftEdits = draftHasContent && (
                    (draft.notes || '') !== (serverEntry.notes || '') ||
                    (draft.gratitude || '') !== (serverEntry.gratitude || '')
                );
                if (hasDraftEdits) {
                    setJournalEntryRaw(draft);
                    return;
                }
            }
        } catch {}
        setJournalEntryRaw(serverEntry);
    }, [currentDate, allJournals]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load curated guided-reflection cards whenever the date changes.
    // Per-date model — empty list is the expected state when nothing was curated.
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/user/${userId}/guided-reflections/${currentDate}`);
                if (cancelled) return;
                const prompts = res.data?.prompts || [];
                setGuidedPrompts(prompts);
                savedGuidedResponsesRef.current = Object.fromEntries(
                    prompts.map(p => [p.reflectionId, p.response || ''])
                );
            } catch {
                if (!cancelled) setGuidedPrompts([]);
            }
        })();
        return () => { cancelled = true; };
    }, [currentDate, userId, guidedReloadNonce]);

    // After Start Writing curates a prompt and lands on the Journal, scroll the
    // freshly loaded guided-reflection block into view so the page opens on it.
    useEffect(() => {
        const pid = pendingScrollPromptIdRef.current;
        if (!pid || activeTab !== 'journal') return;
        if (guidedPrompts.some(p => p.promptId === pid)) {
            const t = setTimeout(() => {
                document.getElementById(`gr-prompt-${pid}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 150);
            pendingScrollPromptIdRef.current = null;
            return () => clearTimeout(t);
        }
    }, [guidedPrompts, activeTab]);

    useEffect(() => {
        if (activeTab === 'chat') chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatHistory, activeTab]);

    // Clear the active editor reference when leaving the journal tab or switching dates —
    // the underlying DOM node will be unmounted, and a stale ref would break toolbar actions.
    useEffect(() => { setActiveEditor(null); }, [currentDate, activeTab]);

    // Refetch journals when re-entering the Journal tab so any voice-note
    // changes made from the Voice tab (delete / accept / edit / apply typo
    // fix) are reflected in Today's Story without a manual reload.
    useEffect(() => {
      if (activeTab === 'journal' && userId) {
        loadJournals(userId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, userId]);

    // Floating toolbar — appears whenever the in-page toolbar scrolls off screen
    useEffect(() => {
        if (activeTab !== 'journal') {
            setToolbarFloating(false);
            return;
        }

        const handleScroll = () => {
            const el = inPageToolbarRef.current;
            if (!el) return;
            // Show floating toolbar once the in-page toolbar's bottom edge
            // goes behind or above the sticky header (~64px tall)
            setToolbarFloating(el.getBoundingClientRect().bottom < 64);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        // Check immediately in case the page is already scrolled
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeTab]);

    // --- VIEW ---
    if (!isLoggedIn) {
        return (
            <div className="min-h-screen font-sans relative flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #1C1917 0%, #292524 50%, #1C1917 100%)' }}>
                {/* Background blobs */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px] pointer-events-none" style={{ background: 'radial-gradient(circle, #7C9A82 0%, transparent 70%)' }} />
                <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-10 blur-[80px] pointer-events-none" style={{ background: 'radial-gradient(circle, #D4A574 0%, transparent 70%)' }} />

                {/* Card */}
                <div className="relative w-full max-w-sm mx-4 bg-white rounded-[2rem] shadow-2xl overflow-hidden">

                    {/* ── Sign In ── */}
                    {!isRegisterMode && authScreen === 'signin' && (
                        <div className="px-8 py-10">
                            {/* Logo */}
                            <div className="text-center mb-8">
                                <div className="h-14 w-14 rounded-2xl mx-auto flex items-center justify-center mb-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}>
                                    <BookOpen size={26} className="text-white" />
                                </div>
                                <h1 className="text-2xl font-bold text-stone-900 tracking-tight">Welcome back</h1>
                                <p className="text-stone-500 text-sm mt-1">Sign in to continue your journey</p>
                            </div>

                            {/* Google */}
                            <button
                                onClick={handleGoogleSignIn}
                                disabled={authLoading}
                                className="w-full flex items-center justify-center gap-3 py-3.5 bg-white border border-stone-200 hover:border-stone-300 hover:bg-stone-50 rounded-2xl font-semibold text-stone-700 text-sm transition-all duration-200 shadow-sm active:scale-[0.98] disabled:opacity-50"
                                aria-label="Sign in with Google"
                            >
                                <GoogleIcon />
                                Continue with Google
                            </button>

                            <AuthDivider />

                            {/* Email form */}
                            <div className="space-y-3">
                                <AuthInput
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="Email address"
                                    icon={User}
                                />
                                <AuthInput
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Password"
                                    icon={Lock}
                                    rightEl={
                                        <button onClick={() => setShowPassword(!showPassword)} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    }
                                />
                            </div>

                            <div className="flex justify-end mt-1">
                                <button
                                    onClick={() => { setAuthScreen('forgot'); setAuthError(''); }}
                                    className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
                                >
                                    Forgot password?
                                </button>
                            </div>

                            {authError && (
                                <p className={`mt-3 text-sm text-center font-medium ${authError.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`}>{authError}</p>
                            )}

                            <button
                                onClick={() => handleLogin(email, password)}
                                disabled={authLoading}
                                className="w-full mt-5 py-4 rounded-full font-bold text-white text-sm tracking-wide shadow-lg active:scale-[0.98] transition-all duration-200 disabled:opacity-60"
                                style={{ background: authLoading ? '#9ab5a0' : 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}
                            >
                                {authLoading ? 'Signing in…' : 'Sign In'}
                            </button>

                            <p className="text-center text-sm text-stone-500 mt-6">
                                New to Mindwire?{' '}
                                <button onClick={() => { setIsRegisterMode(true); setAuthScreen('signin'); setAuthError(''); }} className="font-semibold text-stone-800 hover:underline">
                                    Create an account
                                </button>
                            </p>

                            <div className="mt-4 text-center">
                                <button
                                    onClick={handleGuestLogin}
                                    disabled={authLoading}
                                    className="text-xs text-stone-400 hover:text-stone-600 transition-colors disabled:opacity-50"
                                >
                                    Continue without signing in
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Forgot Password ── */}
                    {!isRegisterMode && authScreen === 'forgot' && (
                        <div className="px-8 py-10">
                            <div className="flex items-center gap-3 mb-7">
                                <button
                                    onClick={() => { setAuthScreen('signin'); setAuthError(''); }}
                                    className="h-9 w-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
                                    aria-label="Back to sign in"
                                >
                                    <ArrowLeft size={16} className="text-stone-600" />
                                </button>
                                <div>
                                    <h2 className="text-xl font-bold text-stone-900 leading-tight">Forgot password?</h2>
                                    <p className="text-stone-500 text-xs mt-0.5">We'll send a reset link to your email</p>
                                </div>
                            </div>

                            <AuthInput
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="Your email address"
                                icon={User}
                            />

                            {authError && (
                                <p className="mt-3 text-sm text-red-500 text-center font-medium">{authError}</p>
                            )}

                            <button
                                onClick={handleForgotPassword}
                                disabled={authLoading}
                                className="w-full mt-6 py-4 rounded-full font-bold text-white text-sm tracking-wide shadow-lg active:scale-[0.98] transition-all duration-200 disabled:opacity-60"
                                style={{ background: authLoading ? '#9ab5a0' : 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}
                            >
                                {authLoading ? 'Sending…' : 'Send Reset Link'}
                            </button>
                        </div>
                    )}

                    {/* ── Check Email confirmation ── */}
                    {!isRegisterMode && authScreen === 'checkEmail' && (
                        <div className="px-8 py-12 text-center">
                            <div className="h-16 w-16 rounded-full mx-auto flex items-center justify-center mb-5" style={{ background: 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                    <polyline points="22,6 12,13 2,6"/>
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-stone-900 mb-2">Check your inbox</h2>
                            <p className="text-stone-500 text-sm leading-relaxed mb-1">
                                If a Mindwire account exists for <span className="font-semibold text-stone-700">{email}</span>, a reset link is on its way.
                            </p>
                            <p className="text-stone-400 text-xs mb-8">The link expires in 1 hour. Check your spam folder if you don't see it.</p>
                            <button
                                onClick={() => { setAuthScreen('signin'); setAuthError(''); }}
                                className="text-sm font-semibold text-stone-600 hover:text-stone-900 transition-colors"
                            >
                                ← Back to Sign In
                            </button>
                        </div>
                    )}

                    {/* ── Reset Password ── */}
                    {authScreen === 'reset' && (
                        <div className="px-8 py-10">
                            <div className="text-center mb-7">
                                <div className="h-14 w-14 rounded-2xl mx-auto flex items-center justify-center mb-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}>
                                    <Lock size={22} className="text-white" />
                                </div>
                                <h2 className="text-xl font-bold text-stone-900">Set new password</h2>
                                <p className="text-stone-500 text-xs mt-1">Choose something strong and memorable</p>
                            </div>

                            <div className="space-y-3">
                                <AuthInput
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    placeholder="New password"
                                    icon={Lock}
                                    rightEl={
                                        <button onClick={() => setShowPassword(!showPassword)} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    }
                                />
                                <AuthInput
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmNewPassword}
                                    onChange={e => setConfirmNewPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    icon={Lock}
                                />
                            </div>

                            {authError && (
                                <p className="mt-3 text-sm text-red-500 text-center font-medium">{authError}</p>
                            )}

                            <button
                                onClick={handleResetPassword}
                                disabled={authLoading}
                                className="w-full mt-6 py-4 rounded-full font-bold text-white text-sm tracking-wide shadow-lg active:scale-[0.98] transition-all duration-200 disabled:opacity-60"
                                style={{ background: authLoading ? '#9ab5a0' : 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}
                            >
                                {authLoading ? 'Updating…' : 'Update Password'}
                            </button>
                        </div>
                    )}

                    {/* ── Sign Up ── */}
                    {isRegisterMode && (
                        <div className="px-8 py-10">
                            {/* Back + heading */}
                            <div className="flex items-center gap-3 mb-7">
                                <button
                                    onClick={() => { setIsRegisterMode(false); setAuthError(''); }}
                                    className="h-9 w-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
                                    aria-label="Back to sign in"
                                >
                                    <ArrowLeft size={16} className="text-stone-600" />
                                </button>
                                <div>
                                    <h2 className="text-xl font-bold text-stone-900 leading-tight">Create account</h2>
                                    <p className="text-stone-500 text-xs mt-0.5">Start your mindful journey today</p>
                                </div>
                            </div>

                            {/* Google */}
                            <button
                                onClick={handleGoogleSignIn}
                                disabled={authLoading}
                                className="w-full flex items-center justify-center gap-3 py-3.5 bg-white border border-stone-200 hover:border-stone-300 hover:bg-stone-50 rounded-2xl font-semibold text-stone-700 text-sm transition-all duration-200 shadow-sm active:scale-[0.98] disabled:opacity-50"
                                aria-label="Sign up with Google"
                            >
                                <GoogleIcon />
                                Sign up with Google
                            </button>

                            <AuthDivider />

                            {/* Register form — minimal: name, email, password only */}
                            <div className="space-y-3">
                                <div>
                                    <AuthInput
                                        type="text"
                                        value={regForm.name}
                                        onChange={e => setRegForm({ ...regForm, name: e.target.value })}
                                        placeholder="Your name"
                                        icon={User}
                                    />
                                    {regErrors.name && <p className="text-xs text-red-500 mt-1 ml-1">{regErrors.name}</p>}
                                </div>
                                <AuthInput
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="Email address"
                                    icon={User}
                                />
                                <AuthInput
                                    type={showPassword ? 'text' : 'password'}
                                    value={regForm.password}
                                    onChange={e => setRegForm({ ...regForm, password: e.target.value })}
                                    placeholder="Create a password"
                                    icon={Lock}
                                    rightEl={
                                        <button onClick={() => setShowPassword(!showPassword)} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    }
                                />
                                <AuthInput
                                    type={showPassword ? 'text' : 'password'}
                                    value={regForm.confirmPassword}
                                    onChange={e => setRegForm({ ...regForm, confirmPassword: e.target.value })}
                                    placeholder="Confirm password"
                                    icon={Lock}
                                />

                                {/* Language — pills rather than a select: three
                                    options are faster to tap than to open, and
                                    each reads in its own script. Registry-driven,
                                    so a new language needs no change here. */}
                                <fieldset className="pt-1">
                                    <legend className="text-xs font-semibold text-stone-500 mb-2 ml-1">
                                        Preferred language
                                    </legend>
                                    <div className="flex flex-wrap gap-2">
                                        {SUPPORTED_LANGUAGES.map(lang => {
                                            const selected = normalizeLanguageCode(regForm.preferred_language) === lang.code;
                                            return (
                                                <button
                                                    key={lang.code}
                                                    type="button"
                                                    lang={lang.code}
                                                    aria-pressed={selected}
                                                    onClick={() => setRegForm({ ...regForm, preferred_language: lang.code })}
                                                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2
                                                        ${selected
                                                            ? 'border-transparent text-white shadow-sm'
                                                            : 'border-stone-200 bg-white text-stone-600 hover:bg-stone-50 hover:-translate-y-0.5'}`}
                                                    style={selected ? { background: 'linear-gradient(135deg, #7C9A82, #5a7a60)' } : undefined}
                                                >
                                                    {lang.nativeLabel}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[11px] text-stone-400 mt-2 ml-1">
                                        You can change this any time in your profile.
                                    </p>
                                </fieldset>
                            </div>

                            {authError && (
                                <div className="mt-3 text-center">
                                    <p className="text-sm text-red-500 font-medium">{authError}</p>
                                    {authError.includes('already exists') && (
                                        <button
                                            onClick={() => { setIsRegisterMode(false); setAuthError(''); }}
                                            className="text-sm text-stone-600 hover:text-stone-800 font-medium underline mt-1"
                                        >
                                            Sign in instead
                                        </button>
                                    )}
                                </div>
                            )}

                            <button
                                onClick={handleRegister}
                                disabled={authLoading}
                                className="w-full mt-6 py-4 rounded-full font-bold text-white text-sm tracking-wide shadow-lg active:scale-[0.98] transition-all duration-200 disabled:opacity-60"
                                style={{ background: authLoading ? '#9ab5a0' : 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}
                            >
                                {authLoading ? 'Creating account…' : 'Create Account'}
                            </button>

                            <p className="text-center text-[11px] text-stone-400 mt-5 leading-relaxed px-2">
                                By creating an account you agree to our{' '}
                                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-stone-600 font-medium underline">Terms of Service</a> and{' '}
                                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-stone-600 font-medium underline">Privacy Policy</a>
                            </p>

                            <p className="text-center text-sm text-stone-500 mt-4">
                                Have a Mindwire account?{' '}
                                <button onClick={() => { setIsRegisterMode(false); setAuthError(''); }} className="font-semibold text-stone-800 hover:underline">
                                    Sign In
                                </button>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const navItems = [
        { id: 'home', icon: Home, label: 'Home' },
        { id: 'feed', icon: Layers3, label: 'Feed' },
        { id: 'journal', icon: BookOpen, label: 'Journal' },
        { id: 'chat', icon: MessageCircle, label: 'Chat' },
        { id: 'voice', icon: AudioLines, label: 'Voice' },
    ];

    // Elegant "weaving into memory" save indicator — a soft breathing halo plus a
    // calm staggered dot pulse (soft pulse, never a spinner; matches the design
    // system). Shown while the save request is in flight, i.e. until the embedding
    // pipeline is enqueued on the backend.
    const savingIndicator = (
        <span className="relative flex items-center justify-center">
            <span
                aria-hidden="true"
                className="pointer-events-none absolute h-7 w-7 rounded-full bg-white/25 animate-ping [animation-duration:1600ms] motion-reduce:hidden"
            />
            <span className="relative flex items-center gap-[3px]">
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse [animation-duration:1200ms] motion-reduce:animate-none" />
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse [animation-duration:1200ms] [animation-delay:200ms] motion-reduce:animate-none" />
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse [animation-duration:1200ms] [animation-delay:400ms] motion-reduce:animate-none" />
            </span>
        </span>
    );

    const toolbarButtons = [
        {
            id: 'save',
            icon: isSaved ? <Check size={18} strokeWidth={2.5} /> : isSaving ? savingIndicator : <Check size={18} strokeWidth={2.5}/>,
            label: isSaved ? 'Saved' : isSaving ? 'Saving…' : 'Save Entry',
            action: handleManualSave,
            active: isSaved,
            disabled: isSaving || isSaved,
            busy: isSaving,
            testId: 'toolbar-save',
            group: 'left',
            variant: 'primary',
        },
        {
            id: 'clear',
            icon: <Trash2 size={18}/>,
            label: 'Clear',
            action: handleClearSection,
            active: false,
            disabled: false,
            testId: 'toolbar-clear',
            group: 'left',
            variant: 'ghost',
        },
        {
            id: 'focus',
            icon: <Maximize2 size={18}/>,
            label: 'Focus',
            action: () => activeEditor?.enterFocusMode?.(),
            active: false,
            // Not applicable for Gratitudes (plain inputs, no RichEditor).
            disabled: !activeEditor?.enterFocusMode || activeSection === 'gratitude',
            testId: 'toolbar-focus',
            group: 'right',
            variant: 'ghost',
        },
        {
            id: 'voice',
            icon: journalVoice.isListening ? <MicOff size={18}/> : <Mic size={18}/>,
            label: journalVoice.isListening ? 'Stop' : 'Speak to Write',
            action: journalVoice.toggleListening,
            active: journalVoice.isListening,
            // Not applicable when there's no editor focused or Gratitudes is active, or no browser support.
            disabled: !journalVoice.hasSupport || !activeEditor || activeSection === 'gratitude',
            testId: 'toolbar-voice',
            group: 'right',
            variant: 'ghost',
        },
        {
            id: 'voice-journal',
            icon: <AudioLines size={18}/>,
            label: 'Voice journal',
            action: () => setVoiceCaptureOpen(true),
            active: voiceCaptureOpen,
            disabled: false,
            testId: 'toolbar-voice-journal',
            group: 'right',
            variant: 'ghost',
        },
        {
            // The mobile toolbar puts this behind its ⋯ overflow; the web toolbar
            // has no overflow, so it sits in the row directly.
            id: 'reflection',
            icon: <Sparkles size={18}/>,
            label: 'Reflection for this day',
            action: showStoredReflection,
            active: !!smartReflection,
            // Offered only when this day actually has one. Greyed rather than
            // hidden: a toolbar whose buttons come and go is harder to learn
            // than one where a button is visibly unavailable.
            disabled: !(dayHasReflection || dayReflection),
            testId: 'toolbar-reflection',
            group: 'right',
            variant: 'ghost',
        },
    ];

    const renderJournalToolBtn = (btn) => (
        <button
            key={btn.id}
            data-testid={btn.testId}
            onClick={btn.action}
            disabled={btn.disabled}
            aria-pressed={btn.active}
            aria-label={btn.label}
            title={btn.label}
            className={`inline-flex items-center justify-center h-10 w-10 rounded-full transition-all duration-200 active:scale-95 disabled:cursor-not-allowed ${
                btn.busy ? '' : 'disabled:opacity-40'
            } ${
                btn.variant === 'primary'
                    ? 'bg-stone-800 text-white hover:bg-stone-700 shadow-sm disabled:hover:bg-stone-800'
                    : btn.active
                        ? 'bg-stone-100 text-stone-800'
                        : 'text-stone-500 hover:text-stone-700 hover:bg-stone-50'
            }`}
        >
            {btn.icon}
        </button>
    );

    const journalToolButtonsLeft  = toolbarButtons.filter(b => b.group === 'left');
    const journalToolButtonsRight = toolbarButtons.filter(b => b.group === 'right');

    // Word/char count for the toolbar — Today's Story (notes) plain-text length
    const noteStats = (() => {
        const text = (journalEntry.notes || '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        const words = text ? text.split(' ').length : 0;
        return { words, chars: text.length };
    })();

    return (
        <div className="min-h-screen text-stone-800 font-sans pb-24 selection:bg-rose-100 selection:text-rose-900 bg-gradient-to-b from-[#FBF8F1] via-[#F5EFE2] to-[#FBF8F1]">
            <GlobalStyles />
            {showConsentModal && (
                <AiConsentModal
                    userId={userId}
                    onAccept={() => { setAiDataConsent(true); setShowConsentModal(false); }}
                    onDecline={() => { setAiDataConsent(false); setShowConsentModal(false); }}
                />
            )}
            {/* Guest Banner */}
            {isGuest && guestBannerVisible && (
                <div className="bg-amber-50 border-b border-amber-100 px-4 py-2.5 flex items-center justify-between gap-3">
                    <p className="text-xs text-amber-800 font-medium">
                        Exploring as a guest — your data is kept for 24 hours.
                    </p>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <button
                            onClick={async () => { await handleSignOut(); setIsRegisterMode(true); }}
                            className="text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700 transition-colors"
                            aria-label="Create a full account"
                        >
                            Create account
                        </button>
                        <button
                            onClick={() => setGuestBannerVisible(false)}
                            aria-label="Dismiss banner"
                            className="text-amber-400 hover:text-amber-600 transition-colors leading-none"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Header */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-stone-100 px-4 py-2.5 sm:px-6 sm:py-4 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    {activeTab === 'home' ? (
                        <div className="flex items-center gap-3" aria-label="MindWire">
                            <div className="h-9 w-9 sm:h-10 sm:w-10 bg-stone-800 rounded-xl flex items-center justify-center text-white">
                                <BookOpen size={18} />
                            </div>
                            <h1 className="text-xl font-bold text-stone-800 tracking-tight">MindWire</h1>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => handleTabChange('home')}
                            aria-label="Back to home"
                            className="inline-flex items-center gap-2 h-9 sm:h-10 px-3 -ml-1 rounded-2xl text-stone-700 hover:bg-stone-100/80 transition-all active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
                        >
                            <ArrowLeft size={20} strokeWidth={2} />
                            <span className="text-sm font-semibold tracking-tight">Home</span>
                        </button>
                    )}

                    {/* Entries — opens the mobile journal drawer; desktop uses the inline panel */}
                    {activeTab === 'journal' && (
                        <button
                            type="button"
                            onClick={() => setEntriesOpen(true)}
                            aria-label="Show entries"
                            title="Entries"
                            className="md:hidden inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white border border-stone-200 text-stone-700 text-sm font-semibold shadow-sm hover:bg-stone-50 active:scale-95 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
                        >
                            <List size={17} strokeWidth={2.1} />
                            Entries
                        </button>
                    )}
                </div>

                {/* Identity cluster: the avatar, wearing a corner badge when AI
                    personalization is on. One target instead of two — off is the
                    app's baseline, and a badge for it would be a permanent apology.
                    The badge itself is a shortcut into Safety, where it is turned off. */}
                <style>{`
                  @keyframes mwAiBadgeIn {
                    0%   { transform: scale(0);   opacity: 0; }
                    70%  { transform: scale(1.18); opacity: 1; }
                    100% { transform: scale(1);   opacity: 1; }
                  }
                  @keyframes mwAiHaloBreath {
                    0%   { box-shadow: 0 0 0 0px rgba(124,154,130,0.00); }
                    45%  { box-shadow: 0 0 0 7px rgba(124,154,130,0.16); }
                    100% { box-shadow: 0 0 0 4px rgba(124,154,130,0.10); }
                  }
                  .mw-ai-badge-in { animation: mwAiBadgeIn 240ms cubic-bezier(.34,1.56,.64,1) both; }
                  .mw-ai-halo     { box-shadow: 0 0 0 4px rgba(124,154,130,0.10);
                                    animation: mwAiHaloBreath 1100ms ease-out both; }
                  @media (prefers-reduced-motion: reduce) {
                    .mw-ai-badge-in { animation: none; }
                    .mw-ai-halo     { animation: none; box-shadow: 0 0 0 4px rgba(124,154,130,0.10); }
                  }
                `}</style>
                <div className="relative inline-flex flex-shrink-0">
                    <button
                        onClick={() => handleTabChange(activeTab === 'profile' ? previousTabRef.current : 'profile')}
                        aria-label={activeTab === 'profile' ? 'Close profile' : 'Open profile'}
                        aria-pressed={activeTab === 'profile'}
                        title="Profile"
                        className={`h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-600 font-bold text-sm transition-colors border ${
                            activeTab === 'profile' ? 'border-stone-400' : 'border-stone-200'
                        } ${aiDataConsent === true ? 'mw-ai-halo' : 'shadow-sm'}`}
                    >
                        <ProfileAvatar
                            url={profile.avatar_url}
                            fallback={profile.name ? profile.name[0].toUpperCase() : 'U'}
                        />
                    </button>

                    {aiDataConsent === true && (
                        <button
                            type="button"
                            onClick={() => { handleTabChange('profile'); setProfileRequestTab('safety'); }}
                            aria-label="AI personalization is on"
                            title="AI personalization is on"
                            className="mw-ai-badge-in absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C9A82]/50"
                        >
                            <span
                                className="h-[15px] w-[15px] rounded-full flex items-center justify-center text-white"
                                style={{ backgroundColor: '#7C9A82', boxShadow: '0 0 0 2px #FFFFFF' }}
                            >
                                <Sparkles size={9} strokeWidth={2.4} />
                            </span>
                        </button>
                    )}
                </div>
            </header>

            <main className="max-w-3xl mx-auto p-4 space-y-6">

                {activeTab === 'home' && (() => {
                    const today = getLocalToday();
                    const rec = reflectionOfTheDayQuery.data;
                    return (
                        <div className="max-w-md mx-auto px-1 pt-2 pb-6 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

                            {/* Static rhythm card: grounded encouragement + the week's rhythm +
                                core threads. Wider than the cards below it (-mx-3 against this
                                wrapper's p-4 + px-1 ≈ 20px inset, landing at 8px) — a deliberate
                                "hero" inset, not full-bleed. Renders nothing until there's real
                                history (see JournalRhythmCard's own hasContent check). */}
                            {journalPulseQuery.data && (
                                <div className="-mx-3">
                                    <JournalRhythmCard pulse={journalPulseQuery.data} />
                                </div>
                            )}

                            {/* Reflections of the Day — recommended daily prompt (replaces the old quote) */}
                            <ReflectionOfTheDay
                                loading={reflectionOfTheDayQuery.isLoading}
                                prompt={rec}
                                onStartWriting={handleStartWriting}
                                onSkip={() => handleDailyPromptFeedback('dislike')}
                                onDiscuss={handleDiscussWithAI}
                                busy={reflectionBusy}
                                onPersonalize={() => setAiNudge({
                                    reason: 'Turning on AI matches each day’s prompt to what you have actually been writing about.',
                                })}
                            />

                            {/* Voice Journal — dark card with an idle animated waveform */}
                            <button
                                type="button"
                                onClick={() => {
                                    if (currentDate !== today) setCurrentDate(today);
                                    setVoiceCaptureOpen(true);
                                }}
                                aria-label="Record a voice note for today"
                                className="w-full text-left rounded-[1.75rem] px-6 py-5 flex items-center gap-4 shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400"
                                style={{ backgroundColor: '#38332F' }}
                            >
                                <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-white">
                                    <Mic size={20} strokeWidth={2} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-white font-semibold text-[17px] tracking-tight">Speak</h3>
                                    <p className="text-white/55 text-[13px] leading-snug mt-0.5">
                                        Say anything — we'll write it down
                                    </p>
                                </div>
                                <AnimatedWaveform bars={5} height={22} color="currentColor" className="text-white/70 flex-shrink-0 mr-1" />
                            </button>

                            {/* Explore & Archives */}
                            <div className="space-y-4 pt-1">
                                <h3 className="text-[11px] font-semibold tracking-[0.18em] uppercase text-stone-400 px-1">
                                    Explore &amp; Archives
                                </h3>

                                {/* Companion card (picks up the latest entry), full width. The "Speak" tile that
                                    used to sit beside it is gone: it opened the
                                    same capture sheet on today as the dark card
                                    directly above, so it was a second door to a
                                    room the user was already standing in. One
                                    tile left means one column — half a row of
                                    empty card reads as something that failed to
                                    load. */}
                                <CompanionCard
                                    opener={companionOpenerQuery.data}
                                    loading={!isGuest && companionOpenerQuery.isLoading}
                                    onTapToChat={handleCompanionTapToChat}
                                    onOpenDialogue={handleCompanionOpenDialogue}
                                />

                                {/* No library tile here. The bottom nav's Voice
                                    tab already opens the list and the insight
                                    strip, and a second door to the same room is
                                    just another thing to read past. (Two used to
                                    sit here, Voice Notes and Echoes List,
                                    pointing at sub-tabs that no longer exist.) */}
                            </div>
                        </div>
                    );
                })()}


                {activeTab === 'journal' && (
                    <div className="flex gap-6 max-w-[1400px] mx-auto items-stretch">
                        <JournalSidebar
                            userId={userId}
                            currentDate={currentDate}
                            onSelectDate={handleDateChange}
                            onNewEntry={() => handleDateChange(getLocalToday())}
                            collapsed={sidebarCollapsed}
                            onToggleCollapsed={() => setSidebarCollapsed(v => !v)}
                            mobileOpen={entriesOpen}
                            onMobileOpenChange={setEntriesOpen}
                        />
                        <div className="flex-1 min-w-0 space-y-3 sm:space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Date Navigator */}
                        <div className="flex items-center justify-between bg-white/80 backdrop-blur-sm p-1.5 sm:p-3 rounded-full shadow-[0_1px_2px_rgba(45,41,38,0.06)] border border-stone-200/50 mx-auto max-w-full sm:max-w-sm">
                            <button onClick={() => navigateDate(-1)} className="p-2 hover:bg-stone-100 rounded-full text-stone-500"><ChevronLeft size={20}/></button>
                            <div  
                               onClick={() => dateInputRef.current?.showPicker()}
                               className="relative flex items-center justify-center gap-2 group cursor-pointer px-4 py-2 rounded-xl hover:bg-stone-50 transition-colors"
                            >
                                <Calendar size={18} className="text-stone-400 group-hover:text-rose-500 transition-colors"/>
                                <span className="font-medium text-sm tracking-tight text-stone-700 px-2.5 py-0.5 rounded-md bg-stone-100/70">{formatDisplayDate(currentDate).fullDate}</span>

                                <div className="w-px h-4 bg-stone-200"></div>
                                <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-[0.2em]">{formatDisplayDate(currentDate).weekday}</span>
                                
                                <input 
                                    ref={dateInputRef}
                                    type="date" 
                                    value={currentDate} 
                                    max={getLocalToday()}
                                    onChange={(e) => handleDateChange(e.target.value)}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                                />
                            </div>
                            <button onClick={() => navigateDate(1)} disabled={currentDate === getLocalToday()} className="p-2 hover:bg-stone-100 rounded-full text-stone-500 disabled:opacity-30"><ChevronRight size={20}/></button>
                        </div>
                        
                        {/* Guided Reflections toast — no prompts for date */}
                        {guidedToast && (
                            <div className="text-sm text-stone-600 text-center px-4 py-3 bg-amber-50 rounded-2xl border border-amber-100">
                                {guidedToast}
                            </div>
                        )}

                        <div className="space-y-6">
                            {/* Bento — toolbar + editor + prompts unified as one paper, thin connected borders */}
                            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-stone-200/50 shadow-[0_1px_3px_rgba(45,41,38,0.06)] overflow-hidden divide-y divide-stone-200/50">
                                {/* Toolbar zone — row 1: journal actions, row 2: text formatting (acts on focused editor) */}
                                <div ref={inPageToolbarRef} data-testid="journal-toolbar">
                                    <div className="px-2.5 py-2 flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-1">
                                            {journalToolButtonsLeft.map(renderJournalToolBtn)}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {journalToolButtonsRight.map(renderJournalToolBtn)}
                                        </div>
                                    </div>
                                    <div
                                        aria-label="Text formatting"
                                        className="border-t border-stone-200/50 flex items-center"
                                    >
                                        <BlockToolbar
                                            editorRef={activeEditorRefAdapter}
                                            onOpenDrawing={() => activeEditor?.onOpenDrawing?.()}
                                            onAddPhoto={triggerPhotoUpload}
                                            onAddPrompts={triggerAddPrompts}
                                            onChange={activeEditor?.onChange}
                                            isEditorActive={!!activeEditor}
                                            className="flex-1 flex items-center gap-0.5 overflow-x-auto h-12 px-2 scrollbar-hide"
                                        />
                                    </div>
                                </div>

                                {/* Editor zone — Today's Story with inline photo strip */}
                                <div className="p-6">
                                    <RichEditor
                                        label="Today's Story"
                                        placeholder={`${STORY_ANCHOR_PROMPT}\n\n${rotatingStoryPrompt(currentDate)}`}
                                        value={journalEntry.notes || ''}
                                        onChange={(val) => setJournalEntry(prev => ({...prev, notes: val}))}
                                        onFocus={() => setActiveSection('notes')}
                                        minHeight="max(220px, calc(100dvh - 420px))"
                                        onOpenDrawing={() => setDrawingTarget('notes')}
                                        onAddPhoto={triggerPhotoUpload}
                                        onAddPrompts={triggerAddPrompts}
                                        voice={journalVoice}
                                        onActivate={setActiveEditor}
                                        editorRef={notesEditorRef}
                                        drawingStoreRef={drawingStoreRef}
                                        topSlot={(() => {
                                            const voiceNotesForDate = allJournals[currentDate]?.voiceNotes || [];
                                            const hasPhotos = (journalEntry.images || []).length > 0 || isPhotoUploading;
                                            const hasVoiceNotes = voiceNotesForDate.length > 0;
                                            if (!hasPhotos && !hasVoiceNotes && !photoError) return null;
                                            return (
                                                <div className="space-y-3">
                                                    {photoError && (
                                                        <div
                                                            data-testid="journal-photo-error"
                                                            className="flex items-start justify-between gap-2 rounded-xl bg-rose-50 border border-rose-100 px-3 py-2"
                                                        >
                                                            <p className="text-[12px] font-sans text-rose-600 leading-snug">{photoError}</p>
                                                            <button
                                                                type="button"
                                                                onClick={() => setPhotoError(null)}
                                                                className="shrink-0 text-rose-400 hover:text-rose-600"
                                                                aria-label="Dismiss"
                                                            >
                                                                <X size={13} />
                                                            </button>
                                                        </div>
                                                    )}
                                                    {hasPhotos && (
                                                        <div className="space-y-1.5">
                                                        <div
                                                            data-testid="journal-photo-strip"
                                                            className="flex items-center gap-2 overflow-x-auto scrollbar-hide"
                                                        >
                                                            {(journalEntry.images || []).map((img, idx) => (
                                                                <div key={`${img}-${idx}`} className="relative flex-shrink-0 group">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setExpandedPhotoIndex(idx)}
                                                                        className="block h-20 w-20 rounded-2xl overflow-hidden border border-stone-200/70 shadow-sm hover:shadow-md transition-shadow"
                                                                        aria-label={`Memory ${idx + 1}`}
                                                                    >
                                                                        <img src={img} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removePhoto(idx)}
                                                                        className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-white text-stone-500 shadow-sm border border-stone-100 opacity-0 group-hover:opacity-100 transition-opacity hover:text-rose-500"
                                                                        aria-label="Remove memory"
                                                                    >
                                                                        <X size={11} />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            {isPhotoUploading && (
                                                                <div className="flex-shrink-0 h-20 w-20 rounded-2xl border border-stone-200 bg-stone-100/60 flex flex-col items-center justify-center gap-1">
                                                                    <Loader2 size={20} className="animate-spin text-stone-400" />
                                                                    {photoUploadProgress && photoUploadProgress.total > 1 && (
                                                                        <span className="text-[10px] font-sans text-stone-400 tabular-nums">
                                                                            {photoUploadProgress.done}/{photoUploadProgress.total}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {(journalEntry.images || []).length > 0 && (
                                                            <p
                                                                data-testid="journal-photo-count"
                                                                className="text-[10px] font-sans text-stone-400 tabular-nums pl-1"
                                                            >
                                                                {(journalEntry.images || []).length} / {MAX_PHOTOS_PER_DAY} photos{(journalEntry.images || []).length >= MAX_PHOTOS_PER_DAY ? ' · limit reached' : ''}
                                                            </p>
                                                        )}
                                                        </div>
                                                    )}
                                                    {hasVoiceNotes && (
                                                        <div
                                                            data-testid="journal-voice-stack"
                                                            className="flex flex-wrap gap-2"
                                                        >
                                                            {voiceNotesForDate.map((vn) => (
                                                                <VoiceNoteCard
                                                                    key={vn.id}
                                                                    note={vn}
                                                                    onDeleted={() => loadJournals(userId)}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    />

                                    {/* Visually-hidden file input for the "Add Visual Memories" button.
                                        Uses `sr-only` (position:absolute + 1px sized) instead of `hidden`
                                        (display:none) because mobile Safari + mobile Chrome silently
                                        no-op `.click()` on display:none file inputs. */}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        ref={photoFileInputRef}
                                        onChange={handlePhotoFileChange}
                                        aria-hidden="true"
                                        tabIndex={-1}
                                        className="sr-only"
                                    />

                                    {/* Gratitudes — appended to Today's Story as a bullet list (display-only merge).
                                        Storage remains a separate `gratitude` field saved as section='Gratitude'.
                                        Renders only when the day has gratitude content (hydrated from server). */}
                                    {(journalEntry.gratitude || '').trim().length > 0 && (
                                        <div data-testid="gratitudes-inline" className="mt-8">
                                            <p className="text-lg text-stone-700 leading-relaxed mb-2">
                                                I am grateful for the following:
                                            </p>
                                            <ul className="space-y-1.5">
                                                {[0, 1, 2].map(i => {
                                                    const lines = (journalEntry.gratitude || '').split('\n').concat(['', '', '']).slice(0, 3);
                                                    return (
                                                        <li key={i} className="flex items-start gap-3">
                                                            <span className="text-stone-400 leading-relaxed select-none mt-1">•</span>
                                                            <input
                                                                data-testid={`gratitude-input-${i}`}
                                                                type="text"
                                                                value={lines[i]}
                                                                onFocus={() => setActiveSection('gratitude')}
                                                                onChange={(e) => {
                                                                    const updated = (journalEntry.gratitude || '').split('\n').concat(['', '', '']).slice(0, 3);
                                                                    updated[i] = e.target.value;
                                                                    setJournalEntry(prev => ({ ...prev, gratitude: updated.join('\n') }));
                                                                }}
                                                                className="flex-1 bg-transparent border-0 border-b border-stone-200/60 py-1 text-lg leading-relaxed text-stone-700 focus:outline-none focus:border-stone-400 transition-colors"
                                                            />
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Guided Prompts zone — separated from editor by bento divide-y */}
                                {guidedPrompts.length > 0 && (
                                    <div className="p-6">
                                        <GuidedReflectionsSection
                                            guidedPrompts={guidedPrompts}
                                            setGuidedPrompts={setGuidedPrompts}
                                            curate={curateGuidedReflections}
                                            setActiveSection={setActiveSection}
                                            setDrawingTarget={setDrawingTarget}
                                            drawingStoreRef={drawingStoreRef}
                                            setActiveEditor={setActiveEditor}
                                            onAddInlinePhoto={triggerInlinePhotoUpload}
                                            onAddPrompts={triggerAddPrompts}
                                            voice={journalVoice}
                                        />
                                    </div>
                                )}
                                <div className="px-6 py-2 text-center text-[10px] text-stone-400 font-medium tracking-tight tabular-nums">
                                    {noteStats.words} {noteStats.words === 1 ? 'word' : 'words'} • {noteStats.chars} {noteStats.chars === 1 ? 'char' : 'chars'}
                                </div>
                            </div>

                        </div>

                        {/* Curate Prompts modal — lifted out of the GR section so it lives at the journal-page level */}
                        <CuratePromptsModal
                            isOpen={isCuratorOpen}
                            onClose={() => setIsCuratorOpen(false)}
                            catalog={promptCatalogQuery.data || []}
                            picks={promptPicksQuery.data || []}
                            picksLoading={promptPicksQuery.isLoading}
                            initiallySelected={guidedPrompts.map(p => p.promptId)}
                            onConfirm={handleConfirmCuration}
                        />

                        {/* Full-screen photo viewer — swipe / arrow / chevron nav + native share */}
                        {expandedPhotoIndex !== null && (journalEntry.images || [])[expandedPhotoIndex] && (
                            <PhotoViewer
                                images={journalEntry.images || []}
                                index={expandedPhotoIndex}
                                onIndexChange={setExpandedPhotoIndex}
                                onClose={() => setExpandedPhotoIndex(null)}
                            />
                        )}

                        {/* Drawing Canvas Modal */}
                        <DrawingCanvas
                            isOpen={drawingTarget !== null}
                            onClose={() => setDrawingTarget(null)}
                            onInsert={(dataURL) => {
                                const ref = notesEditorRef;
                                if (!ref.current) return;
                                const id = Math.random().toString(36).slice(2, 10);
                                drawingStoreRef.current.set(id, dataURL);
                                ref.current.focus();
                                document.execCommand('insertHTML', false, `<img data-drawing-id="${id}" src="${dataURL}">`);
                                const html = sanitizeHTML(ref.current.innerHTML);
                                setJournalEntry(prev => ({ ...prev, [drawingTarget]: html }));
                                setDrawingTarget(null);
                            }}
                        />
                        </div>
                    </div>
                )}

                {/* --- REDESIGNED CHAT TAB --- */}
                {activeTab === 'chat' && (
                  <AiGate
                    feature={AI_FEATURES.chat}
                    consent={aiDataConsent}
                    variant="full"
                    onEnable={() => { setActiveTab('profile'); setProfileRequestTab('safety'); }}
                  >
                    <div className="h-[calc(100dvh-104px)] sm:h-[calc(100vh-140px)] flex flex-col bg-[#FDFCF8] rounded-[2.5rem] shadow-sm border border-stone-100 overflow-hidden relative font-serif">
                        {/* Header */}
                        <div className="px-4 py-3 border-b border-stone-100/50 bg-white/60 backdrop-blur-md flex items-center justify-between z-10 sticky top-0">
                            <div className="flex items-center gap-4">
                                <div className="h-10 w-10 bg-stone-800 text-white rounded-full flex items-center justify-center shadow-lg"><Feather size={18} /></div>
                                <div>
                                    <h3 className="font-serif text-lg font-bold text-stone-800 tracking-tight">Daily Dialogue</h3>
                                    <p className="text-[10px] text-stone-400 font-sans font-medium uppercase tracking-widest">Your Safe Space</p>
                                </div>
                            </div>
                            {!isGuest && (
                                <div className="flex items-center gap-1">
                                    <TemporaryChatToggle
                                        value={isTemporaryChat}
                                        onChange={handleToggleTemporary}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleNewChat}
                                        aria-label="Start a new chat"
                                        title="New chat"
                                        className="p-2.5 rounded-full text-[#7C9A82] hover:bg-[#7C9A82]/10 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C9A82]/40"
                                    >
                                        <Plus size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowChatHistory(true)}
                                        aria-label="Open chat history"
                                        title="Chat history"
                                        className="p-2.5 rounded-full text-stone-500 hover:text-stone-800 hover:bg-stone-100 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-200"
                                    >
                                        <Menu size={18} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Temporary-mode notice — dashed on purpose: the border
                            itself says "this doesn't hold together". */}
                        {isTemporaryChat && !isGuest && (
                            <div className="px-4 pt-3">
                                <p className="rounded-2xl border border-dashed border-[#7C9A82]/45 bg-[#7C9A82]/5 px-4 py-2.5 text-xs font-sans leading-relaxed text-[#7C9A82]">
                                    Temporary chat — this one won't be saved to your history.
                                </p>
                            </div>
                        )}

                        {/* Content Area - Book Style */}
                        <div className="flex-1 overflow-y-auto px-2 py-4 sm:p-4 space-y-6 scroll-smooth">
                            <style>{`
                                @keyframes leafBreath {
                                    0%, 100% { opacity: 0.35; filter: drop-shadow(0 0 3px rgba(74,222,128,0.2)); transform: scale(0.95); }
                                    50% { opacity: 1; filter: drop-shadow(0 0 14px rgba(74,222,128,0.75)); transform: scale(1.12); }
                                }
                            `}</style>
                            {chatHistory.map((msg, idx) => (
                                <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} animate-in fade-in slide-in-from-bottom-2 duration-700`}>
                                    {msg.role === 'ai' ? (
                                        <div className="w-full">
                                            <div className="flex items-start gap-4 mb-2">
                                                <span className="text-stone-300 font-sans text-[10px] font-bold uppercase tracking-widest mt-1">MindWire</span>
                                            </div>
                                            <div className="text-stone-700 text-lg leading-loose font-serif border-l-2 border-stone-200 pl-3 sm:pl-4 py-1">
                                                {msg.content.split('\n').map((line, i) => (
                                                    <p key={i} className="mb-4 last:mb-0">{line}</p>
                                                ))}
                                            </div>
                                            {msg.memories?.length > 0 && (
                                                <MemoryRecall memories={msg.memories} onOpenJournal={navigateToJournal} />
                                            )}
                                            {msg.isDistress && <SupportResources />}
                                        </div>
                                    ) : (
                                        <div className="max-w-[85%] sm:max-w-xl mt-4 mb-4">
                                            <div className="bg-white px-5 py-3 rounded-2xl shadow-sm border border-stone-100 text-stone-600 font-sans text-base leading-relaxed italic">
                                                "{msg.content}"
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {/* In-progress streaming bubble */}
                            {chat.state.isStreaming && chat.state.streamedText && (
                                <div className="flex flex-col items-start animate-in fade-in slide-in-from-bottom-2 duration-700">
                                    <div className="w-full">
                                        <div className="flex items-start gap-4 mb-2">
                                            <span className="text-stone-300 font-sans text-[10px] font-bold uppercase tracking-widest mt-1">MindWire</span>
                                        </div>
                                        <div className="text-stone-700 text-lg leading-loose font-serif border-l-2 border-stone-200 pl-4 py-1">
                                            {chat.state.streamedText.split('\n').map((line, i) => (
                                                <p key={i} className="mb-4 last:mb-0">{line}</p>
                                            ))}
                                            <span className="inline-block w-2 h-5 bg-stone-300 ml-1 align-middle animate-pulse" />
                                        </div>
                                        {/* reply_meta lands at finalize, so this only shows in the
                                            brief window before the turn folds into chatHistory —
                                            it exists so the card never flickers out mid-turn. */}
                                        {chat.state.isDistress && <SupportResources />}
                                    </div>
                                </div>
                            )}

                            {/* Thinking timeline */}
                            {chat.state.isStreaming && (
                                <ChatThinkingTimeline
                                    stages={chat.state.stages}
                                    hasTokens={!!chat.state.streamedText}
                                />
                            )}
                            <div ref={chatEndRef} />
                        </div>

                        {/* Sticky Input Area */}
                        <div
                            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
                            className="px-2 pt-3 sm:p-4 bg-gradient-to-t from-white via-white/90 to-transparent z-20"
                        >
                            {chatCount >= CHAT_LIMIT ? (
                                <div className="text-center py-4 px-6 space-y-3" data-testid="chat-limit-message">
                                    <p className="text-sm text-stone-500 font-sans leading-relaxed">
                                        You've reached {CHAT_LIMIT} messages in this chat. Earlier conversations stay safe in your history.
                                    </p>
                                    {!isGuest && (
                                        <button
                                            type="button"
                                            onClick={handleNewChat}
                                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-sans text-white bg-stone-800 hover:bg-stone-900 transition-colors duration-300"
                                        >
                                            <Plus size={14} /> Start a new chat
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {(() => {
                                        const hasText = chatInput.trim().length > 0;
                                        return (
                                            <div className={`bg-white rounded-full border shadow-sm flex items-center gap-1 px-2 py-1.5 transition-all ${
                                                isRecording
                                                    ? 'border-rose-200 shadow-rose-50/40'
                                                    : 'border-stone-200/80 focus-within:border-stone-300 focus-within:ring-2 focus-within:ring-stone-100'
                                            }`}>
                                                {isRecording && (
                                                    <button
                                                        onClick={stopRecording}
                                                        aria-label="Stop recording"
                                                        title="Stop recording"
                                                        className="shrink-0 p-2.5 rounded-full bg-stone-800 text-white hover:bg-stone-900 transition active:scale-95"
                                                    >
                                                        <Square size={12} className="fill-current" />
                                                    </button>
                                                )}
                                                <textarea
                                                    ref={chatInputRef}
                                                    value={chatInput}
                                                    onChange={(e) => {
                                                        setChatInput(e.target.value);
                                                        // Auto-grow up to ~5 lines; then scroll inside the textarea.
                                                        e.target.style.height = 'auto';
                                                        e.target.style.height = Math.min(e.target.scrollHeight, 144) + 'px';
                                                    }}
                                                    onKeyDown={(e) => {
                                                        // Enter sends; Shift+Enter adds a newline (ChatGPT convention).
                                                        if (e.key === 'Enter' && !e.shiftKey && hasText) {
                                                            e.preventDefault();
                                                            handleChatSubmit();
                                                        }
                                                    }}
                                                    rows={1}
                                                    placeholder={
                                                        isRecording ? "Listening…"
                                                        : isTranscribing ? "Transcribing…"
                                                        : "Message..."
                                                    }
                                                    disabled={isRecording || isTranscribing}
                                                    className="flex-1 min-w-0 bg-transparent px-4 py-3 outline-none text-base font-sans text-stone-700 placeholder-stone-400 disabled:opacity-60 resize-none overflow-y-auto leading-relaxed"
                                                />
                                                {isSpeechSupported() && !isRecording && !isTranscribing && (
                                                    <button
                                                        onClick={() => { if (ttsEnabled) cancelSpeech(); setTtsEnabled((v) => !v); }}
                                                        title={ttsEnabled ? 'Reading next reply — tap to mute' : 'Hear next reply'}
                                                        aria-label={ttsEnabled ? 'Mute next reply' : 'Hear next reply'}
                                                        className="shrink-0 p-2 text-stone-400 hover:text-stone-700 transition"
                                                    >
                                                        {ttsEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={isRecording ? stopRecording : startRecording}
                                                    disabled={isTranscribing}
                                                    aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
                                                    title={isRecording ? 'Tap to stop' : 'Tap to speak'}
                                                    className={`shrink-0 p-2 rounded-full transition active:scale-95 ${
                                                        isRecording
                                                            ? 'text-rose-500 animate-pulse'
                                                            : 'text-stone-400 hover:text-stone-700'
                                                    } disabled:opacity-50`}
                                                >
                                                    {isTranscribing ? <Loader2 className="animate-spin" size={18} /> : <Mic size={18} />}
                                                </button>
                                                {hasText && !isRecording && !isTranscribing && (
                                                    <button
                                                        onClick={handleChatSubmit}
                                                        disabled={isGuest ? false : chat.state.isStreaming}
                                                        aria-label="Send message"
                                                        className="shrink-0 p-2.5 bg-stone-800 text-white rounded-full hover:bg-stone-900 transition active:scale-95 disabled:opacity-50"
                                                    >
                                                        <Send size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    {voiceError && (
                                        <p className="text-center text-[11px] text-red-500 font-sans mt-1">
                                            Voice: {voiceError}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <ChatHistoryDrawer
                        open={showChatHistory}
                        onClose={() => setShowChatHistory(false)}
                        userId={userId}
                        onNewChat={handleNewChat}
                        onPickChat={handlePickChat}
                    />
                  </AiGate>
                )}

                {/* {activeTab === 'motivation' && (
                    <GlassCard className="min-h-[400px] flex flex-col items-center justify-center text-center space-y-8 !bg-white">
                        <div className="h-24 w-24 bg-stone-800 rounded-full flex items-center justify-center shadow-xl mb-2"><Sparkles className="text-white" size={40} /></div>
                        <div className="max-w-md w-full">
                            {isAiLoading ? (
                                <div className="space-y-4">
                                    <div className="text-stone-400 font-medium animate-pulse">Connecting to your inner wisdom...</div>
                                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden w-48 mx-auto"><div className="h-full bg-stone-800 animate-progress"></div></div>
                                </div>
                            ) : (
                                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
                                    <div className="bg-stone-50 p-8 rounded-3xl border border-stone-100 relative">
                                        <div className="absolute top-0 left-0 -mt-3 -ml-3 text-6xl text-stone-200 font-serif leading-none">"</div>
                                        <div className="text-xl font-medium text-stone-800 leading-relaxed font-serif relative z-10">{aiResponse || "Your daily wisdom awaits."}</div>
                                    </div>
                                    <button onClick={generateAiMotivation} className="px-10 py-4 bg-stone-800 hover:bg-black text-white font-bold rounded-full transition-all hover:shadow-xl hover:-translate-y-1">Inspire Me</button>
                                </div>
                            )}
                        </div>
                    </GlassCard>
                )} */}

        {activeTab === 'voice' && (
          <VoiceTab
            key={`voice-${voiceNav.n}`}
            userId={userId}
            onRecord={() => setVoiceCaptureOpen(true)}
            onOpenJournal={navigateToJournal}
          />
        )}

        {activeTab === 'motivation' && (

                <GlassCard
                className={`
                    min-h-[400px]
                    flex flex-col items-center justify-center text-center space-y-6
                    bg-gradient-to-br ${moodStyle.gradient}
                    relative overflow-hidden
                `}
                >
                {/* subtle animated light */}
                {/* <div className="absolute inset-0 opacity-40 animate-slow-float bg-[radial-gradient(circle_at_30%_20%,white,transparent_40%)]" /> */}
                <div className={` absolute inset-0 opacity-50 animate-slow-float bg-[radial-gradient(${moodStyle.glow},white,transparent_45%)]`} />
                {/* grain texture */}
                <div className="absolute inset-0 pointer-events-none opacity-[0.025] bg-[radial-gradient(#000_1px,transparent_1px)] bg-[size:3px_3px]" />

                <div className="relative z-10 w-full flex flex-col items-center">

                    {/* 🔀 Quote Mode Toggle */}
                    <div className="mb-6">
                    <div className="flex rounded-full bg-white/60 backdrop-blur p-1 shadow-inner">
                        <button
                        onClick={() => setQuoteMode("personal")}
                        className={`px-4 py-2 text-sm rounded-full transition-all
                            ${quoteMode === "personal"
                            ? "bg-white text-stone-900 shadow"
                            : "text-stone-500 hover:text-stone-800"
                            }`}
                        >
                        Personal
                        </button>

                        <button
                        onClick={() => setQuoteMode("wisdom")}
                        className={`px-4 py-2 text-sm rounded-full transition-all
                            ${quoteMode === "wisdom"
                            ? "bg-white text-stone-900 shadow"
                            : "text-stone-500 hover:text-stone-800"
                            }`}
                        >
                        Wisdom
                        </button>
                    </div>
                    </div>

                    {/* Icon */}
                    <div className="h-24 w-24 bg-stone-800 rounded-full flex items-center justify-center shadow-xl mb-2">
                    <Sparkles className="text-white" size={40} />
                    </div>

                    {/* <div className="max-w-md w-full">
                    {isAiLoading ? (
                        <div className="space-y-4">
                        <div className="text-stone-500 font-medium animate-pulse">
                            {quoteMode === "wisdom"
                            ? "Listening to voices that endured time…"
                            : "Connecting to your inner wisdom…"}
                        </div>
                        <div className="h-2 bg-stone-200 rounded-full overflow-hidden w-48 mx-auto">
                            <div className="h-full bg-stone-800 animate-progress"></div>
                        </div>
                        </div>
                    ) : (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
                        <div className="bg-white/70 backdrop-blur p-8 rounded-3xl border border-white/60 relative shadow-lg">
                            <div className="absolute top-0 left-0 -mt-3 -ml-3 text-6xl text-stone-300 font-serif leading-none">
                            "
                            </div>
                            <div className="text-xl font-medium text-stone-800 leading-relaxed font-serif relative z-10">
                            {aiResponse || "Your daily wisdom awaits."}
                            </div>
                        </div>

                        <button
                            onClick={generateAiMotivation}
                            className="px-10 py-4 bg-stone-800 hover:bg-black text-white font-bold rounded-full transition-all hover:shadow-xl hover:-translate-y-1"
                        >
                            Inspire Me
                        </button>
                        </div>
                    )}
                    </div> */}
                <div className="max-w-md w-full">
  {isAiLoading ? (
    <div className="space-y-4">
      <div className="text-stone-500 font-medium animate-pulse">
        {quoteMode === "wisdom"
          ? "Listening to voices that endured time…"
          : "Connecting to your inner wisdom…"}
      </div>
      <div className="h-2 bg-stone-200 rounded-full overflow-hidden w-48 mx-auto">
        <div className="h-full bg-stone-800 animate-progress"></div>
      </div>
    </div>
  ) : (
    <div
      onClick={handleQuoteTap}
      className={`
        space-y-8 animate-in fade-in slide-in-from-bottom-4
        ${sessionLimitReached ? "cursor-default" : "cursor-pointer"}
      `}
    >
      <div
        className={`
          bg-white/70 backdrop-blur p-8 rounded-3xl border border-white/60
          relative shadow-lg transition-all
          ${!sessionLimitReached ? "hover:shadow-xl hover:-translate-y-0.5" : ""}
        `}
      >
        <div className="absolute top-0 left-0 -mt-3 -ml-3 text-6xl text-stone-300 font-serif leading-none">
          "
        </div>

        <div className="text-xl font-medium text-stone-800 leading-relaxed font-serif relative z-10">
          {aiResponse || "Your daily wisdom awaits."}
        </div>
      </div>

      {!sessionLimitReached ? (
        <p className="text-xs text-stone-400 italic">
          Tap anywhere to receive another thought
        </p>
      ) : (
        <p className="text-sm text-stone-500 italic">
          {/* That's enough for now. Let one of them stay with you. */}
          I wish you to let one of them stay with you.
        </p>
      )}
    </div>
  )}
</div>
                </div>
                </GlassCard>
                )}


{/* FEED BEGIN */}
{activeTab === 'feed' && (
    <FeedView
        feedData={feedData}
        feedLoading={feedLoading}
        feedError={feedError}
        feedHasMore={feedHasMore}
        feedStats={feedStats}
        onLoadMore={fetchNextPage}
        onRefresh={refetchFeed}
        onNavigateToJournal={navigateToJournal}
    />
)}

{/* PROFILE BEGIN */}

{activeTab === 'profile' && (
  <ProfilePage
    userId={userId}
    userEmail={email}
    profile={profile}
    isGuest={isGuest}
    isProfileDirty={isProfileDirty}
    isNameValid={isNameValid}
    updateProfileField={updateProfileField}
    persistProfile={persistProfile}
    onPhotoChange={applyProfilePhoto}
    onProfileReset={() => { setProfile(lastSavedProfile); setIsProfileDirty(false); }}
    onLogout={handleSignOut}
    aiDataConsent={aiDataConsent}
    onConsentChange={(val) => {
        setAiDataConsent(val);
        if (val === true) setShowConsentModal(true);
    }}
    requestTab={profileRequestTab}
    onNavigateToJournal={async (dateStr) => {
    //   await handleTabChange('journal');
      setActiveTab('journal');
    //   handleDateChange(dateStr);
      console.log("setting current date to ProfilePage", dateStr);
      console.log("current date before setting ", currentDate);
      setCurrentDate(dateStr);
    }}
  />
)}
            </main>

            {/* Floating Toolbar — slides in from top when in-page toolbar scrolls out of view */}
            {activeTab === 'journal' && (
                <div className={`fixed top-[68px] left-0 right-0 px-4 z-30 transition-all duration-200 ease-out ${
                    toolbarFloating
                        ? 'opacity-100 translate-y-0 pointer-events-auto'
                        : 'opacity-0 -translate-y-2 pointer-events-none'
                }`}>
                    <div className="max-w-3xl mx-auto">
                        {guidedToast && (
                            <div className="text-sm text-stone-600 text-center px-4 py-2.5 bg-amber-50 rounded-xl border border-amber-100 mb-1.5 shadow-sm">
                                {guidedToast}
                            </div>
                        )}
                        <div className="bg-white/90 backdrop-blur-xl border border-stone-200/80 shadow-lg rounded-2xl overflow-hidden">
                            <div className="px-2.5 py-2 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1">
                                    {journalToolButtonsLeft.map(renderJournalToolBtn)}
                                </div>
                                <div className="flex items-center gap-1">
                                    {journalToolButtonsRight.map(renderJournalToolBtn)}
                                </div>
                            </div>
                            <div
                                aria-label="Text formatting"
                                className="border-t border-stone-200/60 flex items-center"
                            >
                                <BlockToolbar
                                    editorRef={activeEditorRefAdapter}
                                    onOpenDrawing={() => activeEditor?.onOpenDrawing?.()}
                                    onAddPhoto={triggerPhotoUpload}
                                    onAddPrompts={triggerAddPrompts}
                                    onChange={activeEditor?.onChange}
                                    isEditorActive={!!activeEditor}
                                    className="flex-1 flex items-center gap-0.5 overflow-x-auto h-12 px-2 scrollbar-hide"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* One capture sheet for every entry point — Home, the journal
                toolbar and the Voice tab. Merged 2026-09-23 from
                VoiceJournalCapture + EchoCapture, which differed in ways no
                user asked for (one gated on an emotion, the other on a
                section; 2-minute cap vs 6). guidedReflections is passed only
                here because the journal is the one caller that knows which
                prompts are on screen. */}
            <VoiceCapture
                open={voiceCaptureOpen}
                onClose={() => setVoiceCaptureOpen(false)}
                entryDate={currentDate}
                guidedReflections={guidedPrompts.map(p => ({
                    prompt_id: p.promptId,
                    prompt_text: p.promptView === 'research' ? p.researchPrompt : p.simplePrompt,
                }))}
                onUploaded={() => { loadJournals(userId); }}
                onOpenVoice={openVoice}
                onOpenJournal={navigateToJournal}
                onAskAi={() => setAiNudge({
                    reason: "Your recording is saved. Turning on AI also writes it down for you and lets chat draw on it.",
                })}
            />

            <AiEnableDialog
                open={!!aiNudge}
                userId={userId}
                reason={aiNudge?.reason}
                onEnabled={() => {
                    setAiDataConsent(true);
                    setAiNudge(null);
                    // Today's hero was picked from the evergreen catalog; drop the
                    // cache entry so it can be matched to the user's own writing
                    // instead of sitting stale until the 30-minute staleTime.
                    queryClient.invalidateQueries({
                        queryKey: ['reflectionOfTheDay', userId, getLocalToday()],
                    });
                }}
                onClose={() => setAiNudge(null)}
            />

            {/* Smart Reflection — shown once, after the save that earned it.
                Sits above the "Saved · AI is off" notice in the DOM because the
                two are mutually exclusive: that notice only appears when AI is
                off, and this card only exists when it is on. */}
            <SmartReflectionCard
                open={!!smartReflection}
                reflection={smartReflection?.reflection}
                invitation={smartReflection?.invitation}
                feedback={smartReflection?.feedback}
                onFeedback={handleSmartReflectionFeedback}
                onClose={() => setSmartReflection(null)}
            />

            {/* "Saved · AI is off" — a statement, not a second ask. The action
                still opens the dialog: a cooldown governs prompts we raise, never
                a button the user just pressed. */}
            {aiOffNotice && (
                <div
                    role="status"
                    style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5.5rem)' }}
                    className="fixed left-1/2 -translate-x-1/2 z-[55] flex items-center gap-3 pl-5 pr-2 py-2 bg-white/95 dark:bg-[#2D2A27]/95 backdrop-blur-sm border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300 text-sm font-sans rounded-full shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-500 pointer-events-auto"
                >
                    <span>Saved · AI is off</span>
                    <button
                        type="button"
                        onClick={() => {
                            setAiOffNotice(false);
                            setAiNudge({
                                reason: 'Your entry is saved. Turning on AI also tags it and lets chat draw on what you have written.',
                            });
                        }}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold text-white transition-all duration-300 ease-out hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
                        style={{ backgroundColor: '#7C9A82' }}
                    >
                        Turn on
                    </button>
                </div>
            )}

            {chatToast && (
                <div
                    role="status"
                    onClick={() => setChatToast(null)}
                    style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5.5rem)' }}
                    className="fixed left-1/2 -translate-x-1/2 z-[55] px-5 py-2.5 bg-amber-50/95 backdrop-blur-sm border border-amber-200 text-amber-800 text-sm font-sans rounded-full shadow-lg cursor-pointer animate-in fade-in slide-in-from-bottom-2 duration-500 pointer-events-auto"
                >
                    {chatToast}
                </div>
            )}

            {activeTab === 'home' && (
                <nav
                    style={{
                        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)',
                        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
                        backdropFilter: 'blur(28px) saturate(180%)',
                    }}
                    className="fixed left-1/2 -translate-x-1/2 w-[min(420px,calc(100vw-1rem))] bg-white/25 border border-white/30 shadow-[0_4px_20px_rgba(45,41,38,0.06)] rounded-[28px] px-1.5 py-1.5 flex items-stretch z-50"
                    aria-label="Main navigation"
                >
                    {navItems.map((item) => {
                        const isActive = activeTab === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => handleTabChange(item.id)}
                                aria-label={item.label}
                                aria-current={isActive ? 'page' : undefined}
                                className={`flex-1 flex flex-col items-center justify-center gap-1 min-h-[56px] px-1 rounded-2xl transition-all duration-200 active:scale-95 ${
                                    isActive
                                        ? 'text-stone-900 bg-white/50'
                                        : 'text-stone-500 hover:text-stone-800 hover:bg-white/25'
                                }`}
                            >
                                <item.icon size={22} strokeWidth={isActive ? 2.4 : 1.8} />
                                <span className={`text-[10px] font-medium font-sans leading-none tracking-tight transition-opacity ${isActive ? 'opacity-100' : 'opacity-75'}`}>
                                    {item.label}
                                </span>
                            </button>
                        );
                    })}
                </nav>
            )}

            {sessionExpired && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center">
                    <div className="bg-white rounded-3xl shadow-2xl px-8 py-10 max-w-xs w-full mx-4 text-center">
                        <div className="h-12 w-12 rounded-2xl mx-auto flex items-center justify-center mb-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}>
                            <BookOpen size={22} className="text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-stone-900 mb-2">Session expired</h2>
                        <p className="text-stone-500 text-sm mb-6">Your draft is saved. Sign in again to continue.</p>
                        <button
                            onClick={() => { setSessionExpired(false); handleSignOut(); }}
                            className="w-full py-3 rounded-2xl text-white font-semibold text-sm transition-opacity hover:opacity-90 active:scale-95"
                            style={{ background: 'linear-gradient(135deg, #7C9A82, #5a7a60)' }}
                        >
                            Sign in again
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}